var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/index.ts
import { CartaVisualized } from "@customerio/carta-visualized";
import { MarshaledPreview as MarshaledPreview2 } from "@customerio/marshaled-preview";
import { atom } from "nanostores";
import { components } from "@customerio/carta-visualized";

// src/context/main.tsx
import { useStore } from "@nanostores/react";
import { createContext, useContext, useEffect } from "react";
import { jsx } from "react/jsx-runtime";
var Context = createContext(null);
function useEncompass() {
  const encompass = useContext(Context);
  if (!encompass) {
    throw new Error("Encompass context is not provided");
  }
  return encompass;
}
function useCodeEditor() {
  const encompass = useEncompass();
  return encompass.codeEditor;
}
function MainProvider({
  encompass,
  children,
  onCodeChange
}) {
  const reactEncompass = {
    codeEditor: {
      ...encompass.codeEditor,
      state: useStore(encompass.codeEditor.state)
    },
    visualEditor: {
      ...encompass.visualEditor,
      state: useStore(encompass.visualEditor.state)
    }
  };
  useEffect(() => {
    onCodeChange?.(reactEncompass.codeEditor.state.value);
  }, [reactEncompass.codeEditor.state.value]);
  useEffect(() => {
    const onMouseOver = () => {
      setTimeout(() => {
        reactEncompass.visualEditor.actions.setHoveredComponent(null);
      }, 10);
    };
    document.addEventListener("mouseover", onMouseOver);
    return () => document.removeEventListener("mouseover", onMouseOver);
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      const target = e.target;
      if (!document.body.contains(target)) {
        return;
      }
      if (target.closest("[data-encompass-area]")) {
        return;
      }
      reactEncompass.visualEditor.actions.blur();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return /* @__PURE__ */ jsx(Context.Provider, { value: reactEncompass, children });
}

// src/context/selection.tsx
import {
  valueToPrimitive
} from "@customerio/carta-visualized";
import { debounce } from "lodash";
import { createContext as createContext2, useContext as useContext2, useEffect as useEffect2, useRef, useState } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Context2 = createContext2(null);
function useInternalSelection() {
  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState();
  const { visualEditor } = useEncompass();
  const previousSelectionRef = useRef(null);
  function update(state2) {
    setIsLoading(false);
    setState(state2);
    previousSelectionRef.current = state2.selection;
  }
  useEffect2(() => {
    if (visualEditor.state.editMode === "structure" && visualEditor.state.selectedComponents?.length !== 1) {
      return update({ selection: "none" });
    }
    const selectedComponent = visualEditor.state.editMode === "structure" ? visualEditor.state.selectedComponents[0] : visualEditor.state.contentTarget ? {
      type: "component",
      name: visualEditor.state.contentTarget.name,
      index: visualEditor.state.contentTarget.index
    } : null;
    if (!selectedComponent) {
      return update({ selection: "none" });
    }
    const definition = visualEditor.actions.getComponentDefinition(selectedComponent.name);
    if (!definition) {
      return update({ selection: "none" });
    }
    setIsLoading(previousSelectionRef.current !== "single");
    (async () => {
      const values = await visualEditor.actions.getComponentProps(selectedComponent);
      if (!values) {
        return update({ selection: "none" });
      }
      const throttledMap = {};
      function throttledSetProps(name, value) {
        if (!throttledMap[name]) {
          throttledMap[name] = debounce((value2) => {
            visualEditor.commands.setProps(selectedComponent, { [name]: value2 });
          }, 100);
        }
        throttledMap[name](value);
      }
      const setProps = (update2) => {
        for (const [name, value] of Object.entries(update2)) {
          if (value.isPrimitive === true) {
            definition.props.schema.shape[name].parse(valueToPrimitive(value));
          }
        }
        for (const [name, value] of Object.entries(update2)) {
          throttledSetProps(name, value);
        }
        setState((state3) => {
          if (state3?.selection === "single" && state3.props) {
            return {
              ...state3,
              props: {
                ...state3.props,
                values: {
                  ...state3.props.values,
                  ...update2
                }
              }
            };
          }
          return state3;
        });
      };
      const removeProps = async (names) => {
        if (state2.selection !== "single" || !state2.props) {
          return;
        }
        await visualEditor.commands.removeProps(selectedComponent, names);
        const updatedValues = await visualEditor.actions.getComponentProps(selectedComponent);
        setState((state3) => {
          if (state3.selection !== "single" || !state3.props) {
            return state3;
          }
          const newValues = { ...state3.props.values };
          for (const name of names) {
            newValues[name] = updatedValues[name]?.isComputed === true ? updatedValues[name] : void 0;
          }
          return {
            ...state3,
            props: {
              ...state3.props,
              values: newValues
            }
          };
        });
      };
      const state2 = {
        selection: "single",
        component: selectedComponent,
        name: definition.name,
        label: definition.label,
        componentType: definition.componentType,
        props: definition.props ? {
          fields: definition.props.fields,
          schema: definition.props.schema,
          values,
          setProps,
          removeProps
        } : null,
        slots: definition.slots ?? null
      };
      return update(state2);
    })();
  }, [
    visualEditor.state.editMode,
    JSON.stringify(visualEditor.state.selectedComponents),
    JSON.stringify(visualEditor.state.contentTarget)
  ]);
  return {
    isLoading,
    state
  };
}
function SelectionProvider({ children }) {
  const data = useInternalSelection();
  return /* @__PURE__ */ jsx2(Context2.Provider, { value: data, children });
}
function useSelection() {
  const state = useContext2(Context2);
  if (!state) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return state;
}

// src/visual-editor/index.tsx
var visual_editor_exports = {};
__export(visual_editor_exports, {
  ContextBar: () => ContextBar,
  CorePropertiesMenu: () => CorePropertiesMenu,
  FullPropertiesMenu: () => FullPropertiesMenu,
  InsertMenu: () => InsertMenu,
  Layers: () => Layers,
  Overlay: () => MarshaledPreviewOverlay,
  Root: () => Root17,
  Script: () => MarshaledPreviewScript,
  Style: () => MarshaledPreviewStyle,
  Tooltip: () => MarshaledPreviewTooltip,
  View: () => View
});
import { useEffect as useEffect19 } from "react";

// src/components/button.tsx
import { styled } from "@stitches/react";
import { Slot } from "@radix-ui/react-slot";
import { jsx as jsx3 } from "react/jsx-runtime";
var Button = styled(
  function Button2({
    variant = "primary",
    asChild,
    ...props
  }) {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx3(Comp, { ...props, "data-variant": variant });
  },
  {
    background: "transparent",
    border: "none",
    borderRadius: 3,
    padding: "4px 12px",
    font: "inherit",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "20px",
    cursor: "pointer",
    '&[data-variant="primary"]': {
      background: "rgba(87, 33, 204, 1)",
      color: "white"
    },
    '&[data-variant="secondary"]': {
      background: "transparent",
      color: "#343446"
    },
    '&[data-variant="danger"]': {
      background: "transparent",
      color: "#DB234B"
    }
  }
);

// src/visual-editor/error-display/index.tsx
import { styled as styled2 } from "@stitches/react";
import { Fragment, jsx as jsx4, jsxs } from "react/jsx-runtime";
function ErrorDisplay({ error }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx4(Cover, {}),
    /* @__PURE__ */ jsx4(ErrorMessage, { error })
  ] });
}
var Cover = styled2("div", {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "#FFF",
  opacity: 0.8
});
var ErrorMessage = styled2(
  function ErrorMessage2({ error, className }) {
    const { visualEditor } = useEncompass();
    return /* @__PURE__ */ jsx4("div", { className, children: /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          background: "#FFF",
          border: "1px solid #DFE0E8",
          borderRadius: 4,
          padding: 16,
          boxShadow: "1px 2px 2px 0px rgba(52, 52, 70, 0.01), 4px 4px 24px 0px rgba(52, 52, 70, 0.16)",
          width: "100%",
          maxWidth: 640
        },
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start"
              },
              children: [
                /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx4("h3", { style: { margin: "0 0 4px" }, children: "Error found" }),
                  /* @__PURE__ */ jsx4(
                    "p",
                    {
                      style: {
                        color: "#5D6074",
                        fontSize: "13px",
                        fontWeight: 500,
                        margin: 0
                      },
                      children: "There was an error in the code which might prevent saving the message."
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx4(
                  Button,
                  {
                    variant: "primary",
                    onClick: () => {
                      visualEditor.actions.undo();
                    },
                    style: {
                      flexShrink: 0
                    },
                    children: "Undo Change"
                  }
                )
              ]
            }
          ),
          error.message ? /* @__PURE__ */ jsx4(
            "pre",
            {
              style: {
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "#FF95CA",
                background: "#2A2A38",
                padding: 16,
                fontSize: 12,
                fontFamily: "monospace",
                margin: "14px 0px 0px",
                borderRadius: 4
              },
              children: /* @__PURE__ */ jsx4("code", { children: error.message })
            }
          ) : null
        ]
      }
    ) });
  },
  {
    position: "absolute",
    bottom: 0,
    left: 0,
    top: 0,
    right: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 16
  }
);

// src/visual-editor/index.tsx
import { MarshaledPreview } from "@customerio/marshaled-preview/react";
import {
  MarshaledPreviewOverlay,
  MarshaledPreviewScript,
  MarshaledPreviewStyle,
  MarshaledPreviewTooltip
} from "@customerio/marshaled-preview/react";

// src/visual-editor/context-bar/index.tsx
import { styled as styled4 } from "@stitches/react";
import { Fragment as Fragment3 } from "react";

// src/components/icon.tsx
import { Slot as Slot2 } from "@radix-ui/react-slot";
import * as RadixAccessibleIcon from "@radix-ui/react-accessible-icon";
import { styled as styled3 } from "@stitches/react";
import { Fragment as Fragment2, forwardRef } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Icon = styled3(
  forwardRef(function Icon2({ label, icon, children, asChild, onClick, size, color, ...props }, ref) {
    const Comp = asChild ? Slot2 : onClick ? "button" : "span";
    const renderComp = /* @__PURE__ */ jsx5(
      Comp,
      {
        ...props,
        onClick,
        "data-type": icon ? "icon" : "svg",
        className: `${props.className ?? ""} ${icon ? "material-symbols-outlined" : ""}`,
        style: { fontSize: size, color, ...props.style ?? {} },
        ref,
        children: icon ?? children
      }
    );
    return label ? /* @__PURE__ */ jsx5(RadixAccessibleIcon.Root, { label, children: renderComp }) : /* @__PURE__ */ jsx5(Fragment2, { children: renderComp });
  }),
  {
    background: "transparent",
    border: "none",
    fontWeight: "inherit",
    lineHeight: "inherit",
    padding: 0,
    margin: 0,
    color: "inherit"
  }
);

// src/visual-editor/context-bar/index.tsx
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
function ContextBar() {
  const { visualEditor } = useEncompass();
  const selectedTarget = visualEditor.state.editMode === "content" ? visualEditor.state.contentTarget : visualEditor.state.selectedComponents?.[0];
  if (!selectedTarget) {
    return /* @__PURE__ */ jsx6(Container, { children: /* @__PURE__ */ jsx6(Text, { children: "No component selected" }) });
  }
  const selectedComponent = {
    type: "component",
    name: selectedTarget.name,
    index: selectedTarget.index
  };
  let path = [selectedComponent];
  let parent = visualEditor.actions.getParentComponentTarget(selectedComponent);
  while (parent) {
    path.unshift(parent);
    parent = visualEditor.actions.getParentComponentTarget(parent);
  }
  return /* @__PURE__ */ jsx6(Container, { children: path.map((component, index) => /* @__PURE__ */ jsxs2(Fragment3, { children: [
    /* @__PURE__ */ jsx6(
      Button3,
      {
        onClick: () => {
          visualEditor.actions.setSelectedComponent(component);
        },
        children: visualEditor.actions.getComponentDefinition(component.name)?.label || component.name
      }
    ),
    index < path.length - 1 ? /* @__PURE__ */ jsx6(Icon, { icon: "chevron_right", color: "#5D6074" }) : null
  ] }, index)) });
}
var textStyles = {
  textAlign: "left",
  padding: `6px 8px`,
  display: "flex",
  alignItems: "center",
  color: "#5D6074",
  background: "transparent",
  border: "none",
  font: "inherit",
  fontSize: "13px",
  fontWeight: 400
};
var Container = styled4("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "0 8px",
  borderTop: "1px solid #EBECF2",
  gap: "0px"
});
var Button3 = styled4("button", {
  ...textStyles,
  cursor: "pointer",
  "&:hover": {
    color: "#0652BB"
  }
});
var Text = styled4("div", { ...textStyles });

// src/visual-editor/insert-menu/index.tsx
import { useDraggable as useDraggable2 } from "@dnd-kit/core";
import { styled as styled12 } from "@stitches/react";
import { useEffect as useEffect10, useId as useId3, useRef as useRef8, useState as useState9 } from "react";

// src/visual-editor/overlays/index.tsx
import { useDndContext } from "@dnd-kit/core";

// src/components/color-preview.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var ColorPreview = forwardRef2(function ColorPreview2({ size, color, style, className, ...rest }, ref) {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      ref,
      style: {
        width: size,
        height: size,
        backgroundImage: `
              linear-gradient(45deg, #ccc 25%, transparent 25%), 
              linear-gradient(135deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(135deg, transparent 75%, #ccc 75%)`,
        backgroundSize: `${size / 2}px ${size / 2}px`,
        backgroundPosition: `0 0, ${size / 4}px 0, ${size / 4}px -${size / 4}px, 0px ${size / 4}px`,
        cursor: "pointer",
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: 4,
        ...style
      },
      className,
      ...rest,
      children: /* @__PURE__ */ jsx7(
        "div",
        {
          style: {
            width: "100%",
            height: "100%",
            backgroundColor: color,
            border: "1px solid rgba(0, 0, 0, .25)",
            borderRadius: 4
          }
        }
      )
    }
  );
});

// src/components/inputs/color.tsx
import * as Popover from "@radix-ui/react-popover";
import { styled as styled6 } from "@stitches/react";
import { useEffect as useEffect4, useRef as useRef2, useState as useState3 } from "react";

// src/components/inputs/text.tsx
import { styled as styled5 } from "@stitches/react";
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var inputContainerStyle = {
  border: "1px solid #C7C7D4",
  boxShadow: " 0 0 1px rgb(0 0 0 / 12%) inset",
  color: "#4b4b60",
  textAlign: "left",
  backgroundColor: " #fff",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 500,
  minHeight: "28px",
  minWidth: "30px",
  padding: "0 4px",
  display: "flex",
  alignItems: "center",
  gap: 4,
  borderColor: "#DFE0E8",
  // breaking the rules
  "&:focus-within": {
    outline: "none",
    borderColor: "#0652BB"
  },
  "&[data-disabled]": {
    backgroundColor: "#F7F8FA",
    color: "#A1A2B2"
  },
  "&[data-readonly]": {
    backgroundColor: "#F7F8FA"
  }
};
var inputStyle = {
  textAlign: "inherit",
  width: "100%",
  height: "100%",
  border: "none",
  background: "transparent",
  font: "inherit",
  display: "inline-block",
  "&:focus": {
    outline: "none"
  },
  /* Chrome, Safari, Edge, Opera */
  "&::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0
  },
  "&::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0
  },
  /* Firefox */
  "&[type=number]": {
    "-moz-appearance": "textfield"
  }
};
var InputContainer = styled5("div", inputContainerStyle);
var Input = styled5("input", inputStyle);
var IconWrapper = styled5("div", {
  flexShrink: 0
});
var TextInput = forwardRef3(({ value, onChange, leadingIcon, disabled, className, style, ...props }, ref) => {
  return /* @__PURE__ */ jsxs3(InputContainer, { className, style, "data-disabled": disabled, "data-readonly": props.readOnly, children: [
    leadingIcon ? /* @__PURE__ */ jsx8(IconWrapper, { children: leadingIcon }) : null,
    /* @__PURE__ */ jsx8(
      Input,
      {
        type: "text",
        value: value ?? "",
        onChange: (e) => onChange(e.target.value),
        ...props,
        disabled,
        ref,
        onFocus: (event) => {
          event.target.select();
        }
      }
    )
  ] });
});

// src/components/inputs/color.tsx
import { colord } from "colord";
import { HexColorInput, RgbaStringColorPicker } from "react-colorful";

// src/components/inputs/number.tsx
import { forwardRef as forwardRef4, useEffect as useEffect3, useState as useState2 } from "react";
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var NumberInput = forwardRef4(({ onChange, leadingIcon, trailingIcon, className, style, disabled, value, ...props }, ref) => {
  const [valueOnFocus, setValueOnFocus] = useState2(void 0);
  const [internalValue, setInternalValue] = useState2(value);
  useEffect3(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, internalValue]);
  return /* @__PURE__ */ jsxs4(InputContainer, { className, style, "data-disabled": disabled, children: [
    leadingIcon ? /* @__PURE__ */ jsx9(IconWrapper, { children: leadingIcon }) : null,
    /* @__PURE__ */ jsx9(
      Input,
      {
        type: "number",
        onChange: (event) => {
          const value2 = event.target.valueAsNumber;
          if (isNaN(value2)) {
            setInternalValue(void 0);
          } else if (props.min != null && value2 < Number(props.min) || props.max != null && value2 > Number(props.max)) {
            setInternalValue(value2);
          } else {
            setInternalValue(value2);
            onChange(value2);
          }
        },
        onBlur: () => {
          if (props.min != null && internalValue < Number(props.min)) {
            setInternalValue(Number(props.min));
            onChange(Number(props.min));
          } else if (props.max != null && internalValue > Number(props.max)) {
            setInternalValue(Number(props.max));
            onChange(Number(props.max));
          } else if (internalValue == null) {
            setInternalValue(valueOnFocus);
            onChange(valueOnFocus);
          }
          setValueOnFocus(void 0);
        },
        onKeyDown: (event) => {
          if (event.shiftKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
            event.preventDefault();
            const newValue = (internalValue ?? 0) + (event.key === "ArrowUp" ? 10 : -10);
            if (props.min != null && newValue < Number(props.min)) {
              setInternalValue(Number(props.min));
              onChange(Number(props.min));
            } else if (props.max != null && newValue > Number(props.max)) {
              setInternalValue(Number(props.max));
              onChange(Number(props.max));
            } else {
              setInternalValue(newValue);
              onChange(newValue);
            }
          }
        },
        value: internalValue ?? "",
        disabled,
        ref,
        min: props.min,
        max: props.max,
        step: props.step,
        onFocus: (event) => {
          event.target.select();
          setValueOnFocus(value);
        },
        ...props
      }
    ),
    trailingIcon ? /* @__PURE__ */ jsx9(IconWrapper, { children: trailingIcon }) : null
  ] });
});

// src/components/inputs/color.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
function outputFormat(color) {
  const c = colord(color);
  if (c.toRgb().a === 1) {
    return c.toHex();
  }
  return c.toRgbString();
}
function internalFormat(color) {
  return colord(color).toRgbString();
}
var ColorInput = ({
  onChange,
  value,
  className,
  style,
  variant = "input"
}) => {
  const [color, setColor] = useState3(value ? internalFormat(value) : "rgba(0, 0, 0, 0)");
  const [isChanging, setIsChanging] = useState3(false);
  const timeout = useRef2();
  useEffect4(() => {
    if (isChanging) {
      return;
    }
    if (!value) {
      setColor("rgba(0, 0, 0, 0)");
      return;
    }
    if (internalFormat(value) !== color) {
      setColor(internalFormat(value));
    }
  }, [internalFormat(value), isChanging]);
  if (variant === "input") {
    return /* @__PURE__ */ jsx10(Popover.Root, { children: /* @__PURE__ */ jsxs5(InputContainer, { className, style, children: [
      /* @__PURE__ */ jsx10(Popover.Trigger, { asChild: true, children: /* @__PURE__ */ jsx10(ColorPreview, { color: value, size: 16 }) }),
      /* @__PURE__ */ jsxs5(PopoverContent, { side: "left", sideOffset: 20, children: [
        /* @__PURE__ */ jsx10(ColorPickerWrapper, { children: /* @__PURE__ */ jsx10(
          RgbaStringColorPicker,
          {
            color,
            onChange: (color2) => {
              if (!value) {
                color2 = colord(color2).alpha(1).toRgbString();
              }
              setColor(internalFormat(color2));
              onChange(outputFormat(color2));
              setIsChanging(true);
              clearTimeout(timeout.current);
              timeout.current = window.setTimeout(() => {
                setIsChanging(false);
              }, 100);
            }
          }
        ) }),
        /* @__PURE__ */ jsx10(
          PresetColors,
          {
            colors: ["#000000", "#ffffff", "#ff0000", "#00ff00", "#0000ff"],
            onChange: (color2) => {
              onChange(outputFormat(color2));
            }
          }
        )
      ] }),
      /* @__PURE__ */ jsx10(
        HexColorInput,
        {
          color: colord(color).toHex(),
          onChange: (color2) => {
            onChange(outputFormat(color2));
          },
          style: {
            width: "100%",
            height: "100%",
            border: 0,
            outline: 0
          },
          onFocus: (event) => {
            event.target.select();
          }
        }
      ),
      /* @__PURE__ */ jsx10(
        AlphaInput,
        {
          value: colord(color).toRgb().a * 100,
          onChange: (number) => {
            const c = colord(color).alpha(number / 100);
            const newColor = c.toRgbString();
            setColor(newColor);
            onChange(outputFormat(newColor));
          },
          min: 0,
          max: 100,
          step: 1,
          onFocus: (event) => {
            event.target.select();
          }
        }
      ),
      /* @__PURE__ */ jsx10("span", { children: "%" })
    ] }) });
  }
  return /* @__PURE__ */ jsxs5("div", { className, style, children: [
    /* @__PURE__ */ jsxs5(ColorPickerWrapper, { children: [
      /* @__PURE__ */ jsx10(
        RgbaStringColorPicker,
        {
          color,
          onChange: (color2) => {
            setColor(internalFormat(color2));
            onChange(outputFormat(color2));
            setIsChanging(true);
            clearTimeout(timeout.current);
            timeout.current = window.setTimeout(() => {
              setIsChanging(false);
            }, 100);
          }
        }
      ),
      /* @__PURE__ */ jsx10(
        PresetColors,
        {
          colors: ["#000000", "#ffffff", "#ff0000", "#00ff00", "#0000ff"],
          onChange: (color2) => {
            onChange(outputFormat(color2));
          }
        }
      )
    ] }),
    /* @__PURE__ */ jsx10("div", { style: { padding: 4 }, children: /* @__PURE__ */ jsxs5(InputContainer, { style: { width: 192 }, children: [
      /* @__PURE__ */ jsx10(
        HexColorInput,
        {
          color: colord(color).toHex(),
          onChange: (color2) => {
            onChange(outputFormat(color2));
          },
          style: {
            width: "100%",
            height: "100%",
            border: 0,
            outline: 0
          },
          onFocus: (event) => {
            event.target.select();
          }
        }
      ),
      /* @__PURE__ */ jsx10(
        AlphaInput,
        {
          value: colord(color).toRgb().a * 100,
          onChange: (number) => {
            const c = colord(color).alpha(number / 100);
            const newColor = c.toRgbString();
            setColor(newColor);
            onChange(outputFormat(newColor));
          },
          min: 0,
          max: 100,
          step: 1,
          onFocus: (event) => {
            event.target.select();
          }
        }
      ),
      /* @__PURE__ */ jsx10("span", { children: "%" })
    ] }) })
  ] });
};
var PopoverContent = styled6(Popover.Content, {
  borderRadius: 4,
  padding: 0,
  backgroundColor: "white",
  boxShadow: "hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px",
  zIndex: 1,
  outline: 0
});
var ColorPickerWrapper = styled6("div", {
  ".react-colorful__saturation": {
    borderRadius: "4px 4px 0 0"
  },
  ".react-colorful__pointer": {
    width: "12px",
    height: "12px"
  },
  ".react-colorful__hue": {
    height: "10px",
    margin: "8px 8px 0 8px",
    borderRadius: "4px"
  },
  ".react-colorful__alpha": {
    height: "10px",
    margin: "8px",
    borderRadius: "4px"
  }
});
var AlphaInput = styled6(NumberInput, {
  marginLeft: 4,
  width: "30px",
  flexShrink: 0,
  textAlign: "right",
  border: 0,
  outline: 0,
  padding: 0,
  boxShadow: "none",
  borderRadius: 0,
  minHeight: "auto",
  input: {
    borderLeft: "1px solid #C7C7D4"
  },
  borderColor: "#DFE0E8",
  // breaking the rules
  /* Chrome, Safari, Edge, Opera */
  "&::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0
  },
  "&::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0
  },
  /* Firefox */
  "&[type=number]": {
    "-moz-appearance": "textfield"
  },
  "&:focus-within": {
    outline: "none",
    borderColor: "#C7C7D4"
  }
});
function PresetColors({ colors, onChange }) {
  return /* @__PURE__ */ jsx10(
    "div",
    {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        padding: "4px 8px 8px 8px"
      },
      children: colors.map((color) => /* @__PURE__ */ jsx10(
        "button",
        {
          style: {
            width: "18px",
            height: "18px",
            borderRadius: "2px",
            backgroundColor: color,
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
            boxShadow: "0px 0px 1px 0px rgba(0,0,0,0.4) inset"
          },
          onClick: () => onChange(color)
        },
        color
      ))
    }
  );
}

// src/visual-editor/overlays/bubble-menu.tsx
import { marks } from "@customerio/carta-visualized";
import * as RadixDialog from "@radix-ui/react-dialog";
import * as Select from "@radix-ui/react-select";
import * as Toolbar from "@radix-ui/react-toolbar";
import { styled as styled7 } from "@stitches/react";
import { useEffect as useEffect5, useRef as useRef3, useState as useState4 } from "react";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
function useShowLinkPopover({ isActive }) {
  const { visualEditor } = useEncompass();
  const [state, setState] = useState4("hidden");
  useEffect5(() => {
    if (isActive) {
      setState("auto-visible");
    } else {
      setState("hidden");
    }
  }, [isActive]);
  useEffect5(() => {
    if (state !== "auto-visible") {
      return;
    }
    if (!visualEditor.state.textSelection) {
      return;
    }
    if (visualEditor.state.textSelection.isSelectionCollapsed) {
      return;
    }
    setState("hidden");
  }, [
    state === "auto-visible",
    !!visualEditor.state.textSelection,
    visualEditor.state.textSelection?.isSelectionCollapsed
  ]);
  return [
    state === "auto-visible" || state === "manual-visible",
    (visible) => {
      if (visible) {
        setState("manual-visible");
      } else {
        setState("hidden");
      }
    },
    state
  ];
}
function BubbleMenu() {
  const { visualEditor } = useEncompass();
  const { textSelection } = visualEditor.state;
  const [showLinkPopover, setShowLinkPopover, linkPopoverState] = useShowLinkPopover({
    isActive: textSelection?.commandStates?.cartaVisualizedLink?.state?.active === true
  });
  const [showColorPopover, setShowColorPopover] = useState4(false);
  if (visualEditor.state.contentTarget == null || !textSelection) {
    return null;
  }
  const { commandStates } = textSelection;
  if (!commandStates) {
    return null;
  }
  const componentDefintion = visualEditor.actions.getComponentDefinition(visualEditor.state.contentTarget.name);
  let allowedMarks = marks;
  if (visualEditor.state.contentTarget.type === "slot") {
    const slotDefintion = visualEditor.actions.getSlotDefinition(
      visualEditor.state.contentTarget.name,
      visualEditor.state.contentTarget.slot
    );
    if (slotDefintion.type === "text") {
      allowedMarks = slotDefintion.marks;
    }
  }
  const onToolbarMouseCommand = (event, command, value) => {
    event.stopPropagation();
    visualEditor.actions.editorExecCommand(command, value);
  };
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    shouldShowBubbleMenu(visualEditor) ? /* @__PURE__ */ jsx11(
      MarshaledPreviewTooltip,
      {
        target: {
          // add 6px of padding to the bounding box
          top: textSelection.selectionBoundingBox.top - 6,
          left: textSelection.selectionBoundingBox.left - 6,
          width: textSelection.selectionBoundingBox.width + 12,
          height: textSelection.selectionBoundingBox.height + 12
        },
        style: { pointerEvents: "auto" },
        zIndex: 910,
        children: /* @__PURE__ */ jsx11(ToolbarRoot, { "data-test-id": "encompass-bubble-menu", children: Object.keys(commandStates).map((key) => {
          let cmd = commandStates[key];
          if (cmd?.mark && !allowedMarks.includes(cmd.mark)) {
            return null;
          }
          switch (key) {
            case "cartaVisualizedLink": {
              return /* @__PURE__ */ jsx11(
                ToolbarButton,
                {
                  title: "Link",
                  "data-state": cmd.state.active ? "on" : "off",
                  onClick: () => {
                    setShowLinkPopover(!showLinkPopover);
                  },
                  children: /* @__PURE__ */ jsx11(Icon, { size: 16, icon: "link" })
                },
                key
              );
            }
            case "cartaVisualizedRemoveLink": {
              return null;
            }
            case "cartaVisualizedColor": {
              return /* @__PURE__ */ jsx11(
                ToolbarButton,
                {
                  title: "Color",
                  onClick: () => {
                    setShowColorPopover(!showColorPopover);
                  },
                  children: /* @__PURE__ */ jsx11(ColorPreview, { color: cmd.state.color, size: 14 })
                },
                key
              );
            }
            default: {
              return /* @__PURE__ */ jsx11(
                ToolbarButton,
                {
                  onMouseDown: (e) => onToolbarMouseCommand(e, key),
                  title: cmd.title,
                  "data-state": cmd.state === true ? "on" : "off",
                  children: /* @__PURE__ */ jsx11(Icon, { size: 16, icon: "buttonIcon" in cmd ? cmd.buttonIcon : key })
                },
                key
              );
            }
          }
        }) })
      }
    ) : null,
    showLinkPopover ? /* @__PURE__ */ jsx11(
      LinkPopover,
      {
        onClose: () => {
          setShowLinkPopover(false);
        },
        shouldAutoFocus: linkPopoverState === "manual-visible"
      }
    ) : null,
    showColorPopover ? /* @__PURE__ */ jsx11(
      ColorPopover,
      {
        onClose: () => {
          setShowColorPopover(false);
        }
      }
    ) : null
  ] });
}
var ToolbarRoot = styled7(Toolbar.Root, {
  display: "flex",
  alignItems: "center",
  width: "100%",
  minWidth: "max-content",
  borderRadius: 4,
  backgroundColor: "white",
  padding: 4,
  filter: "drop-shadow(2px 2px 4px rgba(52, 52, 70, 0.01)) drop-shadow(1px 2px 2px rgba(52, 52, 70, 0.04))",
  border: "1px solid #EBECF2"
});
var itemStyles = {
  all: "unset",
  flex: "0 0 auto",
  color: "#343446",
  height: 25,
  minWidth: 25,
  boxSizing: "border-box",
  padding: "0 5px",
  borderRadius: 4,
  display: "inline-flex",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": { backgroundColor: "#F7F8FA" },
  "&:focus": { position: "relative", boxShadow: `0 0 0 2px #843CC7` }
};
var ToolbarButton = styled7(Toolbar.Button, {
  ...itemStyles,
  marginLeft: 2,
  "&:first-child": { marginLeft: 0 },
  "&[data-state=on]": { background: "#EBECF2" }
});
var ToolbarSelectTrigger = styled7(Select.Trigger, {
  display: "flex",
  alignItems: "center",
  backgroundColor: "white",
  borderColor: "transparent"
});
var ToolbarSelectTriggerValue = styled7("span", {
  lineHeight: "20px",
  fontSize: 12,
  fontWeight: 500
});
var ToolbarSelectTriggerIcon = styled7(Select.Icon, {
  marginLeft: 6
});
var ToolbarSelectItem = styled7(Select.Item, {
  padding: "2px 20px 2px 4px",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "20px",
  "[data-highlighted], &:hover, &:focus": {
    borderColor: "transparent",
    outline: "none",
    backgroundColor: "#F7F8FA",
    borderRadius: 4
  }
});
var ToolbarSelectContent = styled7(Select.Content, {
  backgroundColor: "white",
  borderRadius: 4,
  border: "1px solid #EBECF2",
  padding: 6,
  cursor: "pointer",
  marginTop: 8,
  marginLeft: -4
});
var ToolbarSeparator = styled7(Toolbar.Separator, {
  width: 1,
  height: 16,
  backgroundColor: "#C7C7D4",
  margin: "0 4px"
});
function shouldShowBubbleMenu(visualEditor) {
  const { textSelection } = visualEditor.state;
  if (visualEditor.state.editMode !== "content") {
    return false;
  }
  if (!visualEditor.state.contentTarget) {
    return false;
  }
  if (!textSelection) {
    return false;
  }
  if (textSelection.isSelectionCollapsed) {
    return false;
  }
  return true;
}
function LinkPopover({ onClose, shouldAutoFocus }) {
  const { visualEditor } = useEncompass();
  const { textSelection } = visualEditor.state;
  const state = textSelection?.commandStates?.cartaVisualizedLink.state;
  const [href, setHref] = useState4(state.href ?? "");
  useEffect5(() => {
    setHref(state.href ?? "");
  }, [state.href]);
  const urlInputRef = useRef3(null);
  useEffect5(() => {
    if (shouldAutoFocus) {
      urlInputRef.current?.focus();
    }
  }, [shouldAutoFocus]);
  useEffect5(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        visualEditor.actions.focus();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);
  return /* @__PURE__ */ jsx11(
    Popover2,
    {
      target: {
        ...textSelection.selectionBoundingBox,
        top: textSelection.selectionBoundingBox.top - 10,
        height: textSelection.selectionBoundingBox.height + 20
      },
      onClose,
      focusLock: shouldAutoFocus,
      children: /* @__PURE__ */ jsxs6(
        "form",
        {
          onSubmit: (event) => {
            event.preventDefault();
            visualEditor.actions.editorExecCommand("cartaVisualizedLink", {
              href
            });
            onClose();
          },
          style: {
            maxWidth: "100%",
            width: 300,
            margin: 0
          },
          children: [
            /* @__PURE__ */ jsx11(
              "label",
              {
                style: {
                  display: "inline-block",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "13px",
                  color: "#6F7488",
                  marginBottom: 4
                },
                children: "URL"
              }
            ),
            /* @__PURE__ */ jsx11(
              TextInput,
              {
                ref: urlInputRef,
                value: href,
                onChange: (href2) => {
                  setHref(href2);
                },
                style: {
                  marginBottom: 8
                }
              }
            ),
            /* @__PURE__ */ jsxs6(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 12,
                  gap: 8
                },
                children: [
                  /* @__PURE__ */ jsx11(
                    Button,
                    {
                      type: "button",
                      onClick: () => {
                        visualEditor.actions.editorExecCommand("cartaVisualizedRemoveLink");
                        onClose();
                      },
                      variant: "danger",
                      children: "Remove"
                    }
                  ),
                  /* @__PURE__ */ jsxs6("div", { children: [
                    /* @__PURE__ */ jsx11(
                      Button,
                      {
                        type: "button",
                        onClick: () => {
                          visualEditor.actions.focus();
                          onClose();
                        },
                        variant: "secondary",
                        children: "Cancel"
                      }
                    ),
                    /* @__PURE__ */ jsx11(Button, { type: "submit", variant: "primary", children: "Save" })
                  ] })
                ]
              }
            )
          ]
        }
      )
    }
  );
}
function ColorPopover({ onClose }) {
  const { visualEditor } = useEncompass();
  const { textSelection } = visualEditor.state;
  const state = textSelection?.commandStates?.cartaVisualizedColor.state;
  const [color, setColor] = useState4(state.color);
  useEffect5(() => {
    setColor(state.color);
  }, [state.color]);
  return /* @__PURE__ */ jsx11(
    Popover2,
    {
      onClose,
      target: {
        ...textSelection.selectionBoundingBox,
        top: textSelection.selectionBoundingBox.top - 10,
        height: textSelection.selectionBoundingBox.height + 20
      },
      style: { padding: 0 },
      children: /* @__PURE__ */ jsxs6(
        "form",
        {
          onSubmit: (event) => {
            event.preventDefault();
            visualEditor.actions.editorExecCommand("cartaVisualizedColor", color);
            onClose();
          },
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              visualEditor.actions.editorExecCommand("cartaVisualizedColor", color);
              onClose();
            }
          },
          style: { margin: 0 },
          children: [
            /* @__PURE__ */ jsx11(
              ColorInput,
              {
                variant: "inline",
                value: color,
                onChange: (color2) => {
                  setColor(color2);
                }
              }
            ),
            /* @__PURE__ */ jsxs6(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "8px",
                  gap: 8
                },
                children: [
                  /* @__PURE__ */ jsx11(
                    "button",
                    {
                      type: "button",
                      onClick: () => {
                        visualEditor.actions.editorExecCommand("cartaVisualizedColor", "");
                        onClose();
                      },
                      style: {
                        background: "transparent",
                        color: "#DB234B",
                        border: "none",
                        borderRadius: 3,
                        padding: "4px 12px",
                        font: "inherit",
                        fontWeight: 600,
                        fontSize: 12,
                        lineHeight: "20px",
                        cursor: "pointer"
                      },
                      children: "Remove"
                    }
                  ),
                  /* @__PURE__ */ jsx11(
                    "button",
                    {
                      type: "submit",
                      style: {
                        background: "rgba(87, 33, 204, 1)",
                        color: "white",
                        border: "none",
                        borderRadius: 3,
                        padding: "4px 12px",
                        font: "inherit",
                        fontWeight: 600,
                        fontSize: 12,
                        lineHeight: "20px",
                        cursor: "pointer"
                      },
                      children: "Save"
                    }
                  )
                ]
              }
            )
          ]
        }
      )
    }
  );
}
function Popover2({
  target,
  onClose,
  children,
  focusLock = true,
  style
}) {
  return /* @__PURE__ */ jsx11(
    MarshaledPreviewTooltip,
    {
      target,
      style: {
        backgroundColor: "white",
        borderRadius: 4,
        border: "1px solid #EBECF2",
        padding: 16,
        filter: "drop-shadow(2px 2px 4px rgba(52, 52, 70, 0.01)) drop-shadow(1px 2px 2px rgba(52, 52, 70, 0.04))",
        ...style
      },
      zIndex: 911,
      interactive: true,
      children: focusLock ? /* @__PURE__ */ jsxs6(
        RadixDialog.Root,
        {
          open: true,
          onOpenChange: (open) => {
            if (!open) {
              onClose();
            }
          },
          children: [
            /* @__PURE__ */ jsx11(RadixDialog.Overlay, {}),
            /* @__PURE__ */ jsx11(RadixDialog.Content, { children })
          ]
        }
      ) : children
    }
  );
}

// src/visual-editor/overlays/drop-targets.tsx
import { useDroppable } from "@dnd-kit/core";
import { motion } from "framer-motion";
import { Fragment as Fragment5, useId } from "react";
import { Fragment as Fragment6, jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
function DropTarget({ node }) {
  const { visualEditor } = useEncompass();
  if (node.type === "slot") {
    const definition = visualEditor.actions.getSlotDefinition(node.name, node.slot);
    if (definition?.type !== "children") {
      return null;
    }
    const { children } = node;
    return /* @__PURE__ */ jsxs7(Fragment6, { children: [
      children.map((child, index) => /* @__PURE__ */ jsxs7(Fragment5, { children: [
        /* @__PURE__ */ jsx12(
          SlotDropTargetLocation,
          {
            target: node,
            direction: definition.direction,
            allowedChildren: definition.allowedChildren,
            index,
            top: definition.direction === "horizontal" ? node.boundingBox.y : child.boundingBox.y,
            left: definition.direction === "horizontal" ? child.boundingBox.x : node.boundingBox.x,
            width: definition.direction === "horizontal" ? child.boundingBox.width : node.boundingBox.width,
            height: definition.direction === "horizontal" ? node.boundingBox.height : child.boundingBox.height
          }
        ),
        /* @__PURE__ */ jsx12(DropTarget, { node: child }, index),
        index === children.length - 1 && (definition.direction === "horizontal" ? /* @__PURE__ */ jsx12(
          SlotDropTargetLocation,
          {
            target: node,
            direction: definition.direction,
            allowedChildren: definition.allowedChildren,
            index: -1,
            top: node.boundingBox.y,
            left: child.boundingBox.x + child.boundingBox.width,
            width: child.boundingBox.width,
            height: node.boundingBox.height
          }
        ) : /* @__PURE__ */ jsx12(
          SlotDropTargetLocation,
          {
            target: node,
            direction: definition.direction,
            allowedChildren: definition.allowedChildren,
            index: index + 1,
            top: child.boundingBox.y + child.boundingBox.height,
            left: node.boundingBox.x,
            width: node.boundingBox.width,
            height: child.boundingBox.height
          }
        ))
      ] }, index)),
      children.length === 0 ? /* @__PURE__ */ jsx12(
        SlotDropTargetLocation,
        {
          target: node,
          direction: definition.direction,
          allowedChildren: definition.allowedChildren,
          index: -1,
          top: node.boundingBox.y,
          left: node.boundingBox.x,
          width: node.boundingBox.width,
          height: node.boundingBox.height,
          cover: true
        }
      ) : null
    ] });
  }
  return /* @__PURE__ */ jsx12(Fragment6, { children: node.children.map((child, index) => /* @__PURE__ */ jsx12(
    DropTarget,
    {
      node: {
        ...child,
        boundingBox: (
          /**
           * If the child has no bounding box, use the parent's bounding box
           */
          child.boundingBox.x === null && child.boundingBox.y === null && child.boundingBox.width === null && child.boundingBox.height === null && "boundingBox" in node ? node.boundingBox : child.boundingBox
        )
      }
    },
    index
  )) });
}
function SlotDropTargetLocation({
  target,
  index,
  direction,
  allowedChildren,
  top,
  left,
  width,
  height,
  cover
}) {
  const id = useId();
  const { setNodeRef, isOver, active } = useDroppable({
    id,
    data: { target, index }
  });
  if (allowedChildren.length === 0) {
    return null;
  }
  const isAllowedToDrop = allowedChildren.includes(active?.data?.current?.target.name);
  if (!isAllowedToDrop) {
    return null;
  }
  return /* @__PURE__ */ jsx12(
    MarshaledPreviewOverlay,
    {
      ref: setNodeRef,
      top,
      left,
      interactive: true,
      style: {
        zIndex: 1,
        ...direction === "horizontal" ? {
          width: "2px",
          height
        } : {
          zIndex: 1,
          width,
          height: "2px"
        },
        ...cover ? {
          width,
          height
        } : {}
      },
      children: isOver && /* @__PURE__ */ jsx12(
        motion.div,
        {
          style: {
            background: "#0652BB",
            height: "100%",
            width: "100%"
          },
          layoutId: "drop-target-target",
          transition: {
            duration: 0,
            ease: "easeOut"
          }
        }
      )
    }
  );
}

// src/components/dropdown-menu.tsx
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { styled as styled8 } from "@stitches/react";
import { jsx as jsx13, jsxs as jsxs8 } from "react/jsx-runtime";
var { Root: Root5, Trigger: Trigger3, Portal, Label, Group, CheckboxItem, RadioGroup, RadioItem, ItemIndicator, Sub } = RadixDropdownMenu;
var Content5 = styled8(RadixDropdownMenu.Content, {
  minWidth: 220,
  backgroundColor: "white",
  borderRadius: 4,
  overflow: "hidden",
  boxShadow: "rgb(73 79 186 / 8%) 0 3px 4px 0px, rgb(73 79 186 / 2%) 0 -1px 3px 0px",
  border: "1px solid #EBECF2",
  zIndex: 999,
  marginTop: 4
});
var Item3 = styled8(RadixDropdownMenu.Item, {
  fontSize: 13,
  lineHeight: 1,
  color: "#343446",
  display: "flex",
  alignItems: "center",
  height: 32,
  padding: "0 8px",
  position: "relative",
  paddingLeft: 25,
  userSelect: "none",
  outline: "none",
  cursor: "pointer",
  "&[data-disabled]": {
    color: "#A1A2B2",
    pointerEvents: "none",
    cursor: "not-allowed"
  },
  "&[data-highlighted]": {
    backgroundColor: "#F7F8FA"
  }
});
var SubTrigger2 = styled8(
  function SubTrigger3(props) {
    return /* @__PURE__ */ jsxs8(RadixDropdownMenu.SubTrigger, { ...props, children: [
      props.children,
      /* @__PURE__ */ jsx13(RightSlot, { children: /* @__PURE__ */ jsx13(Icon, { icon: "chevron_right", size: 14 }) })
    ] });
  },
  {
    fontSize: 13,
    lineHeight: 1,
    color: "#343446",
    display: "flex",
    alignItems: "center",
    height: 32,
    padding: "0 8px",
    position: "relative",
    paddingLeft: 25,
    userSelect: "none",
    outline: "none",
    cursor: "pointer",
    "&[data-disabled]": {
      color: "#A1A2B2",
      pointerEvents: "none",
      cursor: "not-allowed"
    },
    "&[data-highlighted]": {
      backgroundColor: "#F7F8FA"
    }
  }
);
var SubContent2 = styled8(RadixDropdownMenu.SubContent, {
  minWidth: 220,
  backgroundColor: "white",
  borderRadius: 4,
  overflow: "hidden",
  boxShadow: "rgb(73 79 186 / 8%) 0 3px 4px 0px, rgb(73 79 186 / 2%) 0 -1px 3px 0px",
  border: "1px solid #EBECF2",
  zIndex: 999,
  marginLeft: 4,
  marginRight: 4
});
var RightSlot = styled8("div", {
  marginLeft: "auto",
  paddingLeft: 20
});
var Separator3 = styled8(RadixDropdownMenu.Separator, {
  height: 1,
  backgroundColor: "#EBECF2",
  margin: "4px 8px"
});

// src/visual-editor/overlays/interaction-overlays.tsx
import {
  CARTA_DATA_TEST_ATTRIBUTE,
  IS_CIO_FLY_ENVIRONMENT,
  IS_DEV_ENVIRONMENT
} from "@customerio/carta-visualized";
import { useDraggable } from "@dnd-kit/core";

// ../../node_modules/@dnd-kit/utilities/dist/utilities.esm.js
import { useMemo, useLayoutEffect, useEffect as useEffect6, useRef as useRef4, useCallback } from "react";
var canUseDOM = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined";
function isWindow(element) {
  const elementString = Object.prototype.toString.call(element);
  return elementString === "[object Window]" || // In Electron context the Window object serializes to [object global]
  elementString === "[object global]";
}
function isNode(node) {
  return "nodeType" in node;
}
function getWindow(target) {
  var _target$ownerDocument, _target$ownerDocument2;
  if (!target) {
    return window;
  }
  if (isWindow(target)) {
    return target;
  }
  if (!isNode(target)) {
    return window;
  }
  return (_target$ownerDocument = (_target$ownerDocument2 = target.ownerDocument) == null ? void 0 : _target$ownerDocument2.defaultView) != null ? _target$ownerDocument : window;
}
function useInterval() {
  const intervalRef = useRef4(null);
  const set2 = useCallback((listener, duration) => {
    intervalRef.current = setInterval(listener, duration);
  }, []);
  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  return [set2, clear];
}
function hasViewportRelativeCoordinates(event) {
  return "clientX" in event && "clientY" in event;
}
function isTouchEvent(event) {
  if (!event) {
    return false;
  }
  const {
    TouchEvent
  } = getWindow(event.target);
  return TouchEvent && event instanceof TouchEvent;
}
function getEventCoordinates(event) {
  if (isTouchEvent(event)) {
    if (event.touches && event.touches.length) {
      const {
        clientX: x,
        clientY: y
      } = event.touches[0];
      return {
        x,
        y
      };
    } else if (event.changedTouches && event.changedTouches.length) {
      const {
        clientX: x,
        clientY: y
      } = event.changedTouches[0];
      return {
        x,
        y
      };
    }
  }
  if (hasViewportRelativeCoordinates(event)) {
    return {
      x: event.clientX,
      y: event.clientY
    };
  }
  return null;
}
var CSS = /* @__PURE__ */ Object.freeze({
  Translate: {
    toString(transform) {
      if (!transform) {
        return;
      }
      const {
        x,
        y
      } = transform;
      return "translate3d(" + (x ? Math.round(x) : 0) + "px, " + (y ? Math.round(y) : 0) + "px, 0)";
    }
  },
  Scale: {
    toString(transform) {
      if (!transform) {
        return;
      }
      const {
        scaleX,
        scaleY
      } = transform;
      return "scaleX(" + scaleX + ") scaleY(" + scaleY + ")";
    }
  },
  Transform: {
    toString(transform) {
      if (!transform) {
        return;
      }
      return [CSS.Translate.toString(transform), CSS.Scale.toString(transform)].join(" ");
    }
  },
  Transition: {
    toString(_ref) {
      let {
        property,
        duration,
        easing
      } = _ref;
      return property + " " + duration + "ms " + easing;
    }
  }
});

// src/visual-editor/overlays/interaction-overlays.tsx
import { styled as styled10 } from "@stitches/react";
import { Resizable as BaseResizable } from "re-resizable";
import React2, { useEffect as useEffect7, useId as useId2, useRef as useRef5, useState as useState6, useMemo as useMemo2 } from "react";
import * as RadixDialog2 from "@radix-ui/react-dialog";
import { CommandInput as CmdKCommandInput, Command } from "cmdk";

// src/visual-editor/hooks/use-is-scrolling.ts
import { useLayoutEffect as useLayoutEffect2, useState as useState5 } from "react";
function useIsScrolling() {
  const { visualEditor } = useEncompass();
  const [isScrolling, setIsScrolling] = useState5(false);
  useLayoutEffect2(() => {
    setIsScrolling(true);
    const timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 200);
    return () => {
      clearTimeout(timeout);
    };
  }, [
    visualEditor.state.scroll?.top,
    visualEditor.state.scroll?.height,
    visualEditor.state.scroll?.left,
    visualEditor.state.scroll?.width
  ]);
  return isScrolling;
}

// src/visual-editor/overlays/interaction-overlays.tsx
import { OFF_CANVAS_SPACE_PADDING } from "@customerio/marshaled-preview/react";

// src/components/svg-icon.tsx
import { Slot as Slot3 } from "@radix-ui/react-slot";
import * as RadixAccessibleIcon2 from "@radix-ui/react-accessible-icon";
import { styled as styled9 } from "@stitches/react";
import { Fragment as Fragment7, forwardRef as forwardRef5 } from "react";
import { jsx as jsx14 } from "react/jsx-runtime";
var SvgIcon = styled9(
  forwardRef5(function Icon4({ label, svg, asChild, onClick, ...props }, ref) {
    const Comp = asChild ? Slot3 : onClick ? "button" : "span";
    const renderComp = /* @__PURE__ */ jsx14(
      Comp,
      {
        ...props,
        onClick,
        "data-type": "svg",
        className: props.className ?? "",
        style: { ...props.style ?? {} },
        ref,
        dangerouslySetInnerHTML: { __html: svg ?? "" }
      }
    );
    return label ? /* @__PURE__ */ jsx14(RadixAccessibleIcon2.Root, { label, children: renderComp }) : /* @__PURE__ */ jsx14(Fragment7, { children: renderComp });
  }),
  {
    background: "transparent",
    border: "none",
    fontWeight: "inherit",
    lineHeight: "inherit",
    padding: 0,
    margin: 0
  }
);

// src/icons.tsx
var icons = {
  properties: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.16673 3.83337C5.43043 3.83337 4.83355 4.43026 4.83355 5.16656C4.83355 5.90286 5.43043 6.49974 6.16673 6.49974C6.90303 6.49974 7.49992 5.90286 7.49992 5.16656C7.49992 4.43026 6.90303 3.83337 6.16673 3.83337ZM3.87526 5.66663L3.88723 5.66648C4.11609 6.71483 5.04975 7.49974 6.16673 7.49974C7.28372 7.49974 8.21737 6.71483 8.44623 5.66648L8.45823 5.66663H13.4995C13.7756 5.66663 13.9995 5.44277 13.9995 5.16663C13.9995 4.89048 13.7756 4.66663 13.4995 4.66663H8.45823L8.44626 4.66677C8.21745 3.61835 7.28376 2.83337 6.16673 2.83337C5.0497 2.83337 4.11601 3.61835 3.8872 4.66677L3.87526 4.66663H2.50037C2.22422 4.66663 2.00037 4.89048 2.00037 5.16663C2.00037 5.44277 2.22422 5.66663 2.50037 5.66663H3.87526ZM12.1126 11.3334C11.8838 12.3818 10.9501 13.1667 9.83311 13.1667C8.7161 13.1667 7.78242 12.3818 7.55359 11.3334C7.54962 11.3335 7.54563 11.3335 7.54164 11.3335H2.50038C2.22424 11.3335 2.00038 11.1097 2.00038 10.8335C2.00038 10.5574 2.22424 10.3335 2.50038 10.3335H7.54164C7.54564 10.3335 7.54962 10.3336 7.5536 10.3337C7.78244 9.2853 8.71611 8.50037 9.83311 8.50037C10.9501 8.50037 11.8838 9.2853 12.1126 10.3337C12.1166 10.3336 12.1206 10.3335 12.1246 10.3335H13.4995C13.7756 10.3335 13.9995 10.5574 13.9995 10.8335C13.9995 11.1097 13.7756 11.3335 13.4995 11.3335H12.1246C12.1206 11.3335 12.1166 11.3335 12.1126 11.3334ZM8.49992 10.8336C8.49992 10.0973 9.09681 9.50037 9.83311 9.50037C10.5694 9.50037 11.1663 10.0973 11.1663 10.8336C11.1663 11.5698 10.5694 12.1667 9.83311 12.1667C9.09681 12.1667 8.49992 11.5698 8.49992 10.8336Z" fill="#FFFFFF"/>
  </svg>`,
  duplicate: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.8334 5.33333C11.8334 4.689 11.3111 4.16666 10.6667 4.16666L3.33341 4.16666C2.68908 4.16666 2.16675 4.689 2.16675 5.33333L2.16675 12.6667C2.16675 13.311 2.68908 13.8333 3.33342 13.8333H10.6668C11.3111 13.8333 11.8334 13.311 11.8334 12.6667L11.8334 5.33333ZM10.6667 5.16666C10.7588 5.16666 10.8334 5.24128 10.8334 5.33333L10.8334 12.6667C10.8334 12.7587 10.7588 12.8333 10.6668 12.8333L3.33342 12.8333C3.24137 12.8333 3.16675 12.7587 3.16675 12.6667L3.16675 5.33333C3.16675 5.24128 3.24137 5.16666 3.33341 5.16666L10.6667 5.16666Z" fill="#FFFFFF"/>
  <path d="M7.50008 7C7.50008 6.72385 7.27623 6.5 7.00008 6.5C6.72394 6.5 6.50008 6.72385 6.50008 7V8.5H5.00008C4.72394 8.5 4.50008 8.72385 4.50008 9C4.50008 9.27614 4.72394 9.5 5.00008 9.5H6.50008V11C6.50008 11.2761 6.72394 11.5 7.00008 11.5C7.27622 11.5 7.50008 11.2761 7.50008 11V9.5H9.00008C9.27623 9.5 9.50008 9.27614 9.50008 9C9.50008 8.72385 9.27623 8.5 9.00008 8.5L7.50008 8.5V7Z" fill="#FFFFFF"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3334 11.1667C13.6095 11.1667 13.8334 10.9428 13.8334 10.6667L13.8334 3.33335C13.8334 2.68902 13.3111 2.16669 12.6667 2.16669L5.33341 2.16669C5.05727 2.16669 4.83341 2.39054 4.83341 2.66669C4.83341 2.94283 5.05727 3.16669 5.33341 3.16669H12.6667C12.7588 3.16669 12.8334 3.24131 12.8334 3.33335L12.8334 10.6667C12.8334 10.9428 13.0573 11.1667 13.3334 11.1667Z" fill="#FFFFFF"/>
  </svg>`,
  trash: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66671 1.5C6.39057 1.5 6.16671 1.72386 6.16671 2V2.16667H4.00004C3.35571 2.16667 2.83337 2.689 2.83337 3.33333V4.66667C2.83337 4.94281 3.05723 5.16667 3.33337 5.16667H3.50004V12.6667C3.50004 13.6792 4.32085 14.5 5.33337 14.5H10.6667C11.6792 14.5 12.5 13.6792 12.5 12.6667V5.16667H12.6667C12.9428 5.16667 13.1667 4.94281 13.1667 4.66667V3.33333C13.1667 2.689 12.6444 2.16667 12 2.16667H9.83337V2C9.83337 1.72386 9.60952 1.5 9.33337 1.5H6.66671ZM11.5 5.16667H4.50004V12.6667C4.50004 13.1269 4.87314 13.5 5.33337 13.5H10.6667C11.1269 13.5 11.5 13.1269 11.5 12.6667V5.16667ZM12.1667 4.16667V3.33333C12.1667 3.24129 12.0921 3.16667 12 3.16667H4.00004C3.90799 3.16667 3.83337 3.24129 3.83337 3.33333V4.16667H12.1667Z" fill="#FFFFFF"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.33337 6.16667C9.60952 6.16667 9.83337 6.39052 9.83337 6.66667V12C9.83337 12.2761 9.60952 12.5 9.33337 12.5C9.05723 12.5 8.83337 12.2761 8.83337 12V6.66667C8.83337 6.39052 9.05723 6.16667 9.33337 6.16667Z" fill="#FFFFFF"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66671 6.16667C6.94285 6.16667 7.16671 6.39052 7.16671 6.66667L7.16671 12C7.16671 12.2761 6.94285 12.5 6.66671 12.5C6.39056 12.5 6.16671 12.2761 6.16671 12L6.16671 6.66667C6.16671 6.39052 6.39057 6.16667 6.66671 6.16667Z" fill="#FFFFFF"/>
  </svg>`
};
var icons_default = icons;

// src/visual-editor/overlays/interaction-overlays.tsx
import { Fragment as Fragment8, jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var isCioFlyEnvironment = IS_CIO_FLY_ENVIRONMENT();
var isDevEnvironment = IS_DEV_ENVIRONMENT();
function InteractionOverlays({ node, parent }) {
  if (node.type === "component") {
    return /* @__PURE__ */ jsx15(ComponentOverlays, { node, parent });
  }
  return /* @__PURE__ */ jsx15(Fragment8, { children: node.children.map((child, index) => /* @__PURE__ */ jsx15(InteractionOverlays, { node: child, parent: node }, index)) });
}
function ComponentOverlays({ node, parent }) {
  const { visualEditor } = useEncompass();
  const id = useId2();
  const isScrolling = useIsScrolling();
  const multipleComponentsSelected = visualEditor.state.selectedComponents.length > 1;
  const bubbleMenuIsVisible = shouldShowBubbleMenu(visualEditor);
  const slashMenuIsVisible = visualEditor.state.commandMenuMode !== null;
  const definition = visualEditor.actions.getComponentDefinition(node.name);
  const isSelected = visualEditor.state.selectedComponents.some((selected) => {
    return selected.index === node.index && selected.name === node.name;
  });
  const isHovered = node.index === visualEditor.state.hoveredComponent?.index && node.name === visualEditor.state.hoveredComponent?.name;
  const isContentTarget = visualEditor.state.contentTarget?.index === node.index && visualEditor.state.contentTarget?.name === node.name;
  const componentTarget = {
    type: "component",
    name: node.name,
    index: node.index
  };
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable({
    id,
    data: {
      action: "move",
      target: componentTarget
    }
  });
  if (!definition) {
    return null;
  }
  if (isDragging) {
    return /* @__PURE__ */ jsx15(DraggingOverlay, { node, setNodeRef, transform });
  }
  const scrollingStyle = isScrolling || visualEditor.state.isLoading ? {
    opacity: 0,
    pointerEvents: "none"
  } : {
    opacity: 1,
    pointerEvents: "auto"
  };
  return /* @__PURE__ */ jsxs9(Fragment8, { children: [
    /* @__PURE__ */ jsx15(
      DragHandle,
      {
        node,
        definition,
        listeners,
        attributes,
        isContentTarget,
        bubbleMenuIsVisible,
        slashMenuIsVisible,
        isSelected,
        isHovered,
        scrollingStyle
      }
    ),
    /* @__PURE__ */ jsx15(
      ComponentMenu,
      {
        node,
        attributes,
        isContentTarget,
        multipleComponentsSelected,
        isSelected,
        scrollingStyle
      }
    ),
    /* @__PURE__ */ jsx15(
      ComponentLabel,
      {
        node,
        definition,
        isSelected,
        isHovered,
        isContentTarget,
        scrollingStyle
      }
    ),
    /* @__PURE__ */ jsx15(AddButton, { node, parent, isHovered, scrollingStyle }),
    /* @__PURE__ */ jsx15(
      ResizableSizeControl,
      {
        node,
        parent,
        definition,
        bubbleMenuIsVisible,
        slashMenuIsVisible,
        isContentTarget,
        isSelected,
        scrollingStyle
      }
    ),
    /* @__PURE__ */ jsx15(
      ComponentSelectionOverlay,
      {
        node,
        isContentTarget,
        isSelected,
        isHovered,
        scrollingStyle
      }
    ),
    node.children.map((child, index) => /* @__PURE__ */ jsx15(InteractionOverlays, { node: child, parent: node }, index))
  ] });
}
function DraggingOverlay({
  node,
  setNodeRef,
  transform
}) {
  const draggingScrollOffset = useDraggingScrollOffset();
  const smallNodeHeight = node.boundingBox.height <= 30;
  return /* @__PURE__ */ jsxs9(Fragment8, { children: [
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        ref: setNodeRef,
        style: {
          transform: CSS.Transform.toString({
            x: transform.x + draggingScrollOffset.x,
            y: transform.y + draggingScrollOffset.y,
            scaleX: 1,
            scaleY: 1
          }),
          backgroundColor: "white",
          border: "1px solid #0652BB",
          borderRadius: 4,
          padding: 8,
          zIndex: 100,
          fontSize: 20,
          color: "#0652BB"
        },
        top: smallNodeHeight ? node.boundingBox.y + node.boundingBox.height / 2 - 15 : node.boundingBox.y,
        left: node.boundingBox.x,
        children: /* @__PURE__ */ jsx15(Icon, { icon: "drag_pan" })
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y,
        left: node.boundingBox.x,
        width: node.boundingBox.width,
        height: node.boundingBox.height,
        style: {
          background: "rgba(255, 255, 255, 0.8)"
        }
      }
    )
  ] });
}
function ComponentSelectionOverlay({
  node,
  isContentTarget,
  isSelected,
  isHovered,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const maybeSetSelected = !isContentTarget && !isSelected && isHovered ? (event) => {
    event.stopPropagation();
    const target = {
      type: "component",
      name: node.name,
      index: node.index
    };
    visualEditor.actions.setSelectedComponent(target);
    visualEditor.actions.setHoveredComponent(target);
    setTimeout(() => {
      visualEditor.actions.focus();
    }, 0);
  } : null;
  return /* @__PURE__ */ jsx15(
    MarshaledPreviewOverlay,
    {
      top: node.boundingBox.y,
      left: node.boundingBox.x,
      width: node.boundingBox.width,
      height: node.boundingBox.height,
      onClick: maybeSetSelected,
      onMouseDown: maybeSetSelected,
      onDragOver: maybeSetSelected,
      onWheel: (event) => {
        visualEditor.actions.scrollBy({ left: event.deltaX, top: event.deltaY });
      },
      style: {
        backgroundColor: isSelected && !isContentTarget ? "rgb(34, 121, 221, 0.1)" : "transparent",
        ...isContentTarget || isSelected || isHovered ? { outline: "1px solid #0652BB" } : {},
        ...scrollingStyle
      }
    }
  );
}
function DragHandle({
  node,
  definition,
  listeners,
  attributes,
  isContentTarget,
  bubbleMenuIsVisible,
  slashMenuIsVisible,
  isSelected,
  isHovered,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const [showMenu, setShowMenu] = useState6(false);
  const canShowDraghandle = (isSelected || isHovered || isContentTarget) && !bubbleMenuIsVisible && !slashMenuIsVisible;
  const isRootComponent = visualEditor.actions.isRootComponent(node);
  const canBeDuplicated = !(isRootComponent || isCioFlyEnvironment && ["column", "row", "x-column", "x-columns", "x-section"].includes(node.name));
  const canBeDeleted = canBeDuplicated;
  const MenuButton = showMenu ? Trigger3 : "button";
  const smallNodeHeight = node.boundingBox.height <= 30;
  const overlayXPadding = 3;
  return /* @__PURE__ */ jsx15(Fragment8, { children: /* @__PURE__ */ jsx15(
    MarshaledPreviewOverlay,
    {
      top: Math.max(smallNodeHeight ? node.boundingBox.y + node.boundingBox.height / 2 - 15 : node.boundingBox.y, -1),
      left: Math.max(node.boundingBox.x - 20 - overlayXPadding, -(OFF_CANVAS_SPACE_PADDING + overlayXPadding * 2)),
      height: smallNodeHeight ? 30 : node.boundingBox.height,
      interactive: canShowDraghandle,
      onMouseOver: (event) => {
        event.stopPropagation();
      },
      onMouseEnter: () => {
        visualEditor.actions.setHoveredComponent({
          type: "component",
          name: node.name,
          index: node.index
        });
      },
      style: {
        zIndex: 907,
        // higher than the overlays
        ...scrollingStyle,
        paddingLeft: overlayXPadding,
        paddingRight: overlayXPadding,
        opacity: canShowDraghandle ? scrollingStyle.opacity : 0,
        backgroundColor: "transparent"
      },
      children: /* @__PURE__ */ jsxs9(
        Root5,
        {
          open: showMenu,
          onOpenChange: (open) => {
            setShowMenu(open);
          },
          children: [
            /* @__PURE__ */ jsx15(
              MenuButton,
              {
                style: {
                  cursor: "pointer",
                  background: "transparent",
                  color: "inherit",
                  font: "inherit",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  alignItems: "center"
                },
                onClick: (event) => {
                  event.stopPropagation();
                  visualEditor.actions.setSelectedComponent({
                    type: "component",
                    name: node.name,
                    index: node.index
                  });
                  if (!showMenu) {
                    setShowMenu(true);
                  }
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `preview-drag-button-component-${node.name}-${node.index}`
                } : {},
                ...isRootComponent ? {} : { ...listeners, ...attributes },
                children: /* @__PURE__ */ jsx15(DragButton, { disabled: isRootComponent, children: "drag_indicator" })
              }
            ),
            /* @__PURE__ */ jsx15(Portal, { children: /* @__PURE__ */ jsxs9(
              Content5,
              {
                align: "start",
                style: {
                  marginTop: 0
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `preview-context-menu`
                } : {},
                children: [
                  /* @__PURE__ */ jsx15(
                    Item3,
                    {
                      onClick: () => {
                        visualEditor.commands.duplicateComponent({
                          type: "component",
                          name: node.name,
                          index: node.index
                        });
                        visualEditor.actions.track("component_duplicated", {
                          componentName: node.name,
                          using: "Context Menu"
                        });
                      },
                      ...!canBeDuplicated ? { disabled: true } : {},
                      ...isDevEnvironment ? {
                        [CARTA_DATA_TEST_ATTRIBUTE]: "preview-context-menu-item-duplicate"
                      } : {},
                      children: "Duplicate"
                    }
                  ),
                  definition.prose && !definition.native ? /* @__PURE__ */ jsx15(Fragment8, { children: /* @__PURE__ */ jsxs9(Sub, { children: [
                    /* @__PURE__ */ jsx15(
                      SubTrigger2,
                      {
                        ...isDevEnvironment ? {
                          [CARTA_DATA_TEST_ATTRIBUTE]: `preview-context-menu-item-convert-to`
                        } : {},
                        children: "Turn into"
                      }
                    ),
                    /* @__PURE__ */ jsx15(SubContent2, { children: visualEditor.state.registeredComponents.map((name) => {
                      return visualEditor.actions.getComponentDefinition(name);
                    }).filter((definition2) => definition2.prose && !definition2.native).map((definition2) => {
                      return /* @__PURE__ */ jsxs9(
                        Item3,
                        {
                          style: {
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between",
                            alignItems: "center"
                          },
                          onClick: () => {
                            if (definition2.name === node.name) {
                              return;
                            }
                            visualEditor.actions.setComponentTagName(
                              {
                                type: "component",
                                name: node.name,
                                index: node.index
                              },
                              definition2.name
                            );
                            visualEditor.actions.track("component_converted", {
                              originalComponentName: node.name,
                              newComponentName: definition2.name,
                              using: "Context Menu"
                            });
                          },
                          ...isDevEnvironment ? {
                            [CARTA_DATA_TEST_ATTRIBUTE]: `preview-context-menu-subitem-convert-to-${definition2.name}`
                          } : {},
                          children: [
                            /* @__PURE__ */ jsx15("div", { children: definition2.label }),
                            definition2.name === node.name && /* @__PURE__ */ jsx15(RightSlot, { children: /* @__PURE__ */ jsx15(Icon, { icon: "check" }) })
                          ]
                        },
                        definition2.name
                      );
                    }) })
                  ] }) }) : null,
                  /* @__PURE__ */ jsx15(Separator3, {}),
                  /* @__PURE__ */ jsx15(
                    Item3,
                    {
                      onClick: () => {
                        const target = {
                          type: "component",
                          name: node.name,
                          index: node.index
                        };
                        visualEditor.actions.queueSiblingSelection(target);
                        visualEditor.commands.deleteComponent(target);
                      },
                      css: {
                        color: "#DB234B"
                      },
                      ...!canBeDeleted ? { disabled: true } : {},
                      ...isDevEnvironment ? {
                        [CARTA_DATA_TEST_ATTRIBUTE]: "preview-context-menu-item-delete"
                      } : {},
                      children: "Delete"
                    }
                  )
                ]
              }
            ) })
          ]
        }
      )
    }
  ) });
}
function ComponentMenu({
  node,
  attributes,
  isContentTarget,
  multipleComponentsSelected,
  isSelected,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const canShowComponentMenu = isSelected && !multipleComponentsSelected || isContentTarget;
  const isRootComponent = visualEditor.actions.isRootComponent(node);
  const canBeDuplicated = !(isRootComponent || isCioFlyEnvironment && ["column", "row", "x-column", "x-columns", "x-section"].includes(node.name));
  const canBeDeleted = canBeDuplicated;
  const haveEnoughSpaceAbove = node.boundingBox.y > 20;
  const overlayTop = Math.max(
    haveEnoughSpaceAbove ? node.boundingBox.y + 2 : node.boundingBox.y - 2 + node.boundingBox.height,
    0
  );
  return /* @__PURE__ */ jsx15(Fragment8, { children: /* @__PURE__ */ jsx15(
    MarshaledPreviewOverlay,
    {
      top: overlayTop,
      left: node.boundingBox.x,
      interactive: canShowComponentMenu,
      style: {
        // invisible padding to make it easier to hover over the overlay
        paddingTop: haveEnoughSpaceAbove ? 0 : 3,
        paddingBottom: haveEnoughSpaceAbove ? 3 : 0,
        paddingLeft: 4,
        paddingRight: 4,
        margin: haveEnoughSpaceAbove ? 0 : 50,
        zIndex: 907,
        // higher than the overlays
        ...scrollingStyle,
        opacity: canShowComponentMenu ? scrollingStyle.opacity : 0,
        transform: haveEnoughSpaceAbove ? "translateY(-100%)" : "none",
        backgroundColor: "transparent"
      },
      children: /* @__PURE__ */ jsxs9(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
            borderLeft: 0,
            margin: 0,
            padding: "0 6px",
            color: "white",
            borderRadius: haveEnoughSpaceAbove ? "3px 3px 0 0" : "0 0 3px 3px",
            backgroundColor: "#0652BB",
            fontWeight: 500,
            cursor: "default"
          },
          onMouseDown: () => {
            visualEditor.actions.setSelectedComponent(node);
          },
          ...isRootComponent ? {} : { ...attributes },
          children: [
            /* @__PURE__ */ jsx15(
              ContextMenuButton,
              {
                title: "View block properties",
                onClick: (event) => {
                  event.stopPropagation();
                  visualEditor.actions.showCorePropertiesMenu({
                    top: Math.max(overlayTop - visualEditor.state.scroll.top, 0),
                    left: Math.max(node.boundingBox.x - visualEditor.state.scroll.left, 0),
                    width: 100,
                    height: 25
                  });
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `component-context-menu-button-core-properties-${node.name}-${node.index}`
                } : {},
                children: /* @__PURE__ */ jsx15(SvgIcon, { svg: icons_default.properties, style: { width: 16, height: 16, display: "block" } })
              }
            ),
            /* @__PURE__ */ jsx15(
              ContextMenuButton,
              {
                title: "Duplicate block",
                style: { opacity: canBeDuplicated ? 1 : 0.5 },
                disabled: !canBeDuplicated,
                onClick: () => {
                  visualEditor.commands.duplicateComponent({
                    type: "component",
                    name: node.name,
                    index: node.index
                  });
                  visualEditor.actions.track("component_duplicated", {
                    componentName: node.name,
                    using: "Context Menu"
                  });
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `component-context-menu-button-duplicate-${node.name}-${node.index}`
                } : {},
                children: /* @__PURE__ */ jsx15(SvgIcon, { svg: icons_default.duplicate, style: { width: 16, height: 16, display: "block" } })
              }
            ),
            /* @__PURE__ */ jsx15(
              ContextMenuButton,
              {
                title: "Delete block",
                style: { opacity: canBeDeleted ? 1 : 0.5 },
                disabled: !canBeDeleted,
                onClick: (event) => {
                  event.stopPropagation();
                  const target = {
                    type: "component",
                    name: node.name,
                    index: node.index
                  };
                  visualEditor.commands.deleteComponent(target);
                  visualEditor.actions.queueSiblingSelection(target, "structure");
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `component-context-menu-button-delete-${node.name}-${node.index}`
                } : {},
                children: /* @__PURE__ */ jsx15(SvgIcon, { svg: icons_default.trash, style: { width: 16, height: 16, display: "block" } })
              }
            )
          ]
        }
      )
    }
  ) });
}
function ComponentLabel({
  node,
  definition,
  isSelected,
  isHovered,
  isContentTarget,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const haveEnoughSpaceAbove = node.boundingBox.y > 22;
  const isVisible = isSelected || isHovered || isContentTarget;
  return /* @__PURE__ */ jsx15(Fragment8, { children: /* @__PURE__ */ jsx15(
    MarshaledPreviewOverlay,
    {
      top: haveEnoughSpaceAbove ? node.boundingBox.y + 2 : node.boundingBox.y - 2 + node.boundingBox.height,
      left: node.boundingBox.x + node.boundingBox.width,
      interactive: isVisible,
      onMouseEnter: () => {
        visualEditor.actions.setHoveredComponent({
          type: "component",
          name: node.name,
          index: node.index
        });
      },
      onMouseDown: () => {
        visualEditor.actions.setSelectedComponent(node);
      },
      style: {
        // invisible padding to make it easier to hover over the overlay
        paddingTop: haveEnoughSpaceAbove ? 0 : 3,
        paddingBottom: haveEnoughSpaceAbove ? 3 : 0,
        paddingRight: 4,
        paddingLeft: 8,
        zIndex: 907,
        // higher than the overlays
        ...scrollingStyle,
        opacity: isVisible ? scrollingStyle.opacity : 0,
        transform: haveEnoughSpaceAbove ? "translateX(-100%) translateY(-100%)" : "translateX(-100%)",
        backgroundColor: "transparent"
      },
      children: /* @__PURE__ */ jsx15(
        "div",
        {
          style: {
            borderRadius: haveEnoughSpaceAbove ? "4px 4px 0 0" : "0 0 4px 4px",
            backgroundColor: "#0652BB",
            padding: "5px 12px",
            color: "white",
            fontSize: 12,
            lineHeight: "12px",
            fontWeight: 500,
            cursor: "default"
          },
          ...isDevEnvironment ? {
            [CARTA_DATA_TEST_ATTRIBUTE]: `component-label-${node.name}-${node.index}`
          } : {},
          children: definition.label
        }
      )
    }
  ) });
}
function AddButton({
  node,
  parent,
  isHovered,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const canShowAddButton = isHovered;
  const [showMenu, setShowMenu] = useState6(false);
  const allowedSiblings = useMemo2(() => {
    if (parent?.type !== "slot") {
      return [];
    }
    const parentSlotDefinition = visualEditor.actions.getSlotDefinition(parent.name, parent.slot);
    if (parentSlotDefinition == null || parentSlotDefinition.type !== "children") {
      return [];
    }
    const siblings = [];
    visualEditor.state.registeredComponents.filter(
      (componentName) => isCioFlyEnvironment ? !["column", "row", "x-column", "x-columns", "x-box", "x-section", "x-video"].includes(componentName) : true
    ).filter((componentName) => {
      return parentSlotDefinition.allowedChildren.includes(componentName);
    }).forEach((componentName) => {
      const definition = visualEditor.actions.getComponentDefinition(componentName);
      definition.presets.forEach((preset) => {
        siblings.push({ name: definition.name, label: definition.label, preset });
      });
    });
    return siblings;
  }, [parent]);
  const [typedSearchText, setTypedSearchText] = useState6("");
  const [filteredSiblings, setFilteredSiblings] = useState6(allowedSiblings);
  useEffect7(() => {
    const search = typedSearchText.trim().toLowerCase();
    if (search.length === 0) {
      setFilteredSiblings(allowedSiblings);
      return;
    }
    const filteredItems = allowedSiblings.filter((item) => {
      return item.preset.label.toLowerCase().includes(search);
    });
    setFilteredSiblings(filteredItems);
  }, [typedSearchText, allowedSiblings]);
  const buttonRef = useRef5(null);
  const [buttonBoundingRect, setButtonBoundingRect] = useState6(null);
  useEffect7(() => {
    if (buttonRef.current) {
      setButtonBoundingRect(buttonRef.current.getBoundingClientRect());
    }
  }, [buttonRef, isHovered]);
  if (allowedSiblings.length === 0) {
    return null;
  }
  const overlayTop = node.boundingBox.y - 2 + node.boundingBox.height;
  return /* @__PURE__ */ jsxs9(Fragment8, { children: [
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: overlayTop,
        left: node.boundingBox.x + node.boundingBox.width / 2,
        interactive: canShowAddButton || showMenu,
        style: {
          // invisible padding to make it easier to hover over the overlay
          padding: "7px 50px 0 50px",
          zIndex: 907,
          // higher than the overlays
          ...scrollingStyle,
          opacity: canShowAddButton || showMenu ? scrollingStyle.opacity : 0,
          background: "transparent",
          transform: "translateX(-50%)",
          borderRadius: "0% 0% 50% 50% / 0% 0% 100% 100%"
        },
        children: /* @__PURE__ */ jsx15(
          "div",
          {
            style: {
              borderRadius: 3,
              backgroundColor: "#0652BB",
              padding: 0,
              color: "white",
              fontSize: 12,
              fontWeight: 500,
              cursor: "default"
            },
            children: /* @__PURE__ */ jsxs9(
              "button",
              {
                ref: buttonRef,
                title: "Add block below",
                style: {
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                  padding: "4px 10px",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  color: "white",
                  lineHeight: "16px"
                },
                onClick: (event) => {
                  event.stopPropagation();
                  setTypedSearchText("");
                  setShowMenu(!showMenu);
                },
                ...isDevEnvironment ? {
                  [CARTA_DATA_TEST_ATTRIBUTE]: `component-add-button-${node.name}-${node.index}`
                } : {},
                children: [
                  /* @__PURE__ */ jsx15(
                    Icon,
                    {
                      icon: "add",
                      size: 16,
                      style: {
                        paddingRight: 6
                      }
                    }
                  ),
                  "Add block"
                ]
              }
            )
          }
        )
      }
    ),
    showMenu && buttonBoundingRect ? /* @__PURE__ */ jsx15(
      MarshaledPreviewTooltip,
      {
        target: {
          top: overlayTop,
          left: node.boundingBox.x + node.boundingBox.width / 2 - buttonBoundingRect.width / 2 - 3,
          width: 100,
          height: 32
        },
        style: {
          pointerEvents: "auto",
          padding: "8px 8px",
          margin: 3,
          borderRadius: 4,
          fontSize: 14,
          color: "#333",
          backgroundColor: "white",
          filter: "drop-shadow(2px 2px 4px rgba(52, 52, 70, 0.01)) drop-shadow(1px 2px 2px rgba(52, 52, 70, 0.04))",
          border: "1px solid #EBECF2",
          minWidth: 200,
          width: 200,
          maxWidth: "100%",
          transition: "width 0.2s ease-in-out"
        },
        alignX: "left",
        alignY: "bottom",
        zIndex: 908,
        interactive: true,
        children: /* @__PURE__ */ jsxs9(
          RadixDialog2.Root,
          {
            open: showMenu,
            onOpenChange: (open) => {
              if (!open) {
                setShowMenu(false);
              }
            },
            children: [
              /* @__PURE__ */ jsx15(RadixDialog2.Overlay, {}),
              /* @__PURE__ */ jsx15(RadixDialog2.Content, { children: /* @__PURE__ */ jsxs9(
                Command,
                {
                  loop: true,
                  label: "Add Component",
                  filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0,
                  onKeyDown: (e) => {
                    if (e.key === "Escape") {
                      setShowMenu(false);
                    }
                  },
                  ...isDevEnvironment ? {
                    [CARTA_DATA_TEST_ATTRIBUTE]: `component-add-menu-${node.name}-${node.index}`
                  } : {},
                  children: [
                    /* @__PURE__ */ jsxs9("div", { style: { position: "relative", height: 32, marginBottom: 8 }, children: [
                      /* @__PURE__ */ jsx15(
                        Icon,
                        {
                          icon: "search",
                          style: {
                            width: 28,
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#6f7488"
                          }
                        }
                      ),
                      /* @__PURE__ */ jsx15(
                        CmdKCommandInput,
                        {
                          onValueChange: setTypedSearchText,
                          style: {
                            width: "100%",
                            height: "100%",
                            backgroundColor: " #fff",
                            borderRadius: " 4px",
                            border: "1px solid #c7c7d4",
                            borderColor: "#DFE0E8",
                            // breaking the rules
                            boxShadow: " 0 0 1px rgb(0 0 0 / 12%) inset",
                            color: "#4b4b60",
                            fontSize: 14,
                            verticalAlign: "middle",
                            padding: "0 10px 0 24px"
                          }
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsxs9(
                      Command.List,
                      {
                        style: {
                          overflow: "auto",
                          overscrollBehavior: "contain",
                          height: "min(200px, calc(var(--cmdk-list-height)))",
                          maxHeight: 165,
                          minHeight: 165,
                          transition: "100ms ease",
                          transitionProperty: "height width",
                          padding: "8px 0 0 0"
                        },
                        children: [
                          /* @__PURE__ */ jsx15(Command.Empty, { children: "No results found." }),
                          filteredSiblings.map((item) => /* @__PURE__ */ jsxs9(
                            AddMenuItem,
                            {
                              value: `${item.name}-${item.preset.label}`,
                              onSelect: () => {
                                setShowMenu(false);
                                const currentComponentTarget = {
                                  type: "component",
                                  name: node.name,
                                  index: node.index
                                };
                                visualEditor.actions.queueSelection({
                                  target: currentComponentTarget,
                                  nextRelative: true
                                });
                                visualEditor.actions.setHoveredComponent(null);
                                visualEditor.commands.insertComponent(item.preset.content, {
                                  sibling: currentComponentTarget,
                                  position: "after"
                                });
                                visualEditor.actions.track("component_added", {
                                  componentName: item.name,
                                  presetLabel: item.preset.label,
                                  using: "Context Menu"
                                });
                              },
                              children: [
                                /* @__PURE__ */ jsx15(Icon, { icon: item.preset.icon || "drag_indicator", style: { marginRight: 4 } }),
                                item.preset.label,
                                " "
                              ]
                            },
                            `${item.name}-${item.preset.label}`
                          ))
                        ]
                      }
                    )
                  ]
                }
              ) })
            ]
          }
        )
      }
    ) : null
  ] });
}
function ResizableSizeControl({
  node,
  parent,
  definition,
  bubbleMenuIsVisible,
  slashMenuIsVisible,
  isContentTarget,
  isSelected,
  scrollingStyle
}) {
  const { visualEditor } = useEncompass();
  const { isLoading, state } = useSelection();
  const showResizableControl = (isSelected || isContentTarget) && !bubbleMenuIsVisible && !slashMenuIsVisible;
  const [isHoveringOverControls, setIsHoveringOverControls] = useState6(false);
  const mouseLeaveTimeout = useRef5(null);
  const [{ isResizing, size, direction }, setResizing] = useState6({
    isResizing: false,
    size: null,
    direction: null
  });
  if (!showResizableControl) {
    return null;
  }
  if (!parent) {
    return null;
  }
  if (isLoading) {
    return null;
  }
  if (state.selection !== "single") {
    return null;
  }
  const resizableField = definition.props?.fields.find(
    (field) => field.type === "size" && field.resizable && field.name === "width"
  );
  if (!resizableField) {
    return null;
  }
  const onMouseEnter = () => {
    if (mouseLeaveTimeout.current) {
      window.clearTimeout(mouseLeaveTimeout.current);
    }
    setIsHoveringOverControls(true);
  };
  const onMouseLeave = () => {
    mouseLeaveTimeout.current = window.setTimeout(() => {
      setIsHoveringOverControls(false);
    }, 100);
  };
  return /* @__PURE__ */ jsxs9(Fragment8, { children: [
    isResizing ? /* @__PURE__ */ jsx15(Fragment8, { children: /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        size: "100%",
        style: {
          top: visualEditor.state.scroll.top,
          left: visualEditor.state.scroll.left,
          height: "100%",
          position: "absolute",
          zIndex: -1,
          cursor: "nwse-resize"
        },
        interactive: true
      }
    ) }) : null,
    isHoveringOverControls || isResizing ? /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y,
        height: node.boundingBox.height,
        left: node.boundingBox.x,
        width: node.boundingBox.width,
        zIndex: 100,
        interactive: true,
        onMouseEnter: () => {
          visualEditor.actions.setHoveredComponent(node);
        },
        onMouseMove: () => {
          visualEditor.actions.setHoveredComponent(isResizing ? null : node);
        },
        style: {
          display: "flex",
          // if direction contains left, we are resizing the left side towards the right
          flexDirection: direction?.toLowerCase().includes("left") ? "row-reverse" : "row",
          // if direction contains top, we are resizing the top side towards the bottom
          alignItems: direction?.toLowerCase().includes("top") ? "flex-end" : "flex-start"
        },
        children: /* @__PURE__ */ jsx15(
          Resizable,
          {
            size: {
              width: size?.width ?? node.boundingBox.width,
              height: size?.height ?? node.boundingBox.height
            },
            style: {
              ...isResizing ? {
                outline: `2px solid #0652BB`,
                backgroundColor: "rgb(34, 121, 221, 0.1)"
              } : {},
              ...scrollingStyle
            },
            handleStyles: {
              left: {
                height: "100%",
                width: 12,
                left: -6
              },
              right: {
                height: "100%",
                width: 12,
                right: -6
              },
              top: {
                height: 12,
                width: "100%",
                top: -6
              },
              bottom: {
                height: 12,
                width: "100%",
                bottom: -6
              },
              bottomRight: {
                height: 12,
                width: 12,
                background: "white",
                border: "2px solid #0652BB",
                right: -6,
                bottom: -6
              },
              bottomLeft: {
                height: 12,
                width: 12,
                background: "white",
                border: "2px solid #0652BB",
                left: -6,
                bottom: -6
              },
              topLeft: {
                height: 12,
                width: 12,
                background: "white",
                border: "2px solid #0652BB",
                left: -6,
                top: -6
              },
              topRight: {
                height: 12,
                width: 12,
                background: "white",
                border: "2px solid #0652BB",
                right: -6,
                top: -6
              }
            },
            handleComponent: {
              top: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              bottom: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              left: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              right: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              topLeft: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              topRight: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              bottomRight: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave }),
              bottomLeft: /* @__PURE__ */ jsx15(Handle, { onMouseEnter, onMouseLeave })
            },
            maxWidth: parent.boundingBox.width,
            lockAspectRatio: true,
            onResizeStart: (_event, direction2) => {
              setResizing({
                isResizing: true,
                size: { width: node.boundingBox.width, height: node.boundingBox.height },
                direction: direction2
              });
            },
            onResize: (_event, _direction, _elementRef, delta) => {
              setResizing((state2) => ({
                ...state2,
                size: {
                  width: state2.size.width + delta.width,
                  height: state2.size.height + delta.height
                }
              }));
            },
            onResizeStop: (_event, _direction, _elementRef, delta) => {
              setResizing({
                isResizing: false,
                size: null,
                direction: null
              });
              onMouseLeave();
              const newWidth = node.boundingBox.width + delta.width;
              const roundedWidthPercentage = Math.round(newWidth / parent.boundingBox.width * 20) * 5;
              state.props.setProps({
                [resizableField.name]: {
                  isPrimitive: true,
                  isExpression: false,
                  isComputed: false,
                  value: `${roundedWidthPercentage}%`
                }
              });
            }
          }
        )
      }
    ) : /* @__PURE__ */ jsx15(
      FakeResize,
      {
        node,
        onMouseEnter,
        onMouseLeave,
        scrollingStyle
      }
    )
  ] });
}
var Resizable = styled10(BaseResizable, {
  // this is needed for the handles to be positioned correctly
  "& > :last-child": {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%"
  }
});
function FakeResize({
  node,
  onMouseEnter,
  onMouseLeave,
  scrollingStyle
}) {
  return /* @__PURE__ */ jsxs9(Fragment8, { children: [
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y,
        height: node.boundingBox.height,
        left: node.boundingBox.x - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: scrollingStyle,
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y,
        height: node.boundingBox.height,
        left: node.boundingBox.x + node.boundingBox.width - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: scrollingStyle,
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y - 6,
        height: 12,
        left: node.boundingBox.x,
        width: node.boundingBox.width,
        zIndex: 100,
        interactive: true,
        style: scrollingStyle,
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y + node.boundingBox.height - 6,
        height: 12,
        left: node.boundingBox.x,
        width: node.boundingBox.width,
        zIndex: 100,
        interactive: true,
        style: scrollingStyle,
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y - 6,
        height: 12,
        left: node.boundingBox.x - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: {
          background: "white",
          border: "2px solid #0652BB",
          ...scrollingStyle
        },
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y - 6,
        height: 12,
        left: node.boundingBox.x + node.boundingBox.width - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: {
          background: "white",
          border: "2px solid #0652BB",
          ...scrollingStyle
        },
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y + node.boundingBox.height - 6,
        height: 12,
        left: node.boundingBox.x - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: {
          background: "white",
          border: "2px solid #0652BB",
          ...scrollingStyle
        },
        onMouseEnter,
        onMouseLeave
      }
    ),
    /* @__PURE__ */ jsx15(
      MarshaledPreviewOverlay,
      {
        top: node.boundingBox.y + node.boundingBox.height - 6,
        height: 12,
        left: node.boundingBox.x + node.boundingBox.width - 6,
        width: 12,
        zIndex: 100,
        interactive: true,
        style: {
          background: "white",
          border: "2px solid #0652BB",
          ...scrollingStyle
        },
        onMouseEnter,
        onMouseLeave
      }
    )
  ] });
}
var Handle = styled10("div", {
  height: "100%",
  width: "100%"
});
var ContextMenuButton = styled10("button", {
  background: "transparent",
  border: "none",
  padding: 3,
  margin: "0 2px",
  cursor: "pointer"
});
var DragButton = styled10(
  React2.forwardRef(({ disabled, ...props }, ref) => /* @__PURE__ */ jsx15(
    "div",
    {
      ...props,
      className: `${props.className ? props.className : ""} material-symbols-outlined`,
      ref,
      style: disabled ? { color: "#b9bbc8" } : {
        cursor: "grab",
        color: "#0652BB"
      }
    }
  )),
  {
    "&&&": {
      background: "rgba(255, 255, 255, 0.275)",
      boxShadow: "0px 0px 3px rgba(255, 255, 255, 0.5)",
      border: "0",
      borderRadius: "3px",
      fontWeight: "bold",
      fontSize: "18px",
      padding: "4px 0",
      margin: "2px 0",
      display: "inline-block"
    }
  }
);
var AddMenuItem = styled10(
  (props) => {
    return /* @__PURE__ */ jsx15(
      Command.Item,
      {
        ...props,
        onSelect: (item) => {
          props.onSelect?.(item);
        }
      }
    );
  },
  {
    display: "flex",
    alignItems: "center",
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontSize: 14,
    lineHeight: "20px",
    padding: "4px 8px",
    borderRadius: 4,
    "&[data-selected]": {
      color: "white",
      backgroundColor: "#0652BB"
    }
  }
);

// src/visual-editor/overlays/command-menu.tsx
import { IS_CIO_FLY_ENVIRONMENT as IS_CIO_FLY_ENVIRONMENT2, parse } from "@customerio/carta-visualized";
import * as RadixDialog3 from "@radix-ui/react-dialog";
import { styled as styled11 } from "@stitches/react";
import { emoji } from "node-emoji";
import React3, { useEffect as useEffect8, useMemo as useMemo3, useRef as useRef6, useState as useState7 } from "react";
import { CommandInput as CmdKCommandInput2, Command as Command2 } from "cmdk";
import { Fragment as Fragment9, jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var CHARS_TO_TYPE_AFTER_NO_RESULTS_TO_CLOSE_MENU = 5;
var isCioFlyEnvironment2 = IS_CIO_FLY_ENVIRONMENT2();
function CommandMenu() {
  const { visualEditor } = useEncompass();
  if (visualEditor.state.commandMenuMode === ":") {
    return /* @__PURE__ */ jsx16(Emoji, {});
  }
  return null;
}
function CursorIndicator() {
  const { visualEditor } = useEncompass();
  const boundingBox = visualEditor.state.textSelection?.selectionBoundingBox;
  return /* @__PURE__ */ jsxs10(Fragment9, { children: [
    /* @__PURE__ */ jsx16(
      MarshaledPreviewOverlay,
      {
        top: boundingBox.top,
        left: boundingBox.left,
        width: 1,
        height: boundingBox.height,
        backgroundColor: "#0652BB"
      }
    ),
    /* @__PURE__ */ jsx16(
      MarshaledPreviewOverlay,
      {
        top: boundingBox.top,
        left: boundingBox.left - 2,
        width: 5,
        height: 5,
        backgroundColor: "#0652BB",
        style: {
          borderRadius: "50%"
        }
      }
    )
  ] });
}
function Emoji() {
  const { visualEditor } = useEncompass();
  const boundingBox = visualEditor.state.textSelection?.selectionBoundingBox;
  const [typedText, setTypedText] = useState7("");
  const commandEmptyRef = useRef6(null);
  useCloseOnTypingAfterNoResults(typedText, commandEmptyRef, visualEditor);
  return /* @__PURE__ */ jsxs10(Fragment9, { children: [
    /* @__PURE__ */ jsx16(CursorIndicator, {}),
    /* @__PURE__ */ jsx16(
      MarshaledPreviewTooltip,
      {
        target: boundingBox,
        style: { pointerEvents: "auto" },
        alignX: "left",
        alignY: "bottom",
        zIndex: 908,
        children: /* @__PURE__ */ jsxs10(
          CommandMenuContainer,
          {
            label: "Emoij Menu",
            filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0,
            children: [
              /* @__PURE__ */ jsx16(CommandInput, { character: ":", onValueChange: setTypedText }),
              /* @__PURE__ */ jsxs10(CommandList, { children: [
                /* @__PURE__ */ jsx16(CommandEmpty, { ref: commandEmptyRef, children: "No results found." }),
                Object.keys(emoji).map((key) => /* @__PURE__ */ jsxs10(
                  CommandItem,
                  {
                    value: key,
                    onSelect: () => {
                      visualEditor.actions.insertTextAtCursor(emoji[key]);
                    },
                    children: [
                      emoji[key],
                      " ",
                      key
                    ]
                  },
                  key
                ))
              ] })
            ]
          }
        )
      }
    )
  ] });
}
var CommandMenuContainer = styled11(
  (props) => {
    const { visualEditor } = useEncompass();
    return /* @__PURE__ */ jsxs10(
      RadixDialog3.Root,
      {
        open: !!visualEditor.state.commandMenuMode,
        onOpenChange: (open) => {
          if (!open) {
            visualEditor.actions.discardCommandMenu();
          }
        },
        children: [
          /* @__PURE__ */ jsx16(RadixDialog3.Overlay, {}),
          /* @__PURE__ */ jsx16(RadixDialog3.Content, { children: /* @__PURE__ */ jsx16(
            Command2,
            {
              loop: true,
              ...props,
              "data-test-id": "encompass-command-menu",
              onKeyDown: (e) => {
                if (e.key === "Escape") {
                  visualEditor.actions.discardCommandMenu(e.target.value);
                }
                if (e.key === "Backspace" && e.target.value === "") {
                  visualEditor.actions.setCommandMenuMode(null);
                  visualEditor.actions.focus();
                }
                if (e.key === " " && e.target.value === "") {
                  visualEditor.actions.discardCommandMenu(" ");
                }
              }
            }
          ) })
        ]
      }
    );
  },
  {
    padding: "8px 8px",
    borderRadius: 4,
    fontSize: 14,
    color: "#333",
    backgroundColor: "white",
    filter: "drop-shadow(2px 2px 4px rgba(52, 52, 70, 0.01)) drop-shadow(1px 2px 2px rgba(52, 52, 70, 0.04))",
    border: "1px solid #EBECF2",
    minWidth: 300,
    width: 400,
    maxWidth: "100%",
    transition: "width 0.2s ease-in-out"
  }
);
var CommandInput = React3.forwardRef(
  ({
    character,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs10("div", { style: { marginBottom: 8, position: "relative" }, children: [
      /* @__PURE__ */ jsx16(
        "span",
        {
          style: {
            width: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            fontSize: 12,
            color: "#4b4b60",
            fontWeight: 500
          },
          children: character
        }
      ),
      /* @__PURE__ */ jsx16(
        CmdKCommandInput2,
        {
          ref,
          style: {
            width: "100%",
            backgroundColor: " #fff",
            borderRadius: " 4px",
            border: "1px solid #c7c7d4",
            borderColor: "#DFE0E8",
            // breaking the rules
            boxShadow: " 0 0 1px rgb(0 0 0 / 12%) inset",
            color: "#4b4b60",
            display: "inline-block",
            fontSize: 14,
            minHeight: "32px",
            minWidth: "48px",
            padding: "0 110px 0 24px",
            verticalAlign: "middle"
          },
          ...props
        }
      ),
      /* @__PURE__ */ jsxs10(
        "span",
        {
          style: {
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            fontSize: 13,
            color: "#6f7488"
          },
          children: [
            "or ",
            /* @__PURE__ */ jsx16("span", { style: { fontWeight: 500 }, children: "ESC" }),
            " to insert"
          ]
        }
      )
    ] });
  }
);
var CommandList = styled11(Command2.List, {
  overflow: "auto",
  overscrollBehavior: "contain",
  height: "min(200px, calc(var(--cmdk-list-height)))",
  maxHeight: 330,
  minHeight: 100,
  transition: "100ms ease",
  transitionProperty: "height width",
  padding: "8px 0 0 0"
});
var CommandEmpty = styled11(Command2.Empty, {});
var CommandGroup = styled11(Command2.Group, {
  "&:not(:last-child)": {
    marginBottom: 20
  },
  "[cmdk-group-heading]": {
    fontWeight: "600",
    fontSize: "11px",
    lineHeight: "12px",
    alignItems: "center",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#A1A2B3",
    "&:after": {
      content: '""',
      display: "block",
      height: 1,
      backgroundColor: "#EBECF2",
      marginTop: 8,
      marginBottom: 8
    }
  }
});
var CommandItem = styled11(
  (props) => {
    const { visualEditor } = useEncompass();
    return /* @__PURE__ */ jsx16(
      Command2.Item,
      {
        ...props,
        onSelect: (item) => {
          visualEditor.actions.setCommandMenuMode(null);
          props.onSelect?.(item);
        }
      }
    );
  },
  {
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontSize: 14,
    lineHeight: "20px",
    padding: "4px 8px",
    borderRadius: 4,
    "&[data-selected]": {
      color: "white",
      backgroundColor: "#0652BB"
    }
  }
);
function useCloseOnTypingAfterNoResults(typedText, commandEmptyRef, visualEditor) {
  const [typedTextBeforeNoResults, setTypedTextBeforeNoResults] = useState7("");
  useEffect8(() => {
    if (commandEmptyRef.current) {
      const charsTypedAfterNoResults = typedText.length - typedTextBeforeNoResults.length;
      if (typedText.startsWith(typedTextBeforeNoResults) && charsTypedAfterNoResults >= CHARS_TO_TYPE_AFTER_NO_RESULTS_TO_CLOSE_MENU) {
        visualEditor.actions.discardCommandMenu(typedText);
      }
    } else {
      setTypedTextBeforeNoResults(typedText);
    }
  }, [typedText, commandEmptyRef]);
}

// ../../node_modules/@radix-ui/react-use-rect/dist/index.module.js
import { useState as $jNFHB$useState, useEffect as $jNFHB$useEffect } from "react";

// ../../node_modules/@radix-ui/rect/dist/index.module.js
function $0f3bbd680c63c15c$export$5a50ff2cde8c3802(elementToObserve, callback) {
  const observedData1 = $0f3bbd680c63c15c$var$observedElements.get(elementToObserve);
  if (observedData1 === void 0) {
    $0f3bbd680c63c15c$var$observedElements.set(elementToObserve, {
      rect: {},
      callbacks: [
        callback
      ]
    });
    if ($0f3bbd680c63c15c$var$observedElements.size === 1)
      $0f3bbd680c63c15c$var$rafId = requestAnimationFrame($0f3bbd680c63c15c$var$runLoop);
  } else {
    observedData1.callbacks.push(callback);
    callback(elementToObserve.getBoundingClientRect());
  }
  return () => {
    const observedData = $0f3bbd680c63c15c$var$observedElements.get(elementToObserve);
    if (observedData === void 0)
      return;
    const index = observedData.callbacks.indexOf(callback);
    if (index > -1)
      observedData.callbacks.splice(index, 1);
    if (observedData.callbacks.length === 0) {
      $0f3bbd680c63c15c$var$observedElements.delete(elementToObserve);
      if ($0f3bbd680c63c15c$var$observedElements.size === 0)
        cancelAnimationFrame($0f3bbd680c63c15c$var$rafId);
    }
  };
}
var $0f3bbd680c63c15c$var$rafId;
var $0f3bbd680c63c15c$var$observedElements = /* @__PURE__ */ new Map();
function $0f3bbd680c63c15c$var$runLoop() {
  const changedRectsData = [];
  $0f3bbd680c63c15c$var$observedElements.forEach((data, element) => {
    const newRect = element.getBoundingClientRect();
    if (!$0f3bbd680c63c15c$var$rectEquals(data.rect, newRect)) {
      data.rect = newRect;
      changedRectsData.push(data);
    }
  });
  changedRectsData.forEach((data) => {
    data.callbacks.forEach(
      (callback) => callback(data.rect)
    );
  });
  $0f3bbd680c63c15c$var$rafId = requestAnimationFrame($0f3bbd680c63c15c$var$runLoop);
}
function $0f3bbd680c63c15c$var$rectEquals(rect1, rect2) {
  return rect1.width === rect2.width && rect1.height === rect2.height && rect1.top === rect2.top && rect1.right === rect2.right && rect1.bottom === rect2.bottom && rect1.left === rect2.left;
}

// ../../node_modules/@radix-ui/react-use-rect/dist/index.module.js
function $ccac1052a272b78b$export$9823a655542017cd(measurable) {
  const [rect, setRect] = $jNFHB$useState();
  $jNFHB$useEffect(() => {
    if (measurable) {
      const unobserve = $0f3bbd680c63c15c$export$5a50ff2cde8c3802(measurable, setRect);
      return () => {
        setRect(void 0);
        unobserve();
      };
    }
    return;
  }, [
    measurable
  ]);
  return rect;
}

// src/visual-editor/overlays/context/index.tsx
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  closestCenter,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import React4, { useContext as useContext3, useState as useState8 } from "react";

// src/visual-editor/overlays/context/use-auto-scoller.ts
import { useCallback as useCallback2, useEffect as useEffect9, useRef as useRef7 } from "react";
function useAutoScroller({
  enabled,
  event,
  container: container2,
  rect,
  scroll,
  acceleration,
  interval,
  threshold
}) {
  const scrollSpeed = useRef7({ x: 0, y: 0 });
  const scrollDirection = useRef7({ x: 0, y: 0 });
  const scrollCallback = useRef7(scroll);
  scrollCallback.current = scroll;
  const [setAutoScrollInterval, clearAutoScrollInterval] = useInterval();
  const autoScroll = useCallback2(() => {
    const scrollLeft = scrollSpeed.current.x * scrollDirection.current.x;
    const scrollTop = scrollSpeed.current.y * scrollDirection.current.y;
    scrollCallback.current({
      left: scrollLeft,
      top: scrollTop
    });
  }, []);
  useEffect9(() => {
    if (!enabled || !rect || !event) {
      clearAutoScrollInterval();
      return;
    }
    const activationCoordinates = getEventCoordinates(event.activatorEvent);
    const coordinates = {
      x: activationCoordinates.x + event.delta.x,
      y: activationCoordinates.y + event.delta.y
    };
    const { direction, speed } = getScrollDirectionAndSpeed(
      container2,
      rect,
      {
        top: coordinates.y,
        bottom: coordinates.y,
        left: coordinates.x,
        right: coordinates.x
      },
      acceleration,
      threshold
    );
    if (speed.x > 0 || speed.y > 0) {
      clearAutoScrollInterval();
      setAutoScrollInterval(autoScroll, interval);
      scrollSpeed.current = speed;
      scrollDirection.current = direction;
      return;
    }
    scrollSpeed.current = { x: 0, y: 0 };
    scrollDirection.current = { x: 0, y: 0 };
    clearAutoScrollInterval();
  }, [
    event,
    acceleration,
    autoScroll,
    clearAutoScrollInterval,
    enabled,
    interval,
    JSON.stringify(rect),
    setAutoScrollInterval,
    JSON.stringify(threshold)
  ]);
}
var defaultThreshold = {
  x: 0.2,
  y: 0.2
};
function getScrollDirectionAndSpeed(scrollContainer, scrollContainerRect, { top, left, right, bottom }, acceleration = 10, thresholdPercentage = defaultThreshold) {
  const { isTop, isBottom, isLeft, isRight } = getScrollPosition(scrollContainer);
  const direction = {
    x: 0,
    y: 0
  };
  const speed = {
    x: 0,
    y: 0
  };
  const threshold = {
    height: scrollContainerRect.height * thresholdPercentage.y,
    width: scrollContainerRect.width * thresholdPercentage.x
  };
  if (!isTop && top <= scrollContainerRect.top + threshold.height) {
    direction.y = -1 /* Backward */;
    speed.y = acceleration * Math.abs((scrollContainerRect.top + threshold.height - top) / threshold.height);
  } else if (!isBottom && bottom >= scrollContainerRect.bottom - threshold.height) {
    direction.y = 1 /* Forward */;
    speed.y = acceleration * Math.abs((scrollContainerRect.bottom - threshold.height - bottom) / threshold.height);
  }
  if (!isRight && right >= scrollContainerRect.right - threshold.width) {
    direction.x = 1 /* Forward */;
    speed.x = acceleration * Math.abs((scrollContainerRect.right - threshold.width - right) / threshold.width);
  } else if (!isLeft && left <= scrollContainerRect.left + threshold.width) {
    direction.x = -1 /* Backward */;
    speed.x = acceleration * Math.abs((scrollContainerRect.left + threshold.width - left) / threshold.width);
  }
  return {
    direction,
    speed
  };
}
function getScrollPosition(scrollingContainer) {
  const minScroll = {
    x: 0,
    y: 0
  };
  const dimensions = isDocumentScrollingElement(scrollingContainer) ? {
    height: window.innerHeight,
    width: window.innerWidth
  } : {
    height: scrollingContainer.clientHeight,
    width: scrollingContainer.clientWidth
  };
  const maxScroll = {
    x: scrollingContainer.scrollWidth - dimensions.width,
    y: scrollingContainer.scrollHeight - dimensions.height
  };
  const isTop = scrollingContainer.scrollTop <= minScroll.y;
  const isLeft = scrollingContainer.scrollLeft <= minScroll.x;
  const isBottom = scrollingContainer.scrollTop >= maxScroll.y;
  const isRight = scrollingContainer.scrollLeft >= maxScroll.x;
  return {
    isTop,
    isLeft,
    isBottom,
    isRight,
    maxScroll,
    minScroll
  };
}
function isDocumentScrollingElement(_element) {
  return false;
}

// src/visual-editor/overlays/context/index.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function getCustomCollisionDetectionAlgorithm(draggingScrollOffset) {
  return function customCollisionDetectionAlgorithm(args) {
    args.collisionRect.top += draggingScrollOffset.y;
    args.collisionRect.bottom += draggingScrollOffset.y;
    args.collisionRect.left += draggingScrollOffset.x;
    args.collisionRect.right += draggingScrollOffset.x;
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length > 0) {
      return rectCollisions;
    }
    let [minTop, maxRight, maxBottom, minLeft] = [Infinity, -Infinity, -Infinity, Infinity];
    for (const container2 of args.droppableContainers) {
      const rect = container2.rect?.current;
      if (rect) {
        minTop = Math.min(minTop, rect.top);
        maxRight = Math.max(maxRight, rect.right);
        minLeft = Math.min(minLeft, rect.left);
      }
    }
    const { x, y } = args.pointerCoordinates;
    const gap = 10;
    if (y < minTop - gap || x > maxRight + gap || x < minLeft - gap) {
      return [];
    }
    const closestCornerCollisions = closestCorners(args);
    const closestCenterCollisions = closestCenter(args);
    if (closestCornerCollisions[0]?.data?.value / 3 < closestCenterCollisions[0]?.data?.value) {
      return closestCornerCollisions;
    }
    return closestCenterCollisions;
  };
}
var DraggingScrollOffsetContext = React4.createContext({ x: 0, y: 0 });
function useDraggingScrollOffset() {
  return useContext3(DraggingScrollOffsetContext);
}
function Context3({ children }) {
  const { visualEditor } = useEncompass();
  const activationConstraint = {
    distance: 0
    // 0 means we can drag immediately but we can still click the handle as a button
  };
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint
  });
  const keyboardSensor = useSensor(KeyboardSensor, {});
  const sensors = useSensors(mouseSensor, keyboardSensor);
  const [dragMoveEvent, setDragMoveEvent] = useState8(null);
  const [draggingScrollOffset, setDraggingScrollOffset] = useState8({ x: 0, y: 0 });
  const rect = $ccac1052a272b78b$export$9823a655542017cd(document.querySelector("#encompass-preview"));
  useAutoScroller({
    enabled: true,
    event: dragMoveEvent,
    container: {
      clientHeight: visualEditor.state.size?.height,
      clientWidth: visualEditor.state.size?.width,
      scrollHeight: visualEditor.state.scroll?.height,
      scrollWidth: visualEditor.state.scroll?.width,
      scrollTop: visualEditor.state.scroll?.top,
      scrollLeft: visualEditor.state.scroll?.left
    },
    rect,
    scroll(scrollBy) {
      scrollBy.top = Math.round(scrollBy.top);
      scrollBy.left = Math.round(scrollBy.left);
      if (scrollBy.top < 0 && visualEditor.state.scroll.top === 0 || scrollBy.top > 0 && visualEditor.state.scroll.height <= visualEditor.state.size.height + visualEditor.state.scroll.top + 1) {
        scrollBy.top = 0;
      }
      if (scrollBy.left < 0 && visualEditor.state.scroll.left === 0 || scrollBy.left > 0 && visualEditor.state.scroll.width <= visualEditor.state.size.width + scrollBy.left + 1) {
        scrollBy.left = 0;
      }
      if (scrollBy.top === 0 && scrollBy.left === 0) {
        return;
      }
      setDraggingScrollOffset((prev) => ({
        x: prev.x + scrollBy.left,
        y: prev.y + scrollBy.top
      }));
      visualEditor.actions.scrollBy(scrollBy);
    }
  });
  return /* @__PURE__ */ jsx17(DraggingScrollOffsetContext.Provider, { value: draggingScrollOffset, children: /* @__PURE__ */ jsx17(
    DndContext,
    {
      collisionDetection: getCustomCollisionDetectionAlgorithm(draggingScrollOffset),
      sensors,
      autoScroll: false,
      onDragStart: () => {
        visualEditor.actions.blur();
      },
      onDragMove: setDragMoveEvent,
      onDragEnd: ({ active, over }) => {
        setDragMoveEvent(null);
        setDraggingScrollOffset({ x: 0, y: 0 });
        if (!active || !over) {
          return;
        }
        const slotDefinition = visualEditor.actions.getSlotDefinition(
          over.data.current.target.name,
          over.data.current.target.slot
        );
        const allowedChildren = slotDefinition?.type === "children" ? slotDefinition.allowedChildren : [];
        if (!allowedChildren.includes(active.data.current.target.name)) {
          return;
        }
        const queuedSelection = {
          target: {
            type: "slot",
            name: over.data.current.target.name,
            index: over.data.current.target.index,
            slot: over.data.current.target.slot
          },
          childIndex: over.data.current.index,
          placement: null,
          editMode: null
        };
        switch (active.data.current.action) {
          case "insert": {
            const componentDefinition = visualEditor.actions.getComponentDefinition(active.data.current.target.name);
            if (componentDefinition.prose) {
              queuedSelection.placement = { anchor: "start" };
            }
            visualEditor.actions.queueSelection(queuedSelection);
            if (over.data.current.index >= 1) {
              visualEditor.commands.insertComponent(active.data.current.preset.content, {
                sibling: over.data.current.target.children[over.data.current.index - 1],
                position: "after"
              });
            } else {
              visualEditor.commands.insertComponent(active.data.current.preset.content, {
                parent: over.data.current.target,
                position: over.data.current.index === 0 ? "prepend" : "append"
              });
            }
            visualEditor.actions.track("component_added", {
              componentName: active.data.current.target.name,
              presetLabel: active.data.current.preset.label,
              using: "Insert Panel"
            });
            break;
          }
          case "move": {
            const parent = visualEditor.actions.getParentComponentTarget(active.data.current.target);
            if (parent && parent.name === over.data.current.target.name && parent.index === over.data.current.target.index) {
              const { name, type, index } = active.data.current.target;
              const currentIndex = over.data.current.target.children.findIndex(
                (child) => child.name === name && child.type === type && child.index === index
              );
              if (queuedSelection.childIndex > currentIndex) {
                queuedSelection.childIndex--;
              } else if (queuedSelection.childIndex === currentIndex) {
                break;
              }
            }
            queuedSelection.editMode = "structure";
            visualEditor.actions.queueSelection(queuedSelection);
            if (over.data.current.index >= 1) {
              visualEditor.commands.moveComponent(active.data.current.target, {
                sibling: over.data.current.target.children[over.data.current.index - 1],
                position: "after"
              });
            } else {
              visualEditor.commands.moveComponent(active.data.current.target, {
                parent: over.data.current.target,
                position: over.data.current.index === 0 ? "prepend" : "append"
              });
            }
            break;
          }
          default:
            throw new Error("Unknown dragging action");
        }
      },
      children
    }
  ) });
}

// src/visual-editor/overlays/index.tsx
import { Fragment as Fragment10, jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
function Overlays() {
  const { visualEditor } = useEncompass();
  const { active } = useDndContext();
  const anythingIsBeingDragged = !!active;
  return /* @__PURE__ */ jsxs11(Fragment10, { children: [
    anythingIsBeingDragged && /* @__PURE__ */ jsx18(
      MarshaledPreviewOverlay,
      {
        size: "100%",
        style: {
          top: visualEditor.state.scroll.top,
          left: visualEditor.state.scroll.left,
          height: "100%",
          position: "absolute",
          zIndex: -1,
          cursor: "grabbing"
        },
        interactive: true
      }
    ),
    /* @__PURE__ */ jsx18(DraggingOverlay2, {}),
    visualEditor.state.tree ? /* @__PURE__ */ jsxs11(Fragment10, { children: [
      /* @__PURE__ */ jsx18(InteractionOverlays, { node: visualEditor.state.tree }),
      /* @__PURE__ */ jsx18(DropTarget, { node: visualEditor.state.tree })
    ] }) : null,
    /* @__PURE__ */ jsx18(BubbleMenu, {}),
    /* @__PURE__ */ jsx18(CommandMenu, {})
  ] });
}
function DraggingOverlay2() {
  const { visualEditor } = useEncompass();
  if (!visualEditor.state.draggingBoundingBox) {
    return null;
  }
  const { y, x, width, height } = visualEditor.state.draggingBoundingBox;
  const normalizedY = height < 0 ? y + height : y;
  const normalizedX = width < 0 ? x + width : x;
  const normalizedWidth = Math.abs(width);
  const normalizedHeight = Math.abs(height);
  return /* @__PURE__ */ jsx18(
    MarshaledPreviewOverlay,
    {
      top: normalizedY,
      left: normalizedX,
      width: normalizedWidth,
      height: normalizedHeight,
      backgroundColor: "rgb(34, 121, 221, 0.3)",
      style: {
        borderRadius: 4
      }
    }
  );
}

// src/visual-editor/insert-menu/index.tsx
import { IS_CIO_FLY_ENVIRONMENT as IS_CIO_FLY_ENVIRONMENT3 } from "@customerio/carta-visualized";
import { jsx as jsx19, jsxs as jsxs12 } from "react/jsx-runtime";
var isCioFlyEnvironment3 = IS_CIO_FLY_ENVIRONMENT3();
function InsertMenu() {
  const { visualEditor } = useEncompass();
  let componentKeys = visualEditor.state.registeredComponents;
  if (isCioFlyEnvironment3) {
    componentKeys = componentKeys.filter(
      (key) => !["column", "row", "x-column", "x-columns", "x-box", "x-section", "x-video"].includes(key)
    );
  }
  const insertSections = {
    default: { section: "default", items: [] }
  };
  componentKeys.forEach((key) => {
    const definition = visualEditor.actions.getComponentDefinition(key);
    definition.presets.forEach((preset) => {
      if (preset.section) {
        insertSections[preset.section] = insertSections[preset.section] ?? { section: preset.section, items: [] };
        insertSections[preset.section].items.push({ name: definition.name, preset });
      } else {
        insertSections.default.items.push({ name: definition.name, preset });
      }
    });
  });
  if (insertSections.default.items.length === 0) {
    delete insertSections.default;
  }
  return /* @__PURE__ */ jsxs12(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        background: "white",
        height: "100%"
      },
      children: [
        /* @__PURE__ */ jsx19(
          "h3",
          {
            style: {
              padding: "16px 16px 6px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#4B4B60",
              margin: 0
            },
            children: "Insert"
          }
        ),
        /* @__PURE__ */ jsx19(
          "div",
          {
            style: {
              flexGrow: 1,
              height: 0,
              overflowY: "auto",
              padding: "0 16px 16px"
            },
            children: Object.values(insertSections).map((section) => {
              return /* @__PURE__ */ jsx19(InsertSection, { section: section.section, items: section.items }, section.section);
            })
          }
        )
      ]
    }
  );
}
function InsertSection({
  section,
  items
}) {
  const [open, setOpen] = useState9(false);
  return /* @__PURE__ */ jsxs12("div", { children: [
    /* @__PURE__ */ jsx19(
      "hr",
      {
        style: {
          margin: "6px 0",
          border: "none",
          height: 1,
          background: "#E5E5E5"
        }
      }
    ),
    section !== "default" ? /* @__PURE__ */ jsxs12(
      "button",
      {
        style: {
          color: "inherit",
          background: "transparent",
          border: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          cursor: "pointer",
          padding: "12px 0"
        },
        onClick: () => {
          setOpen(!open);
        },
        children: [
          /* @__PURE__ */ jsx19(
            "h3",
            {
              style: {
                margin: 0,
                fontWeight: 600,
                fontSize: "13px"
              },
              children: section
            }
          ),
          /* @__PURE__ */ jsx19(Icon, { icon: open ? "expand_less" : "expand_more", size: 16 })
        ]
      }
    ) : null,
    open || section === "default" ? /* @__PURE__ */ jsx19(
      "div",
      {
        style: {
          margin: "0",
          display: "grid",
          // as many columns as will fit with a min and max width
          gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
          // rows should shrink to fit content
          gridAutoRows: "min-content"
        },
        children: items.map((item) => {
          return /* @__PURE__ */ jsx19(DraggableInsertItem, { name: item.name, preset: item.preset }, `${item.name}-${item.preset.label}`);
        })
      }
    ) : null
  ] });
}
function DraggableInsertItem({
  name,
  preset
}) {
  const id = useId3();
  const targetRef = useRef8(null);
  const [elementRect, setElementRect] = useState9(null);
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable2({
    id,
    data: {
      action: "insert",
      target: {
        type: "component",
        name
      },
      preset: {
        label: preset.label,
        content: preset.content
      }
    }
  });
  useEffect10(() => {
    if (isDragging) {
      const rect = targetRef.current.getBoundingClientRect();
      setElementRect(rect);
    } else {
      setElementRect(null);
    }
  }, [isDragging]);
  const icon = /* @__PURE__ */ jsx19(
    "div",
    {
      style: {
        display: "flex",
        height: 24,
        width: 24,
        justifyContent: "center",
        alignItems: "center",
        marginRight: 8,
        borderRadius: 4,
        border: "1px solid #EBECF2",
        background: "#F7F8FA"
      },
      children: /* @__PURE__ */ jsx19(Icon, { icon: preset.icon || "drag_indicator", size: 14 })
    }
  );
  const isDraggingVisible = isDragging && elementRect;
  return /* @__PURE__ */ jsxs12("div", { children: [
    /* @__PURE__ */ jsxs12(
      Wrapper,
      {
        ref: (ref) => {
          targetRef.current = ref;
        },
        ...isDraggingVisible ? {} : {
          ...listeners,
          ...attributes
        },
        children: [
          icon,
          preset.label
        ]
      }
    ),
    isDragging && elementRect ? /* @__PURE__ */ jsxs12(
      Wrapper,
      {
        ref: setNodeRef,
        style: {
          top: elementRect.top,
          transform: `translate(${transform.x}px, ${transform.y}px)`,
          position: "fixed",
          zIndex: 100,
          borderColor: "#EBECF2",
          width: elementRect.width
        },
        ...listeners,
        ...attributes,
        children: [
          icon,
          preset.label
        ]
      }
    ) : null
  ] });
}
var Wrapper = styled12("div", {
  padding: "8px 8px",
  margin: "0 -8px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#343446",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  background: "#FFFFFF",
  borderRadius: "4px",
  cursor: "grab",
  border: "1px solid transparent",
  "&:hover": {
    borderColor: "#EBECF2"
  }
});

// src/visual-editor/layers/index.tsx
import { styled as styled14 } from "@stitches/react";
import { cloneDeep, compact, get, omit, set } from "lodash";
import { Fragment as Fragment11, useEffect as useEffect11, useState as useState10 } from "react";

// src/components/context-menu.tsx
import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { styled as styled13 } from "@stitches/react";
var {
  Root: Root9,
  Trigger: Trigger4,
  Portal: Portal2,
  Label: Label2,
  Group: Group2,
  CheckboxItem: CheckboxItem2,
  RadioGroup: RadioGroup2,
  RadioItem: RadioItem2,
  ItemIndicator: ItemIndicator2,
  Sub: Sub2,
  SubTrigger: SubTrigger4,
  SubContent: SubContent3,
  Separator: Separator4
} = RadixContextMenu;
var Content9 = styled13(RadixContextMenu.Content, {
  minWidth: 220,
  backgroundColor: "white",
  borderRadius: 4,
  overflow: "hidden",
  boxShadow: "rgb(73 79 186 / 8%) 0 3px 4px 0px, rgb(73 79 186 / 2%) 0 -1px 3px 0px",
  border: "1px solid #EBECF2",
  zIndex: 999
});
var Item5 = styled13(RadixContextMenu.Item, {
  fontSize: 13,
  lineHeight: 1,
  color: "#343446",
  display: "flex",
  alignItems: "center",
  height: 32,
  padding: "0 8px",
  position: "relative",
  paddingLeft: 25,
  userSelect: "none",
  outline: "none",
  cursor: "pointer",
  "&[data-disabled]": {
    color: "#A1A2B2",
    pointerEvents: "none",
    cursor: "not-allowed"
  },
  "&[data-highlighted]": {
    backgroundColor: "#F7F8FA"
  }
});

// src/visual-editor/layers/index.tsx
import {
  DndContext as DndContext2,
  KeyboardSensor as KeyboardSensor2,
  MouseSensor as MouseSensor2,
  useDndContext as useDndContext2,
  useDraggable as useDraggable3,
  useDroppable as useDroppable2,
  useSensor as useSensor2,
  useSensors as useSensors2
} from "@dnd-kit/core";
import {
  CARTA_DATA_SELECTED_TEST_ATTRIBUTE,
  CARTA_DATA_TEST_ATTRIBUTE as CARTA_DATA_TEST_ATTRIBUTE2,
  IS_DEV_ENVIRONMENT as IS_DEV_ENVIRONMENT2
} from "@customerio/carta-visualized";
import { Fragment as Fragment12, jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
function Layers() {
  const { visualEditor } = useEncompass();
  function makeTreeCollapsible(node) {
    return {
      ...node,
      children: compact(node.children.flatMap((child, index) => makeNodeCollapsible(child, `children.${index}`)))
    };
  }
  function makeNodeCollapsible(node, path) {
    if (node.type === "component") {
      const definition = visualEditor.actions.getComponentDefinition(node.name);
      if (!definition) {
        return null;
      }
      const childrenSlots = Object.entries(definition?.slots?.definitions ?? {}).filter(([, slotDefinition]) => {
        return slotDefinition?.type === "children";
      }).map(([slotName, slotDefinition], slotIndex) => {
        if (slotDefinition.type !== "children") {
          return;
        }
        const slot = node.children.find((child) => child.type === "slot" && child.slot === slotName);
        return {
          slot: slotName,
          label: slotDefinition.label,
          allowedChildren: slotDefinition.allowedChildren,
          children: slot ? compact(
            slot.children.flatMap(
              (child, childIndex) => makeNodeCollapsible(child, `${path}.slots.${slotIndex}.children.${childIndex}`)
            )
          ) : []
        };
      });
      return {
        ...omit(node, ["children"]),
        label: definition.label,
        id: `${node.name}-${node.index}`,
        collapsed: false,
        collapsible: Object.keys(childrenSlots).length > 0,
        slots: childrenSlots,
        path
      };
    }
    throw new Error(`Unexpected node type: ${node.type}`);
  }
  const [tree, setTree] = useState10(null);
  useEffect11(() => {
    if (visualEditor.state.tree) {
      setTree(makeTreeCollapsible(visualEditor.state.tree));
    }
  }, [visualEditor.state.tree]);
  const [lastSelectedComponentId, setLastSelectedComponentId] = useState10(null);
  useEffect11(() => {
    const selectedComponent = visualEditor.state.editMode === "content" ? visualEditor.state.contentTarget : visualEditor.state.editMode === "structure" ? visualEditor.state.selectedComponents?.[0] : null;
    if (!selectedComponent) {
      return;
    }
    const id = `encompass-layer-${selectedComponent.name}-${selectedComponent.index}`;
    if (lastSelectedComponentId === id) {
      return;
    }
    setLastSelectedComponentId(id);
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    const parent = document.querySelector("#encompass-layers-tree");
    const parentRect = parent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    if (elementRect.top >= parentRect.top && elementRect.bottom <= parentRect.bottom) {
      return;
    }
    element.scrollIntoView({ block: "center" });
  }, [visualEditor.state.editMode, visualEditor.state.selectedComponents, visualEditor.state.contentTarget]);
  useEffect11(() => {
    if (tree == null || !visualEditor.state.selectedComponents?.length) {
      return;
    }
    const selectedChild = findBySelected(tree, visualEditor.state.selectedComponents[0]);
    if (!selectedChild) {
      return;
    }
    const clonedTree = cloneDeep(tree);
    let item = clonedTree;
    let isTreeChanged = false;
    selectedChild.path.split(".").forEach((key) => {
      if ("collapsed" in item && item.collapsed) {
        item.collapsed = false;
        isTreeChanged = true;
      }
      item = item[key];
    });
    if (isTreeChanged) {
      setTree(clonedTree);
    }
  }, [lastSelectedComponentId]);
  const actions = {
    toggle(componentPath) {
      setTree((tree2) => {
        if (!tree2) {
          return tree2;
        }
        const clonedTree = cloneDeep(tree2);
        const node = get(clonedTree, componentPath);
        if (!node) {
          return clonedTree;
        }
        if (!node.collapsible) {
          return clonedTree;
        }
        set(clonedTree, componentPath, { ...node, collapsed: !node.collapsed });
        return clonedTree;
      });
    },
    move(componentPath, newParentPath, slot, index) {
      const node = get(tree, componentPath);
      const newParent = get(tree, newParentPath);
      const target = {
        type: "component",
        name: node.name,
        index: node.index
      };
      if (index >= 1) {
        const newParentSlot = newParent.slots.find((s) => s.slot === slot);
        visualEditor.commands.moveComponent(target, {
          sibling: newParentSlot.children[index - 1],
          position: "after"
        });
      } else {
        visualEditor.commands.moveComponent(target, {
          parent: {
            type: "slot",
            name: newParent.name,
            index: newParent.index,
            slot
          },
          position: index === 0 ? "prepend" : "append"
        });
      }
    }
  };
  const activationConstraint = {
    distance: 15
    // drag distance in pixels before activation
  };
  const mouseSensor = useSensor2(MouseSensor2, {
    activationConstraint
  });
  const keyboardSensor = useSensor2(KeyboardSensor2, {});
  const sensors = useSensors2(mouseSensor, keyboardSensor);
  return /* @__PURE__ */ jsx20(Fragment12, { children: /* @__PURE__ */ jsxs13(Container2, { children: [
    /* @__PURE__ */ jsx20(
      "h3",
      {
        style: {
          padding: "16px 16px 16px 16px",
          fontSize: "14px",
          fontWeight: 600,
          color: "#4B4B60",
          margin: 0
        },
        children: "Layers"
      }
    ),
    /* @__PURE__ */ jsx20(
      "div",
      {
        style: {
          flexGrow: 1,
          height: 0,
          overflow: "auto"
        },
        id: "encompass-layers-tree",
        children: tree ? /* @__PURE__ */ jsx20(
          DndContext2,
          {
            sensors,
            onDragEnd: ({ active, over }) => {
              if (!active || !over) {
                return;
              }
              const activeComponent = get(tree, active.data.current.path);
              const overComponent = get(tree, over.data.current.path);
              if (!activeComponent || !overComponent) {
                return;
              }
              const overSlot = overComponent.slots.find((slot) => slot.slot === over.data.current.slot);
              if (!overSlot) {
                return;
              }
              if (!overSlot.allowedChildren.includes(activeComponent.name)) {
                return;
              }
              actions.move(active.data.current.path, over.data.current.path, overSlot.slot, over.data.current.index);
            },
            children: tree.children.map((child, index) => {
              return /* @__PURE__ */ jsx20(Fragment11, { children: /* @__PURE__ */ jsx20(ComponentLayer, { path: child.path, component: child, depth: 0, actions }) }, `children.${index}`);
            })
          }
        ) : /* @__PURE__ */ jsx20(CenteredText, { children: "No Selection" })
      }
    )
  ] }) });
}
var Container2 = styled14("div", {
  display: "flex",
  flexDirection: "column",
  background: "white",
  height: "100%",
  ".collapse-button": {
    opacity: 0
  },
  "&:hover .collapse-button": {
    opacity: 1
  }
});
function ComponentLayer({
  path,
  component,
  depth,
  actions
}) {
  const { visualEditor } = useEncompass();
  const { attributes, listeners, setNodeRef } = useDraggable3({
    id: component.id,
    data: { path, name: component.name }
  });
  const { active } = useDndContext2();
  const nothingIsBeingDragged = !active;
  const isHovered = nothingIsBeingDragged && // disable hover highlighting when dragging
  component.index === visualEditor.state.hoveredComponent?.index && component.name === visualEditor.state.hoveredComponent?.name;
  const isSelected = visualEditor.state.editMode === "content" ? visualEditor.state.contentTarget?.index === component.index && visualEditor.state.contentTarget?.name === component.name : visualEditor.state.editMode === "structure" ? visualEditor.state.selectedComponents.some((selected) => {
    return selected.index === component.index && selected.name === component.name;
  }) : false;
  const componentTarget = {
    type: "component",
    name: component.name,
    index: component.index
  };
  const id = `encompass-layer-${component.name}-${component.index}`;
  let containerProps = {};
  let buttonProps = {};
  let contextMenuProps = {};
  let contextMenuItemProps = {};
  if (IS_DEV_ENVIRONMENT2()) {
    containerProps[CARTA_DATA_TEST_ATTRIBUTE2] = `${id}-container`;
    buttonProps[CARTA_DATA_TEST_ATTRIBUTE2] = `${id}-button`;
    contextMenuProps[CARTA_DATA_TEST_ATTRIBUTE2] = `layers-context-menu`;
    contextMenuItemProps[CARTA_DATA_TEST_ATTRIBUTE2] = `layers-context-menu-item`;
    if (isSelected) {
      containerProps[CARTA_DATA_SELECTED_TEST_ATTRIBUTE] = "";
      buttonProps[CARTA_DATA_SELECTED_TEST_ATTRIBUTE] = "";
    }
  }
  const hasMultipleSlots = component.slots.length > 1;
  const isRootComponent = visualEditor.actions.isRootComponent(componentTarget);
  return /* @__PURE__ */ jsx20(Fragment12, { children: /* @__PURE__ */ jsxs13(
    "div",
    {
      ...containerProps,
      style: {
        width: "100%",
        // #f1eaff with opacity
        background: isSelected ? "#E9F2FC" : "transparent"
      },
      children: [
        /* @__PURE__ */ jsxs13(Root9, { children: [
          /* @__PURE__ */ jsx20(Trigger4, { children: /* @__PURE__ */ jsxs13(
            "button",
            {
              id,
              ...buttonProps,
              ...component.type === "component" && {
                onMouseOver: (event) => {
                  event.stopPropagation();
                },
                onMouseEnter: () => {
                  visualEditor.actions.setHoveredComponent(componentTarget);
                },
                onClick: (event) => {
                  if (!event.metaKey) {
                    return visualEditor.actions.setSelectedComponent(componentTarget);
                  }
                  if (visualEditor.actions.isSelectedComponent(componentTarget)) {
                    visualEditor.actions.removeSelectedComponent(componentTarget);
                  } else {
                    visualEditor.actions.addSelectedComponent(componentTarget);
                  }
                }
              },
              style: {
                textAlign: "left",
                cursor: "pointer",
                width: "100%",
                padding: `6px 4px`,
                display: "flex",
                border: isHovered && !isSelected ? "1px solid #0652BB" : "1px solid transparent",
                background: isSelected ? "#0652BB" : "transparent",
                color: isSelected ? "white" : "#5D6074",
                paddingLeft: `${depth * 20}px`,
                alignItems: "stretch",
                fontSize: "13px",
                fontWeight: 500
              },
              ...isRootComponent ? {} : { ...listeners, ...attributes },
              ref: setNodeRef,
              onContextMenu: () => {
                visualEditor.actions.setSelectedComponent(componentTarget);
              },
              children: [
                /* @__PURE__ */ jsx20(
                  "div",
                  {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      width: 18
                    },
                    children: component.collapsible && /* @__PURE__ */ jsx20(
                      "span",
                      {
                        style: {
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          margin: 0,
                          height: "100%",
                          width: "100%",
                          textAlign: "center",
                          transform: component.collapsed ? "rotate(0deg)" : "rotate(90deg)",
                          cursor: "pointer",
                          color: isSelected ? "white" : "#4B4B60"
                        },
                        className: "material-symbols-outlined collapse-button",
                        onMouseDown: (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        },
                        onPointerDown: (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        },
                        onClick: (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          actions.toggle(path);
                        },
                        children: "arrow_right"
                      }
                    )
                  }
                ),
                component.label
              ]
            }
          ) }),
          /* @__PURE__ */ jsx20(Portal2, { children: /* @__PURE__ */ jsxs13(Content9, { ...contextMenuProps, children: [
            /* @__PURE__ */ jsx20(
              Item5,
              {
                ...contextMenuItemProps,
                onClick: () => {
                  visualEditor.commands.duplicateComponent(componentTarget);
                  visualEditor.actions.track("component_duplicated", {
                    componentName: componentTarget.name,
                    presetLabel: "",
                    using: "Layers Panel"
                  });
                },
                ...isRootComponent ? { disabled: true } : {},
                children: "Duplicate"
              }
            ),
            /* @__PURE__ */ jsx20(
              Item5,
              {
                ...contextMenuItemProps,
                onClick: () => {
                  visualEditor.actions.queueSiblingSelection(componentTarget, "structure");
                  visualEditor.commands.deleteComponent(componentTarget);
                },
                css: {
                  color: "#DB234B"
                },
                ...isRootComponent ? { disabled: true } : {},
                children: "Delete"
              }
            )
          ] }) })
        ] }),
        component.collapsed ? null : /* @__PURE__ */ jsx20(Fragment12, { children: component.slots.map((slot) => {
          return /* @__PURE__ */ jsxs13(Fragment11, { children: [
            hasMultipleSlots ? /* @__PURE__ */ jsx20(
              "h4",
              {
                style: {
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#6F7488",
                  margin: "4px 0",
                  marginLeft: `${(depth + 1) * 20 + 18}px`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textTransform: "uppercase"
                },
                children: slot.label
              }
            ) : null,
            slot.children.map((child, index) => {
              return /* @__PURE__ */ jsxs13(Fragment11, { children: [
                /* @__PURE__ */ jsx20(
                  SlotDropTarget,
                  {
                    path,
                    slot: slot.slot,
                    index,
                    depth: depth ? depth + 1 : 1,
                    allowedChildren: slot.allowedChildren
                  }
                ),
                /* @__PURE__ */ jsx20(
                  ComponentLayer,
                  {
                    path: child.path,
                    component: child,
                    depth: depth ? depth + 1 : 1,
                    actions
                  }
                )
              ] }, index);
            }),
            /* @__PURE__ */ jsx20(
              SlotDropTarget,
              {
                path,
                slot: slot.slot,
                index: -1,
                depth: depth ? depth + 1 : 1,
                allowedChildren: slot.allowedChildren
              }
            )
          ] }, slot.slot);
        }) })
      ]
    }
  ) });
}
function findBySelected(obj, selected) {
  if (obj == null) {
    return null;
  }
  if (obj.name === selected.name && obj.type === selected.type && obj.index === selected.index) {
    return obj;
  }
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const result = findBySelected(obj[i], selected);
      if (result !== null) {
        return result;
      }
    }
  }
  const keys = ["slots", "children"];
  for (const key of keys) {
    if (key in obj) {
      const result = findBySelected(obj[key], selected);
      if (result !== null) {
        return result;
      }
    }
  }
  return null;
}
function SlotDropTarget({
  path,
  slot,
  index,
  depth,
  allowedChildren
}) {
  const { setNodeRef, isOver, active } = useDroppable2({
    id: `${path}-${index}-${slot}`,
    data: { path, index, slot }
  });
  const isAllowedToDrop = allowedChildren.includes(active?.data?.current?.name);
  if (!isAllowedToDrop) {
    return;
  }
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref: setNodeRef,
      style: {
        // height is 10px so it is an easy target
        height: "10px",
        marginLeft: `${depth * 20 + 18}px`,
        marginTop: "-5px",
        marginBottom: "-5px",
        zIndex: 1e3
      },
      children: /* @__PURE__ */ jsx20(
        "div",
        {
          style: {
            height: "2px",
            background: isOver ? "#0652BB" : "transparent",
            top: "50%",
            transform: "translateY(-50%)"
          }
        }
      )
    }
  );
}
function CenteredText({ children }) {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        color: "#6F7488"
      },
      children
    }
  );
}

// src/visual-editor/properties-menu/core/index.tsx
import { DndContext as DndContext3, KeyboardSensor as KeyboardSensor3, MouseSensor as MouseSensor3, useDraggable as useDraggable4, useSensor as useSensor3, useSensors as useSensors3 } from "@dnd-kit/core";
import * as RadixPopover2 from "@radix-ui/react-popover";
import { styled as styled24 } from "@stitches/react";
import { useEffect as useEffect18, useRef as useRef9, useState as useState19 } from "react";
import { Portal as Portal5 } from "@radix-ui/react-portal";

// src/visual-editor/properties-menu/shared/properties-menu.tsx
import * as Popover4 from "@radix-ui/react-popover";
import { styled as styled23 } from "@stitches/react";
import { isNaN as isNaN4 } from "lodash";
import React5, { memo, useEffect as useEffect17, useMemo as useMemo4, useState as useState18 } from "react";

// src/visual-editor/properties-menu/buttons/add-set.tsx
import { styled as styled15 } from "@stitches/react";
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var AddSetButton = styled15(
  function AddSetButton2(props) {
    return /* @__PURE__ */ jsxs14("button", { ...props, children: [
      props.children,
      /* @__PURE__ */ jsx21(Icon, { icon: "add" })
    ] });
  },
  {
    display: "inline-flex",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    font: "inherit",
    fontWeight: 500,
    fontSize: "13px",
    justifyContent: "space-between",
    marginLeft: "auto",
    gap: 8,
    alignItems: "center",
    borderRadius: "4px",
    padding: "0 8px",
    height: "28px",
    "&:hover": {
      color: "#0652BB",
      backgroundColor: "#F1EAFF"
    },
    '&[data-state="open"]': {
      color: "#0652BB",
      backgroundColor: "#F1EAFF"
    }
  }
);

// src/visual-editor/properties-menu/buttons/select-media.tsx
import { styled as styled16 } from "@stitches/react";
import { jsx as jsx22 } from "react/jsx-runtime";
function SelectMediaButton({
  label,
  disabled,
  onSelect,
  onSelected,
  style
}) {
  return /* @__PURE__ */ jsx22(
    StyledButton,
    {
      disabled,
      onClick: () => {
        onSelect().then((imageUrl) => {
          if (imageUrl) {
            onSelected(imageUrl);
          }
        });
      },
      style,
      type: "button",
      children: label
    }
  );
}
var StyledButton = styled16("button", {
  display: "inline-flex",
  border: "none",
  background: "transparent",
  color: "#0652BB",
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  fontSize: "12px",
  padding: "4px"
});

// src/components/inputs/box.tsx
import { isNaN as isNaN2 } from "lodash";

// src/components/inputs/code.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var CodeInput = forwardRef6((props, ref) => {
  return /* @__PURE__ */ jsx23(
    TextInput,
    {
      style: {
        fontFamily: "monospace",
        ...props.style
      },
      ...props,
      ref
    }
  );
});

// src/components/inputs/box.tsx
import { useState as useState11, useEffect as useEffect12 } from "react";
import { Fragment as Fragment13, jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
function BoxInput({ value, onChange }) {
  const [internalValue, setInternalValue] = useState11(value || "");
  useEffect12(() => {
    if (value !== internalValue) {
      setInternalValue(value || "");
    }
  }, [value, internalValue]);
  try {
    const box = parseBox(internalValue);
    if (!box) {
      return /* @__PURE__ */ jsx24(
        BoxInputInternal,
        {
          box: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          },
          onChange
        }
      );
    }
    if (typeof box.top === "string" || typeof box.right === "string" || typeof box.bottom === "string" || typeof box.left === "string") {
      throw new Error("Invalid box");
    }
    return /* @__PURE__ */ jsx24(
      BoxInputInternal,
      {
        box: {
          top: box.top,
          right: box.right,
          bottom: box.bottom,
          left: box.left
        },
        onChange
      }
    );
  } catch (error) {
    return /* @__PURE__ */ jsx24(CodeInput, { value: internalValue, onChange });
  }
}
function BoxInputInternal({
  box,
  onChange: rawOnChange
}) {
  const [{ top, right, bottom, left }, setBox] = useState11(box);
  const [isExpanded, setIsExpanded] = useState11(top === bottom && right === left ? false : true);
  useEffect12(() => {
    if (JSON.stringify(box) !== JSON.stringify({ top, right, bottom, left })) {
      setBox(box);
    }
  }, [JSON.stringify(box)]);
  function onChange({ top: top2, right: right2, bottom: bottom2, left: left2 }) {
    let value;
    if (top2 === right2 && right2 === bottom2 && bottom2 === left2) {
      value = `${top2}px`;
    } else if (top2 === bottom2 && right2 === left2) {
      value = `${top2}px ${right2}px`;
    } else if (right2 === left2) {
      value = `${top2}px ${right2}px ${bottom2}px`;
    } else {
      value = `${top2}px ${right2}px ${bottom2}px ${left2}px`;
    }
    rawOnChange(value);
    setBox({
      top: top2,
      right: right2,
      bottom: bottom2,
      left: left2
    });
  }
  return /* @__PURE__ */ jsx24(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "flex-start"
      },
      children: /* @__PURE__ */ jsxs15(
        "div",
        {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr min-content",
            gap: "8px"
          },
          children: [
            /* @__PURE__ */ jsx24(
              NumberInput,
              {
                value: left,
                leadingIcon: isExpanded ? leftIcon : horizontalIcon,
                onChange: (value) => {
                  if (isExpanded) {
                    return onChange({
                      top,
                      right,
                      bottom,
                      left: value
                    });
                  }
                  onChange({
                    top,
                    right: value,
                    bottom,
                    left: value
                  });
                }
              }
            ),
            /* @__PURE__ */ jsx24(
              NumberInput,
              {
                value: top,
                leadingIcon: isExpanded ? topIcon : verticalIcon,
                onChange: (value) => {
                  if (isExpanded) {
                    return onChange({
                      top: value,
                      right,
                      bottom,
                      left
                    });
                  }
                  onChange({
                    top: value,
                    right,
                    bottom: value,
                    left
                  });
                }
              }
            ),
            /* @__PURE__ */ jsx24(
              "button",
              {
                style: {
                  backgroundColor: "transparent",
                  border: "none",
                  padding: "6px 0",
                  cursor: "pointer",
                  font: "inherit"
                },
                onClick: () => setIsExpanded((isExpanded2) => !isExpanded2),
                className: "material-symbols-outlined",
                children: /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 16, height: 16, fill: "none", viewBox: "0 0 24 24", children: [
                  /* @__PURE__ */ jsx24("path", { fill: "#fff", d: "M0 0h24v24H0z" }),
                  /* @__PURE__ */ jsx24(
                    "path",
                    {
                      fill: "#6F7488",
                      fillRule: "evenodd",
                      d: "M6.75 20a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75ZM6.75 4a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9A.75.75 0 0 1 6.75 4ZM20 6.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9a.75.75 0 0 1 .75-.75ZM4 6.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9A.75.75 0 0 1 4 6.75Z",
                      clipRule: "evenodd"
                    }
                  )
                ] })
              }
            ),
            isExpanded ? /* @__PURE__ */ jsxs15(Fragment13, { children: [
              /* @__PURE__ */ jsx24(
                NumberInput,
                {
                  value: right,
                  leadingIcon: rightIcon,
                  onChange: (value) => {
                    onChange({
                      top,
                      right: value,
                      bottom,
                      left
                    });
                  }
                }
              ),
              /* @__PURE__ */ jsx24(
                NumberInput,
                {
                  value: bottom,
                  leadingIcon: bottomIcon,
                  onChange: (value) => {
                    onChange({
                      top,
                      right,
                      bottom: value,
                      left
                    });
                  }
                }
              )
            ] }) : null
          ]
        }
      )
    }
  );
}
function parseBox(value) {
  if (!value) {
    return null;
  }
  const parts = value.trim().split(/\s+/);
  if (parts.length === 0) {
    return null;
  }
  if (parts.length === 1) {
    return {
      top: pxToNumber(parts[0]),
      right: pxToNumber(parts[0]),
      bottom: pxToNumber(parts[0]),
      left: pxToNumber(parts[0])
    };
  }
  if (parts.length === 2) {
    return {
      top: pxToNumber(parts[0]),
      right: pxToNumber(parts[1]),
      bottom: pxToNumber(parts[0]),
      left: pxToNumber(parts[1])
    };
  }
  if (parts.length === 3) {
    return {
      top: pxToNumber(parts[0]),
      right: pxToNumber(parts[1]),
      bottom: pxToNumber(parts[2]),
      left: pxToNumber(parts[1])
    };
  }
  if (parts.length === 4) {
    return {
      top: pxToNumber(parts[0]),
      right: pxToNumber(parts[1]),
      bottom: pxToNumber(parts[2]),
      left: pxToNumber(parts[3])
    };
  }
  throw new Error("Invalid box");
}
function pxToNumber(value) {
  const number = Number(value.trim().replace("px", ""));
  if (isNaN2(number)) {
    return value;
  }
  return number;
}
var verticalIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 24 24", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M6.75 20a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75ZM6.75 4a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9A.75.75 0 0 1 6.75 4Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24("rect", { width: 8, height: 8, x: 8, y: 8, stroke: "#5D6074", strokeWidth: 1.5, rx: 1 })
] });
var horizontalIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 24 24", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M20 6.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9a.75.75 0 0 1 .75-.75ZM4 6.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9A.75.75 0 0 1 4 6.75Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24("rect", { width: 8, height: 8, x: 8, y: 8, stroke: "#5D6074", strokeWidth: 1.5, rx: 1 })
] });
var topIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 30 30", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M9.75 7a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9A.75.75 0 0 1 9.75 7Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24(
    "path",
    {
      stroke: "#C7C7D4",
      strokeDasharray: "3 3",
      strokeLinecap: "round",
      strokeWidth: 1.5,
      d: "M9.75 23h10.5M7 9.75v10.5M23 9.75v10.5"
    }
  )
] });
var rightIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 30 30", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M23 9.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9a.75.75 0 0 1 .75-.75Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24(
    "path",
    {
      stroke: "#C7C7D4",
      strokeDasharray: "3 3",
      strokeLinecap: "round",
      strokeWidth: 1.5,
      d: "M9.75 7h10.5M9.75 23h10.5M7 9.75v10.5"
    }
  )
] });
var bottomIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 30 30", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      stroke: "#C7C7D4",
      strokeDasharray: "3 3",
      strokeLinecap: "round",
      strokeWidth: 1.5,
      d: "M7 9.75v10.5M23 9.75v10.5"
    }
  ),
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M9.75 23a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24("path", { stroke: "#C7C7D4", strokeDasharray: "3 3", strokeLinecap: "round", strokeWidth: 1.5, d: "M9.75 7h10.5" })
] });
var leftIcon = /* @__PURE__ */ jsxs15("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "none", viewBox: "0 0 30 30", children: [
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M7 9.75a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0v-9A.75.75 0 0 1 7 9.75Z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx24(
    "path",
    {
      stroke: "#C7C7D4",
      strokeDasharray: "3 3",
      strokeLinecap: "round",
      strokeWidth: 1.5,
      d: "M9.75 7h10.5M9.75 23h10.5M23 9.75v10.5"
    }
  )
] });

// src/components/inputs/expression.tsx
import { useState as useState12, useEffect as useEffect13 } from "react";
import { jsx as jsx25, jsxs as jsxs16 } from "react/jsx-runtime";
function ExpressionInput({
  value,
  defaultValue,
  onChange
}) {
  const [expressionString, setExpressionString] = useState12(value || defaultValue || "");
  useEffect13(() => {
    if (value != null) {
      setExpressionString(value);
    }
  }, [value]);
  return /* @__PURE__ */ jsxs16(
    "form",
    {
      onSubmit: (event) => {
        event.preventDefault();
        onChange(event.target[0].value);
      },
      children: [
        /* @__PURE__ */ jsx25(
          "textarea",
          {
            defaultValue: expressionString,
            style: {
              width: "100%",
              backgroundColor: " #fff",
              borderRadius: " 6px",
              border: "1px solid #C7C7D4",
              borderColor: "#DFE0E8",
              // breaking the rules
              boxShadow: " 0 0 1px rgb(0 0 0 / 12%) inset",
              color: "#4b4b60",
              display: "inline-block",
              fontSize: ".875rem",
              minHeight: "30px",
              minWidth: "48px",
              verticalAlign: "middle",
              padding: "12px",
              resize: "none"
            }
          }
        ),
        /* @__PURE__ */ jsx25(
          Button,
          {
            type: "submit",
            variant: "secondary",
            style: {
              position: "absolute",
              bottom: "4px",
              right: "4px"
            },
            children: "Save"
          }
        )
      ]
    }
  );
}

// src/components/inputs/radius.tsx
import { isNaN as isNaN3 } from "lodash";
import { useState as useState13 } from "react";
import { jsx as jsx26, jsxs as jsxs17 } from "react/jsx-runtime";
function stringifyRadius(value) {
  return `${value.top}px ${value.right}px ${value.bottom}px ${value.left}px`;
}
function expandBorderRadius(shortForm) {
  const values = shortForm?.split(" ") || "";
  const expanded = {};
  switch (values.length) {
    case 1: {
      const [radius] = values;
      expanded["border-top-left-radius"] = radius;
      expanded["border-top-right-radius"] = radius;
      expanded["border-bottom-right-radius"] = radius;
      expanded["border-bottom-left-radius"] = radius;
      break;
    }
    case 2: {
      const [horizontalRadius, verticalRadius] = values;
      expanded["border-top-left-radius"] = `${horizontalRadius} ${verticalRadius}`;
      expanded["border-top-right-radius"] = `${horizontalRadius} ${verticalRadius}`;
      expanded["border-bottom-right-radius"] = `${horizontalRadius} ${verticalRadius}`;
      expanded["border-bottom-left-radius"] = `${horizontalRadius} ${verticalRadius}`;
      break;
    }
    case 3: {
      const [topLeftRadius, horizontalRadius, bottomRightRadius] = values;
      expanded["border-top-left-radius"] = topLeftRadius;
      expanded["border-top-right-radius"] = `${horizontalRadius} ${topLeftRadius}`;
      expanded["border-bottom-right-radius"] = `${bottomRightRadius} ${topLeftRadius}`;
      expanded["border-bottom-left-radius"] = `${horizontalRadius} ${bottomRightRadius}`;
      break;
    }
    case 4: {
      expanded["border-top-left-radius"] = values[0];
      expanded["border-top-right-radius"] = values[1];
      expanded["border-bottom-right-radius"] = values[2];
      expanded["border-bottom-left-radius"] = values[3];
      break;
    }
    default: {
      const defaultRadius = "";
      expanded["border-top-left-radius"] = defaultRadius;
      expanded["border-top-right-radius"] = defaultRadius;
      expanded["border-bottom-right-radius"] = defaultRadius;
      expanded["border-bottom-left-radius"] = defaultRadius;
    }
  }
  return expanded;
}
function RadiusInput({
  value,
  onChange
}) {
  try {
    const expanded = expandBorderRadius(value);
    const topLeft = Number(expanded["border-top-left-radius"].replace("px", ""));
    const topRight = Number(expanded["border-top-right-radius"].replace("px", ""));
    const bottomRight = Number(expanded["border-bottom-right-radius"].replace("px", ""));
    const bottomLeft = Number(expanded["border-bottom-left-radius"].replace("px", ""));
    if (isNaN3(topLeft) || isNaN3(topRight) || isNaN3(bottomRight) || isNaN3(bottomLeft)) {
      throw new Error("Not a number");
    }
    return /* @__PURE__ */ jsx26(
      RadiusInputInternal,
      {
        topLeft,
        topRight,
        bottomRight,
        bottomLeft,
        onChange
      }
    );
  } catch (error) {
    return /* @__PURE__ */ jsx26(CodeInput, { value, onChange });
  }
}
function RadiusInputInternal({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
  onChange
}) {
  const allEqual = topLeft === topRight && topLeft === bottomRight && topLeft === bottomLeft;
  const [isExpanded, setIsExpanded] = useState13(!allEqual);
  const toggle = /* @__PURE__ */ jsx26(
    "button",
    {
      style: {
        backgroundColor: "transparent",
        border: "none",
        padding: "6px 0",
        margin: 0,
        cursor: "pointer",
        font: "inherit"
      },
      onClick: () => {
        setIsExpanded((isExpanded2) => !isExpanded2);
      },
      children: /* @__PURE__ */ jsxs17("svg", { xmlns: "http://www.w3.org/2000/svg", width: 16, height: 16, fill: "none", viewBox: "0 0 24 24", children: [
        /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h24v24H0z" }),
        /* @__PURE__ */ jsx26(
          "path",
          {
            fill: "#6F7488",
            fillRule: "evenodd",
            d: "M20.75 9.583a.667.667 0 0 1-1.333 0V4.806c0-.123-.1-.223-.223-.223h-4.777a.667.667 0 1 1 0-1.333h4.777c.86 0 1.556.696 1.556 1.556v4.777ZM9.583 3.25a.667.667 0 1 1 0 1.333H4.806c-.123 0-.223.1-.223.223v4.777a.667.667 0 1 1-1.333 0V4.806c0-.86.696-1.556 1.556-1.556h4.777Zm-5.635 10.5a.667.667 0 0 0-.667.667v4.777c0 .86.697 1.556 1.556 1.556h4.778a.667.667 0 0 0 0-1.333H4.837a.222.222 0 0 1-.222-.223v-4.777a.667.667 0 0 0-.667-.667Zm10.469 7a.667.667 0 0 1 0-1.333h4.777c.123 0 .223-.1.223-.223v-4.777a.667.667 0 1 1 1.333 0v4.777c0 .86-.696 1.556-1.556 1.556h-4.777Z",
            clipRule: "evenodd"
          }
        )
      ] })
    }
  );
  if (!isExpanded) {
    return /* @__PURE__ */ jsx26(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "flex-start",
          gap: "8px"
        },
        children: /* @__PURE__ */ jsxs17(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: "1fr 1fr min-content",
              gap: "8px"
            },
            children: [
              /* @__PURE__ */ jsx26("div", {}),
              /* @__PURE__ */ jsx26(
                NumberInput,
                {
                  value: topLeft,
                  onChange: (value) => onChange(stringifyRadius({ top: value, right: value, bottom: value, left: value }))
                }
              ),
              toggle
            ]
          }
        )
      }
    );
  }
  return /* @__PURE__ */ jsx26(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        marginBottom: "8px"
      },
      children: /* @__PURE__ */ jsxs17(
        "div",
        {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr min-content",
            gap: "8px"
          },
          children: [
            /* @__PURE__ */ jsx26("div", {}),
            /* @__PURE__ */ jsx26(TextInput, { value: allEqual ? `${topLeft}` : "Mixed", onChange: () => {
            }, readOnly: true }),
            toggle,
            /* @__PURE__ */ jsx26(
              NumberInput,
              {
                value: topLeft,
                leadingIcon: topLeftIcon,
                onChange: (value) => onChange(stringifyRadius({ top: value, right: topRight, bottom: bottomRight, left: bottomLeft }))
              }
            ),
            /* @__PURE__ */ jsx26(
              NumberInput,
              {
                value: topRight,
                leadingIcon: topRightIcon,
                onChange: (value) => onChange(stringifyRadius({ top: topLeft, right: value, bottom: bottomRight, left: bottomLeft }))
              }
            ),
            /* @__PURE__ */ jsx26("div", {}),
            /* @__PURE__ */ jsx26(
              NumberInput,
              {
                value: bottomLeft,
                leadingIcon: bottomLeftIcon,
                onChange: (value) => onChange(stringifyRadius({ top: topLeft, right: topRight, bottom: bottomRight, left: value }))
              }
            ),
            /* @__PURE__ */ jsx26(
              NumberInput,
              {
                value: bottomRight,
                leadingIcon: bottomRightIcon,
                onChange: (value) => onChange(stringifyRadius({ top: topLeft, right: topRight, bottom: value, left: bottomLeft }))
              }
            )
          ]
        }
      )
    }
  );
}
var topLeftIcon = /* @__PURE__ */ jsxs17("svg", { xmlns: "http://www.w3.org/2000/svg", width: 8, height: 8, fill: "none", children: [
  /* @__PURE__ */ jsx26("g", { clipPath: "url(#a)", children: /* @__PURE__ */ jsx26(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M7.5 1.167A.667.667 0 0 0 6.833.5H2.056C1.196.5.5 1.196.5 2.056v4.777a.667.667 0 0 0 1.333 0V2.056c0-.123.1-.223.223-.223h4.777a.667.667 0 0 0 .667-.666Z",
      clipRule: "evenodd"
    }
  ) }),
  /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "a", children: /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h8v8H0z" }) }) })
] });
var topRightIcon = /* @__PURE__ */ jsxs17("svg", { xmlns: "http://www.w3.org/2000/svg", width: 8, height: 8, fill: "none", children: [
  /* @__PURE__ */ jsx26("g", { clipPath: "url(#a)", children: /* @__PURE__ */ jsx26(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M7.083 7.25a.667.667 0 0 0 .667-.667V1.806C7.75.946 7.054.25 6.194.25H1.417a.667.667 0 1 0 0 1.333h4.777c.123 0 .223.1.223.223v4.777c0 .368.298.667.666.667Z",
      clipRule: "evenodd"
    }
  ) }),
  /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "a", children: /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h8v8H0z" }) }) })
] });
var bottomLeftIcon = /* @__PURE__ */ jsxs17("svg", { xmlns: "http://www.w3.org/2000/svg", width: 8, height: 8, fill: "none", children: [
  /* @__PURE__ */ jsx26("g", { clipPath: "url(#a)", children: /* @__PURE__ */ jsx26(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M.948.75a.667.667 0 0 0-.667.667v4.777c0 .86.697 1.556 1.556 1.556h4.778a.667.667 0 1 0 0-1.333H1.837a.222.222 0 0 1-.222-.223V1.417A.667.667 0 0 0 .948.75Z",
      clipRule: "evenodd"
    }
  ) }),
  /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "a", children: /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h8v8H0z" }) }) })
] });
var bottomRightIcon = /* @__PURE__ */ jsxs17("svg", { xmlns: "http://www.w3.org/2000/svg", width: 8, height: 8, fill: "none", children: [
  /* @__PURE__ */ jsx26("g", { clipPath: "url(#a)", children: /* @__PURE__ */ jsx26(
    "path",
    {
      fill: "#5D6074",
      fillRule: "evenodd",
      d: "M.75 7.083c0 .369.299.667.667.667h4.777c.86 0 1.556-.696 1.556-1.556V1.417a.667.667 0 1 0-1.333 0v4.777c0 .123-.1.223-.223.223H1.417a.667.667 0 0 0-.667.666Z",
      clipRule: "evenodd"
    }
  ) }),
  /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "a", children: /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h8v8H0z" }) }) })
] });

// src/components/inputs/select.tsx
import * as RadixPopover from "@radix-ui/react-popover";
import { styled as styled17 } from "@stitches/react";
import { forwardRef as forwardRef7, useState as useState14 } from "react";
import { Command as Command3 } from "cmdk";
import { jsx as jsx27, jsxs as jsxs18 } from "react/jsx-runtime";
var SelectInput = forwardRef7(({ onChange, placeholder, className, style, disabled, search, ...props }, ref) => {
  return search && !disabled ? /* @__PURE__ */ jsx27(
    Search,
    {
      className,
      style,
      onChange: (value) => onChange(value),
      ...props,
      value: props.value ?? "",
      options: props.options
    }
  ) : /* @__PURE__ */ jsx27(InputContainer, { className, style, "data-disabled": disabled, children: /* @__PURE__ */ jsxs18(
    Select2,
    {
      onChange: (e) => onChange(e.target.value),
      ...props,
      value: props.value ?? "",
      disabled,
      ref,
      children: [
        /* @__PURE__ */ jsx27("option", { hidden: true, disabled: true, value: "", children: placeholder }),
        props.options.map((option, index) => /* @__PURE__ */ jsx27("option", { value: option.value, children: option.label }, index))
      ]
    }
  ) });
});
var Select2 = styled17("select", inputStyle);
function Search({
  value,
  onChange,
  options: options2,
  className,
  style
}) {
  const [open, setOpen] = useState14(false);
  return /* @__PURE__ */ jsxs18(RadixPopover.Root, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx27(RadixPopover.Trigger, { asChild: true, children: /* @__PURE__ */ jsx27(InputContainer, { className, style, children: options2.find((option) => option.value === value)?.label ?? value }) }),
    /* @__PURE__ */ jsx27(RadixPopover.Portal, { children: /* @__PURE__ */ jsx27(
      RadixPopover.Content,
      {
        align: "end",
        "data-encompass-area": true,
        style: {
          backgroundColor: "#fff",
          border: "1px solid #EBECF2",
          borderRadius: "4px",
          marginTop: 4,
          marginBottom: 4,
          boxShadow: "0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          width: 240
        },
        children: /* @__PURE__ */ jsxs18(Command3, { label: "Select", children: [
          /* @__PURE__ */ jsx27("div", { style: { padding: 8 }, children: /* @__PURE__ */ jsx27(InputContainer, { children: /* @__PURE__ */ jsx27(CommandInput2, {}) }) }),
          /* @__PURE__ */ jsxs18(Command3.List, { style: { maxHeight: 300, overflow: "auto" }, children: [
            /* @__PURE__ */ jsx27(Command3.Empty, { children: "No results found." }),
            options2.map((option) => /* @__PURE__ */ jsx27(
              CommandItem2,
              {
                onSelect: () => {
                  onChange(option.value);
                  setOpen(false);
                },
                children: option.label
              },
              option.value
            ))
          ] })
        ] })
      }
    ) })
  ] });
}
var CommandInput2 = styled17(Command3.Input, inputStyle);
var CommandItem2 = styled17(Command3.Item, {
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "pointer",
  fontSize: 13,
  lineHeight: "20px",
  padding: "4px 8px",
  borderRadius: 4,
  "&[data-selected]": {
    backgroundColor: "#F7F8FA"
  }
});

// src/components/inputs/shadow.tsx
import * as Popover3 from "@radix-ui/react-popover";
import { styled as styled19 } from "@stitches/react";
import { parse as parse2, stringify } from "css-box-shadow";

// src/components/inputs/toggle.tsx
import { styled as styled18 } from "@stitches/react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { jsx as jsx28 } from "react/jsx-runtime";
var ToggleInput = ({
  options: options2,
  value,
  onChange,
  ...props
}) => {
  return /* @__PURE__ */ jsx28(Root12, { type: "single", ...props, onValueChange: onChange, value: value ?? "", children: options2.map((option) => /* @__PURE__ */ jsx28(Item7, { value: option.value, children: option.icon ? /* @__PURE__ */ jsx28(
    "span",
    {
      className: "material-symbols-outlined",
      style: {
        fontSize: 16
      },
      children: option.icon
    }
  ) : option.label }, option.value)) });
};
var Root12 = styled18(ToggleGroup.Root, {
  display: "inline-flex",
  backgroundColor: "#FFF",
  border: "1px solid #C7C7D4",
  borderColor: "#DFE0E8",
  // breaking the rules
  borderRadius: 4,
  padding: 2,
  alignItems: "stretch",
  height: "28px",
  gap: 4,
  flexGrow: 1,
  flexShrink: 0
});
var Item7 = styled18(ToggleGroup.Item, {
  all: "unset",
  backgroundColor: "white",
  color: "#4B4B60",
  padding: "4px 6px",
  display: "flex",
  flexGrow: 1,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  cursor: "pointer",
  "&[data-state=on]": {
    backgroundColor: "#0652BB",
    boxShadow: "0px 2px 4px rgb(13 7 27 / 25%)",
    color: "white"
  }
});

// src/components/inputs/shadow.tsx
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
var ShadowInput = ({
  onChange: rawOnChange,
  value
}) => {
  function onChange(shadows) {
    const shadowString = stringify(shadows);
    rawOnChange(shadowString);
  }
  try {
    const shadows = parse2(value || "").filter((shadow) => {
      if (shadow.inset === false && typeof shadow.offsetX === "undefined" && typeof shadow.offsetY === "undefined" && typeof shadow.blurRadius === "undefined" && typeof shadow.spreadRadius === "undefined" && shadow.color === "") {
        return false;
      }
      return true;
    });
    for (const shadow of shadows) {
      if (typeof shadow.offsetX === "string" || typeof shadow.offsetX === "undefined") {
        throw new Error("Offset X must be a number.");
      }
      if (typeof shadow.offsetY === "string" || typeof shadow.offsetY === "undefined") {
        throw new Error("Offset Y must be a number.");
      }
      if (typeof shadow.blurRadius === "string" || typeof shadow.blurRadius === "undefined") {
        throw new Error("Blur radius must be a number.");
      }
      if (typeof shadow.spreadRadius === "string" || typeof shadow.spreadRadius === "undefined") {
        throw new Error("Spread radius must be a number.");
      }
    }
    return /* @__PURE__ */ jsxs19(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end"
        },
        children: [
          shadows.map((shadow, index) => {
            return /* @__PURE__ */ jsx29(Popover3.Root, { children: /* @__PURE__ */ jsx29(Popover3.Anchor, { children: /* @__PURE__ */ jsxs19(
              "div",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                  marginBottom: 8,
                  gap: 8
                },
                children: [
                  /* @__PURE__ */ jsxs19(
                    InputContainer,
                    {
                      style: {
                        flexGrow: 1
                      },
                      children: [
                        /* @__PURE__ */ jsxs19(
                          Popover3.Trigger,
                          {
                            style: {
                              flexGrow: 1,
                              display: "flex",
                              alignItems: "center",
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              font: "inherit",
                              padding: 0
                            },
                            children: [
                              /* @__PURE__ */ jsx29(
                                ColorPreview,
                                {
                                  size: 16,
                                  color: shadow.color,
                                  style: {
                                    borderRadius: "4px",
                                    marginRight: 8
                                  }
                                }
                              ),
                              /* @__PURE__ */ jsxs19("span", { children: [
                                shadow.offsetX,
                                ", ",
                                shadow.offsetY,
                                ", ",
                                shadow.blurRadius,
                                ", ",
                                shadow.spreadRadius
                              ] })
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsx29(
                          "button",
                          {
                            className: "material-symbols-outlined",
                            style: {
                              padding: "6px 1px",
                              border: "none",
                              backgroundColor: "transparent",
                              cursor: "pointer",
                              marginLeft: "auto",
                              fontSize: 14
                            },
                            onClick: () => {
                              onChange(shadows.filter((_, i) => i !== index));
                            },
                            children: "remove"
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs19(PopoverContent2, { side: "left", sideOffset: 20, children: [
                    /* @__PURE__ */ jsx29(
                      "h4",
                      {
                        style: {
                          margin: 0,
                          fontWeight: 600,
                          fontSize: "13px"
                        },
                        children: "Shadow"
                      }
                    ),
                    /* @__PURE__ */ jsx29(
                      "hr",
                      {
                        style: {
                          margin: "10px 0 18px 0",
                          border: "none",
                          height: 1,
                          background: "#E5E5E5"
                        }
                      }
                    ),
                    /* @__PURE__ */ jsxs19(
                      "div",
                      {
                        style: {
                          display: "grid",
                          gridTemplateColumns: "10px 1fr 50px 1fr",
                          gap: "12px 8px",
                          marginBottom: 12,
                          alignItems: "center"
                        },
                        children: [
                          /* @__PURE__ */ jsx29(
                            "label",
                            {
                              style: {
                                display: "inline-block",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: "13px",
                                color: "#6F7488",
                                flexShrink: 0
                              },
                              children: "X"
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            NumberInput,
                            {
                              value: shadow.offsetX,
                              onChange: (value2) => {
                                onChange(
                                  shadows.map((shadow2, i) => {
                                    if (i === index) {
                                      return {
                                        ...shadow2,
                                        offsetX: value2
                                      };
                                    }
                                    return shadow2;
                                  })
                                );
                              }
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            "label",
                            {
                              style: {
                                display: "inline-block",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: "13px",
                                color: "#6F7488",
                                flexShrink: 0
                              },
                              children: "Blur"
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            NumberInput,
                            {
                              value: shadow.blurRadius,
                              onChange: (value2) => {
                                onChange(
                                  shadows.map((shadow2, i) => {
                                    if (i === index) {
                                      return {
                                        ...shadow2,
                                        blurRadius: value2
                                      };
                                    }
                                    return shadow2;
                                  })
                                );
                              }
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            "label",
                            {
                              style: {
                                display: "inline-block",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: "13px",
                                color: "#6F7488",
                                flexShrink: 0
                              },
                              children: "Y"
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            NumberInput,
                            {
                              value: shadow.offsetY,
                              onChange: (value2) => {
                                onChange(
                                  shadows.map((shadow2, i) => {
                                    if (i === index) {
                                      return {
                                        ...shadow2,
                                        offsetY: value2
                                      };
                                    }
                                    return shadow2;
                                  })
                                );
                              }
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            "label",
                            {
                              style: {
                                display: "inline-block",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: "13px",
                                color: "#6F7488",
                                flexShrink: 0
                              },
                              children: "Spread"
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            NumberInput,
                            {
                              value: shadow.spreadRadius,
                              onChange: (value2) => {
                                onChange(
                                  shadows.map((shadow2, i) => {
                                    if (i === index) {
                                      return {
                                        ...shadow2,
                                        spreadRadius: value2
                                      };
                                    }
                                    return shadow2;
                                  })
                                );
                              }
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsx29(
                      ColorInput,
                      {
                        value: shadow.color,
                        onChange: (value2) => {
                          onChange(
                            shadows.map((shadow2, i) => {
                              if (i === index) {
                                return {
                                  ...shadow2,
                                  color: value2
                                };
                              }
                              return shadow2;
                            })
                          );
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx29(
                      "hr",
                      {
                        style: {
                          margin: "14px 0 14px 0",
                          border: "none",
                          height: 1,
                          background: "#E5E5E5"
                        }
                      }
                    ),
                    /* @__PURE__ */ jsxs19(
                      "div",
                      {
                        style: {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0 12px"
                        },
                        children: [
                          /* @__PURE__ */ jsx29(
                            "label",
                            {
                              style: {
                                display: "inline-block",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "13px",
                                color: "#343446",
                                flexShrink: 0
                              },
                              children: "Position"
                            }
                          ),
                          /* @__PURE__ */ jsx29(
                            ToggleInput,
                            {
                              value: shadow.inset ? "inside" : "outside",
                              options: [
                                {
                                  label: "Outside",
                                  value: "outside"
                                },
                                {
                                  label: "Inside",
                                  value: "inside"
                                }
                              ],
                              onChange: (value2) => {
                                onChange(
                                  shadows.map((shadow2, i) => {
                                    if (i === index) {
                                      return {
                                        ...shadow2,
                                        inset: value2 === "inside"
                                      };
                                    }
                                    return shadow2;
                                  })
                                );
                              }
                            }
                          )
                        ]
                      }
                    )
                  ] })
                ]
              }
            ) }) }, index);
          }),
          /* @__PURE__ */ jsx29(
            AddSetButton,
            {
              onClick: () => {
                onChange([
                  ...shadows,
                  {
                    inset: false,
                    offsetX: 0,
                    offsetY: 0,
                    blurRadius: 0,
                    spreadRadius: 0,
                    color: "rgba(0, 0, 0, 0.5)"
                  }
                ]);
              },
              children: "Add"
            }
          )
        ]
      }
    );
  } catch (error) {
    return /* @__PURE__ */ jsx29(
      CodeInput,
      {
        value,
        onChange: (value2) => {
          rawOnChange(value2);
        }
      }
    );
  }
};
var PopoverContent2 = styled19(Popover3.Content, {
  borderRadius: 4,
  padding: 16,
  backgroundColor: "white",
  boxShadow: "hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px",
  zIndex: 1,
  outline: 0,
  width: 256
});

// src/components/inputs/size.tsx
import { styled as styled20 } from "@stitches/react";
import { Fragment as Fragment14, jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
var unitOptions = [
  {
    label: "Auto",
    value: "auto"
  },
  {
    label: "Fixed",
    value: "px"
  },
  {
    label: "Relative",
    value: "percent"
  }
];
function SizeInput({
  value,
  onChange: rawOnChange,
  units = ["auto", "px", "percent"],
  align = "left"
}) {
  function onChange({ number, unit }) {
    if (unit === "auto") {
      rawOnChange("auto");
    }
    if (unit === "px") {
      rawOnChange(`${number}px`);
    }
    if (unit === "percent") {
      rawOnChange(`${number}%`);
    }
  }
  try {
    const { number, unit } = parseSize(value);
    return /* @__PURE__ */ jsx30(
      SizeInputInternal,
      {
        align,
        number,
        unit,
        units,
        onChange: ({ number: number2, unit: unit2 }) => {
          onChange({ number: number2, unit: unit2 });
        }
      }
    );
  } catch {
    /* @__PURE__ */ jsx30(
      CodeInput,
      {
        value,
        onChange: (value2) => {
          rawOnChange(value2);
        }
      }
    );
  }
}
function SizeInputInternal({
  number,
  unit,
  units,
  onChange,
  align
}) {
  const hasInput = !["auto", ""].includes(unit);
  const select = /* @__PURE__ */ jsx30(
    SelectInput,
    {
      value: unit,
      onChange: (unit2) => {
        onChange({
          // default to 100 when switching to px or percent
          number: ["px", "percent"].includes(unit2) && number === 0 ? 100 : number,
          unit: unit2
        });
      },
      options: unitOptions.filter((option) => {
        if (units) {
          return units.includes(option.value);
        }
        return true;
      }),
      style: !hasInput ? { gridColumn: "1 / -1" } : {}
    }
  );
  const input = /* @__PURE__ */ jsx30(Fragment14, { children: hasInput ? /* @__PURE__ */ jsx30(
    NumberInput,
    {
      value: number,
      onChange: (number2) => {
        onChange({ number: number2, unit });
      },
      min: 0,
      trailingIcon: /* @__PURE__ */ jsx30("span", { style: { padding: "0 4px" }, children: unit === "px" ? "px" : unit === "percent" ? "%" : "" })
    }
  ) : null });
  return /* @__PURE__ */ jsxs20(Container3, { children: [
    align === "left" ? select : input,
    align === "left" ? input : select
  ] });
}
var Container3 = styled20("div", {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px"
});
function parseSize(value) {
  if (!value) {
    return {
      number: 0,
      unit: ""
    };
  }
  if (value === "auto") {
    return {
      number: 0,
      unit: "auto"
    };
  }
  if (value.endsWith("px")) {
    return {
      number: parseInt(value.slice(0, -2)),
      unit: "px"
    };
  }
  if (value.endsWith("%")) {
    return {
      number: parseInt(value.slice(0, -1)),
      unit: "percent"
    };
  }
}

// src/components/inputs/slider.tsx
import * as Slider from "@radix-ui/react-slider";
import { styled as styled21 } from "@stitches/react";
import { useState as useState15, useEffect as useEffect14 } from "react";
import { jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
function SliderInput({
  value,
  onChange: rawOnChange,
  step,
  min,
  max
}) {
  step = step ?? 1;
  const precision = step.toString().split(".")[1]?.length ?? 0;
  const [inputValue, setInputValue] = useState15(value);
  useEffect14(() => {
    if (!isNaN(value) || value == null) {
      setInputValue(value);
    }
  }, [value]);
  function onChange(value2) {
    value2 = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, value2));
    rawOnChange(value2);
  }
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        alignItems: "center",
        width: "100%"
      },
      children: [
        /* @__PURE__ */ jsx31(NumberInput, { value: inputValue, onChange, min, max, step }),
        /* @__PURE__ */ jsxs21(
          SliderRoot,
          {
            value: [inputValue ?? min ?? 0],
            min,
            max,
            step,
            "aria-label": "Slider",
            onValueChange: ([inputValue2]) => onChange(parseFloat(inputValue2.toFixed(precision))),
            children: [
              /* @__PURE__ */ jsx31(SliderTrack, { children: /* @__PURE__ */ jsx31(SliderRange, {}) }),
              /* @__PURE__ */ jsx31(SliderThumb, {})
            ]
          }
        )
      ]
    }
  );
}
var SliderRoot = styled21(Slider.Root, {
  position: "relative",
  display: "flex",
  alignItems: "center",
  userSelect: "none",
  touchAction: "none",
  height: 20,
  marginLeft: 4
});
var SliderTrack = styled21(Slider.Track, {
  backgroundColor: "#C7C7D4",
  position: "relative",
  flexGrow: 1,
  borderRadius: "9999px",
  height: 3
});
var SliderRange = styled21(Slider.Range, {
  position: "absolute",
  backgroundColor: "#0652BB",
  borderRadius: "9999px",
  height: "100%"
});
var SliderThumb = styled21(Slider.Thumb, {
  display: "block",
  width: 10,
  height: 10,
  backgroundColor: "white",
  boxShadow: `0 1px 4px rgba(75, 75, 96, .3)`,
  borderRadius: 10,
  "&:hover": { backgroundColor: "#F7F8FA" },
  "&:focus": { outline: "none", boxShadow: `0 0 0 5px rgba(75, 75, 96, .2)` }
});

// src/components/inputs/stepper.tsx
import { styled as styled22 } from "@stitches/react";
import { useState as useState16, useEffect as useEffect15, forwardRef as forwardRef8 } from "react";
import { jsx as jsx32, jsxs as jsxs22 } from "react/jsx-runtime";
var StepperInput = forwardRef8(function StepperInput2({ value, onChange: rawOnChange, step, min, max }, ref) {
  step = step ?? 1;
  const precision = step.toString().split(".")[1]?.length ?? 0;
  const [inputValue, setInputValue] = useState16(value);
  useEffect15(() => {
    if (!isNaN(value) || value == null) {
      setInputValue(value);
    }
  }, [value]);
  function onChange(value2) {
    if (value2 == null) {
      rawOnChange(value2);
      return;
    }
    value2 = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, value2));
    rawOnChange(value2);
  }
  return /* @__PURE__ */ jsxs22(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 8,
        alignItems: "center",
        width: "100%"
      },
      ref,
      children: [
        /* @__PURE__ */ jsx32(NumberInput, { value: inputValue, onChange, min, max, step }),
        /* @__PURE__ */ jsxs22(
          "div",
          {
            style: {
              display: "flex",
              height: "100%",
              width: "100%"
            },
            children: [
              /* @__PURE__ */ jsx32(
                StepperButton,
                {
                  disabled: min != null && inputValue <= min,
                  onClick: () => {
                    if (!inputValue && inputValue !== 0) {
                      onChange(min ?? 0);
                      return;
                    }
                    onChange(parseFloat((inputValue - step).toFixed(precision)));
                  },
                  icon: "remove"
                }
              ),
              /* @__PURE__ */ jsx32(
                StepperButton,
                {
                  disabled: max != null && inputValue >= max,
                  onClick: () => {
                    if (!inputValue && inputValue !== 0) {
                      onChange(max ?? 0);
                      return;
                    }
                    onChange(parseFloat((inputValue + step).toFixed(precision)));
                  },
                  icon: "add"
                }
              )
            ]
          }
        )
      ]
    }
  );
});
var StepperButton = styled22(Icon, {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  fontSize: 14,
  background: "#FFFFFF",
  border: "1px solid #DFE0E8",
  // breaking the rules
  cursor: "pointer",
  "&:first-child": {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4
  },
  "&:last-child": {
    borderLeft: "none",
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4
  }
});

// src/components/inputs/switch.tsx
import { useState as useState17, useEffect as useEffect16 } from "react";
import { jsx as jsx33 } from "react/jsx-runtime";
var options = [
  {
    label: "Yes",
    value: "true"
  },
  {
    label: "No",
    value: "false"
  }
];
var SwitchInput = ({
  ...props
}) => {
  const booleanValue = props.value;
  const [stringValue, setStringValue] = useState17(booleanValue ? "true" : "false");
  useEffect16(() => {
    if (booleanValue == null) {
      setStringValue("");
    } else if (booleanValue) {
      setStringValue("true");
    } else {
      setStringValue("false");
    }
  }, [booleanValue]);
  const onChange = (value) => {
    const booleanValue2 = value === "true";
    props.onChange(booleanValue2);
  };
  return /* @__PURE__ */ jsx33(ToggleInput, { value: stringValue, onChange, options });
};

// src/components/inputs/url.tsx
import { forwardRef as forwardRef9 } from "react";
import { jsx as jsx34, jsxs as jsxs23 } from "react/jsx-runtime";
var UrlInput = forwardRef9(({ value, onChange, leadingIcon, disabled, className, style, ...props }, ref) => {
  return /* @__PURE__ */ jsxs23(InputContainer, { className, style, "data-disabled": disabled, children: [
    leadingIcon ? /* @__PURE__ */ jsx34(IconWrapper, { children: leadingIcon }) : null,
    /* @__PURE__ */ jsx34(
      Input,
      {
        type: "url",
        value: value ?? "",
        onChange: (event) => {
          onChange(event.target.value);
        },
        ...props,
        disabled,
        ref
      }
    )
  ] });
});

// src/visual-editor/properties-menu/shared/properties-menu.tsx
import { Fragment as Fragment15, jsx as jsx35, jsxs as jsxs24 } from "react/jsx-runtime";
function PropertiesMenu({ core }) {
  const { isLoading, state } = useSelection();
  if (isLoading) {
    return /* @__PURE__ */ jsx35("div", { children: "Loading..." });
  }
  return /* @__PURE__ */ jsx35(PropertiesMenuEditor, { selectedState: state, core });
}
function PropertiesMenuEditor({ selectedState, core }) {
  const { visualEditor } = useEncompass();
  if (selectedState.selection === "none") {
    return /* @__PURE__ */ jsx35(CenteredText2, { children: "No Selection" });
  }
  if (selectedState.selection === "multiple") {
    return /* @__PURE__ */ jsx35(CenteredText2, { children: "Multiple Components selected" });
  }
  const fields = selectedState.props?.fields ?? [];
  const isRootComponent = visualEditor.actions.isRootComponent(selectedState.component);
  if (fields.length === 0) {
    return /* @__PURE__ */ jsxs24(Container4, { children: [
      /* @__PURE__ */ jsx35(
        Title,
        {
          label: selectedState.label,
          onDelete: isRootComponent ? null : () => {
            visualEditor.actions.queueSiblingSelection(selectedState.component, "structure");
            visualEditor.commands.deleteComponent(selectedState.component);
          },
          onDuplicate: isRootComponent ? null : () => {
            visualEditor.commands.duplicateComponent(selectedState.component);
            visualEditor.actions.track("component_duplicated", {
              componentName: selectedState.component.name,
              using: "Properties Panel"
            });
          }
        }
      ),
      /* @__PURE__ */ jsx35(CenteredText2, { children: "No Properties" })
    ] });
  }
  const sections = useMemo4(() => fieldsToSections(fields, { core }), [fields, core]);
  const form = /* @__PURE__ */ jsx35(
    Form,
    {
      sections,
      values: selectedState.props.values,
      setProps: selectedState.props.setProps,
      removeProps: selectedState.props.removeProps,
      collapsible: !core
    },
    // this is a hack to force the form to re-render when the component changes
    // otherwise the form will not update when the component changes
    `${selectedState.component.name}-${selectedState.component.index}`
  );
  if (core) {
    return form;
  }
  return /* @__PURE__ */ jsxs24(Container4, { children: [
    /* @__PURE__ */ jsx35(
      Title,
      {
        label: selectedState.label,
        onDelete: isRootComponent ? null : () => {
          visualEditor.actions.queueSiblingSelection(selectedState.component, "structure");
          visualEditor.commands.deleteComponent(selectedState.component);
        },
        onDuplicate: isRootComponent ? null : () => {
          visualEditor.commands.duplicateComponent(selectedState.component);
          visualEditor.actions.track("component_duplicated", {
            componentName: selectedState.component.name,
            using: "Properties Panel"
          });
        }
      }
    ),
    form
  ] });
}
function fieldsToSections(fields, { core } = {}) {
  const sections = [];
  fields.forEach((field) => {
    if (core && !field.core) {
      return;
    }
    const { section: sectionName, set: setName, line: lineName } = field;
    let section = sections.find((section2) => section2.name === sectionName);
    if (!section) {
      section = { type: "section", name: sectionName, children: [] };
      sections.push(section);
    }
    let set2;
    if (setName) {
      set2 = section.children.find((child) => child.type === "set" && child.name === setName);
      if (!set2) {
        set2 = { type: "set", name: setName, children: [] };
        section.children.push(set2);
      }
    }
    let line;
    if (lineName) {
      const parent2 = set2 ?? section;
      line = parent2.children.find((child) => child.type === "line" && child.name === lineName);
      if (!line) {
        line = { type: "line", name: lineName, children: [] };
        section.children.push(line);
      }
    }
    const parent = line ?? set2 ?? section;
    parent.children.push(field);
  });
  return sections;
}
function Title({
  label,
  onDuplicate,
  onDelete
}) {
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      style: {
        display: "flex",
        justifyContent: "space-between",
        padding: "16px 16px 6px 16px"
      },
      children: [
        /* @__PURE__ */ jsx35(
          "h2",
          {
            style: {
              fontSize: "14px",
              fontWeight: 600,
              color: "#4B4B60",
              margin: 0
            },
            children: label
          }
        ),
        /* @__PURE__ */ jsxs24(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              gap: "8px"
            },
            children: [
              /* @__PURE__ */ jsx35(
                "button",
                {
                  style: {
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    margin: 0,
                    fontSize: "16px"
                  },
                  onClick: () => {
                    onDuplicate();
                  },
                  disabled: onDelete == null,
                  className: "material-symbols-outlined",
                  children: "control_point_duplicate"
                }
              ),
              /* @__PURE__ */ jsx35(
                "button",
                {
                  style: {
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    margin: 0,
                    fontSize: "16px"
                  },
                  onClick: () => {
                    onDelete();
                  },
                  disabled: onDelete == null,
                  className: "material-symbols-outlined",
                  children: "delete"
                }
              )
            ]
          }
        )
      ]
    }
  );
}
function Form({
  sections,
  values,
  setProps,
  removeProps,
  collapsible
}) {
  const formRef = React5.useRef(null);
  const { visualEditor } = useEncompass();
  useEffect17(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && formRef.current?.contains(document.activeElement)) {
        visualEditor.actions.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  return /* @__PURE__ */ jsx35(
    "div",
    {
      ref: formRef,
      style: collapsible ? {
        height: 0,
        flexGrow: 1,
        padding: "0 16px",
        overflowY: "auto"
      } : {
        padding: "0 4px"
      },
      children: sections.map((section) => {
        return /* @__PURE__ */ jsx35(
          FormSection,
          {
            section,
            values,
            setProps,
            removeProps,
            collapsible
          },
          section.name
        );
      })
    }
  );
}
var FormSection = memo(function({
  section,
  values,
  setProps,
  removeProps,
  collapsible
}) {
  const anyFieldHasAValue = section.children.some((field) => {
    if (field.type === "set") {
      return field.children.some((field2) => {
        if (field2.type === "line") {
          return field2.children.some((field3) => {
            return values[field3.name] != null;
          });
        }
        return values[field2.name] != null;
      });
    }
    if (field.type === "line") {
      return field.children.some((field2) => {
        return values[field2.name] != null;
      });
    }
    return values[field.name] != null;
  });
  const [open, setOpen] = useState18(anyFieldHasAValue);
  return /* @__PURE__ */ jsxs24("div", { children: [
    collapsible ? /* @__PURE__ */ jsxs24(Fragment15, { children: [
      /* @__PURE__ */ jsx35(
        "hr",
        {
          style: {
            margin: "6px 0",
            border: "none",
            height: 1,
            background: "#E5E5E5"
          }
        }
      ),
      section.name !== "default" ? /* @__PURE__ */ jsxs24(
        "button",
        {
          style: {
            color: "inherit",
            background: "transparent",
            border: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            cursor: "pointer",
            padding: "12px 0"
          },
          onClick: () => {
            setOpen(!open);
          },
          children: [
            /* @__PURE__ */ jsx35(
              "h3",
              {
                style: {
                  margin: 0,
                  fontWeight: 600,
                  fontSize: "13px"
                },
                children: section.name
              }
            ),
            /* @__PURE__ */ jsx35(Icon, { icon: open ? "expand_less" : "expand_more", size: 16 })
          ]
        }
      ) : null
    ] }) : null,
    open || !collapsible || section.name === "default" ? /* @__PURE__ */ jsx35(
      "div",
      {
        style: {
          margin: "16px 0"
        },
        children: section.children.map((child) => {
          switch (child.type) {
            case "set":
              return /* @__PURE__ */ jsx35(FormSet, { set: child, values, setProps, removeProps }, child.name);
            case "line":
              return /* @__PURE__ */ jsx35(
                FormLine,
                {
                  line: child,
                  values,
                  setProps,
                  removeProps
                },
                child.name
              );
            default:
              return /* @__PURE__ */ jsx35(
                FormField,
                {
                  field: child,
                  values,
                  setProps,
                  removeProps
                },
                child.name
              );
          }
        })
      }
    ) : null
  ] });
});
function FormSet({
  set: set2,
  values,
  setProps,
  removeProps
}) {
  const hasValue = set2.children.some((field) => {
    if (field.type === "line") {
      return field.children.find((field2) => {
        return values[field2.name] != null;
      });
    }
    return values[field.name] != null;
  });
  return /* @__PURE__ */ jsx35(Popover4.Root, { children: /* @__PURE__ */ jsxs24(
    "div",
    {
      style: {
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "12px",
        display: "grid",
        gridTemplateColumns: "minmax(90px,1fr) 2fr",
        gridGap: "8px"
      },
      children: [
        /* @__PURE__ */ jsx35(Label3, { children: set2.name }),
        /* @__PURE__ */ jsx35(
          Popover4.Anchor,
          {
            style: {
              display: "flex",
              justifyContent: "flex-end"
            },
            children: /* @__PURE__ */ jsx35(Popover4.Trigger, { asChild: true, children: hasValue ? /* @__PURE__ */ jsxs24("div", { style: { display: "flex", width: "100%" }, children: [
              /* @__PURE__ */ jsx35(
                "div",
                {
                  style: {
                    background: "#FFFFFF",
                    borderRadius: 4,
                    height: 28,
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#4F5665",
                    border: "1px solid #c7c7d4",
                    borderColor: "#DFE0E8",
                    // breaking the rules
                    cursor: "pointer",
                    flexGrow: 1,
                    textAlign: "left",
                    display: "flex",
                    padding: "0 4px 0 0",
                    alignItems: "center"
                  },
                  children: /* @__PURE__ */ jsxs24(
                    "button",
                    {
                      style: {
                        font: "inherit",
                        border: "none",
                        backgroundColor: "transparent",
                        margin: 0,
                        width: "100%",
                        height: "100%",
                        textAlign: "left",
                        padding: "0 4px",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer"
                      },
                      children: [
                        /* @__PURE__ */ jsx35(
                          Icon,
                          {
                            icon: "expand_content",
                            size: 14,
                            style: {
                              marginRight: 8,
                              color: "#0652BB",
                              background: "#F1EAFF",
                              borderRadius: 4,
                              padding: "1px 1px"
                            }
                          }
                        ),
                        "Edit"
                      ]
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx35(
                "button",
                {
                  className: "material-symbols-outlined",
                  style: {
                    padding: "6px 1px",
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    marginLeft: "8px",
                    flexShrink: 0,
                    fontSize: 15
                  },
                  onClick: () => {
                    removeProps(
                      set2.children.flatMap((field) => {
                        if (field.type === "line") {
                          return field.children.map((field2) => field2.name);
                        }
                        return [field.name];
                      })
                    );
                  },
                  children: "close"
                }
              )
            ] }) : /* @__PURE__ */ jsx35(AddSetButton, { children: "Add" }) })
          }
        ),
        /* @__PURE__ */ jsxs24(PopoverContent3, { side: "left", sideOffset: 20, children: [
          /* @__PURE__ */ jsx35(
            "h4",
            {
              style: {
                margin: 0,
                fontWeight: 600,
                fontSize: "13px"
              },
              children: set2.name
            }
          ),
          /* @__PURE__ */ jsx35(
            "hr",
            {
              style: {
                margin: "10px 0 18px 0",
                border: "none",
                height: 1,
                background: "#E5E5E5"
              }
            }
          ),
          set2.children.map((child) => {
            if (child.type === "line") {
              return /* @__PURE__ */ jsx35(FormLine, { line: child, values, setProps, removeProps }, child.name);
            }
            return /* @__PURE__ */ jsx35(FormField, { field: child, values, setProps, removeProps }, child.name);
          })
        ] })
      ]
    }
  ) });
}
function FormLine({
  line,
  values,
  setProps,
  removeProps
}) {
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      style: {
        marginBottom: "16px"
      },
      children: [
        /* @__PURE__ */ jsx35(Label3, { children: line.name }),
        /* @__PURE__ */ jsx35(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 8
            },
            children: line.children.map((field) => {
              return /* @__PURE__ */ jsx35(
                FormField,
                {
                  field,
                  values,
                  setProps,
                  removeProps,
                  inline: true,
                  placeholder: field.label
                },
                field.name
              );
            })
          }
        )
      ]
    }
  );
}
function FormField({
  field,
  values,
  setProps,
  removeProps,
  inline,
  helpText: helpTextOverride,
  placeholder
}) {
  const propValue = values[field.name];
  const [errorMessages, setErrorMessages] = useState18({});
  let label = /* @__PURE__ */ jsx35(
    Label3,
    {
      hidden: inline,
      style: {
        marginTop: 6
      },
      children: field.label
    }
  );
  const errorMessage = field.name in errorMessages ? /* @__PURE__ */ jsx35(ErrorMessage3, { children: errorMessages[field.name] }) : null;
  const inputWrapperStyle = {
    ...inline ? {} : {
      marginBottom: "12px",
      display: "grid",
      gridTemplateColumns: "minmax(90px,1fr) 2fr",
      gridGap: "8px"
    }
  };
  const helpText = field.helpText || helpTextOverride ? /* @__PURE__ */ jsx35(
    "div",
    {
      style: {
        color: "#6F7488",
        fontSize: "12px",
        marginTop: "0px",
        marginBottom: "2px",
        gridColumn: "1 / -1"
      },
      children: field.helpText || helpTextOverride
    }
  ) : null;
  let wrappedInput;
  if (propValue?.isPrimitive === false) {
    return /* @__PURE__ */ jsxs24("div", { style: inputWrapperStyle, children: [
      label,
      /* @__PURE__ */ jsx35(
        ExpressionInput,
        {
          value: propValue.value,
          onChange: (value) => {
            try {
              setProps({
                [field.name]: {
                  value,
                  isExpression: true,
                  isPrimitive: false,
                  isComputed: false
                }
              });
              setErrorMessages((messages) => {
                const newMessages = { ...messages };
                delete newMessages[field.name];
                return newMessages;
              });
            } catch (error) {
              setErrorMessages({
                [field.name]: error.issues?.[0]?.message ?? error.message
              });
            }
          }
        }
      ),
      helpText,
      errorMessage
    ] });
  }
  const input = /* @__PURE__ */ jsx35(
    PropertyInput,
    {
      value: propValue,
      field,
      onChange: (value) => {
        setProps({
          [field.name]: value
        });
      },
      onError: (message) => {
        if (message) {
          setErrorMessages({
            [field.name]: message
          });
        } else {
          setErrorMessages((messages) => {
            const newMessages = { ...messages };
            delete newMessages[field.name];
            return newMessages;
          });
        }
      },
      placeholder
    }
  );
  switch (field.type) {
    case "hidden": {
      return null;
    }
    case "box":
    case "code":
    case "color":
    case "media":
    case "number":
    case "radius":
    case "select":
    case "size":
    case "slider":
    case "text":
    case "url": {
      wrappedInput = /* @__PURE__ */ jsx35(
        WithClearButton,
        {
          showClear: !field.required && propValue?.isComputed === false,
          onClear: () => {
            removeProps([field.name]);
          },
          children: input
        }
      );
      break;
    }
    case "shadow": {
      wrappedInput = /* @__PURE__ */ jsx35(
        WithClearButton,
        {
          showClear: !field.required && propValue?.isComputed === false,
          onClear: () => {
            removeProps([field.name]);
          },
          children: /* @__PURE__ */ jsx35(
            PropertyInput,
            {
              value: propValue,
              field,
              onChange: (value) => {
                if (value) {
                  setProps({
                    [field.name]: value
                  });
                } else {
                  removeProps([field.name]);
                }
              },
              onError: (message) => {
                if (message) {
                  setErrorMessages({
                    [field.name]: message
                  });
                } else {
                  setErrorMessages((messages) => {
                    const newMessages = { ...messages };
                    delete newMessages[field.name];
                    return newMessages;
                  });
                }
              },
              placeholder
            }
          )
        }
      );
      break;
    }
    case "switch":
    case "toggle": {
      wrappedInput = /* @__PURE__ */ jsx35(
        WithClearButton,
        {
          showClear: !field.required && propValue?.isComputed === false,
          onClear: () => {
            removeProps([field.name]);
          },
          children: /* @__PURE__ */ jsx35(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch"
              },
              children: input
            }
          )
        }
      );
      break;
    }
    case "unknown":
    default: {
      wrappedInput = /* @__PURE__ */ jsx35(WithClearButton, { showClear: false, children: input });
      break;
    }
  }
  return /* @__PURE__ */ jsxs24("div", { style: inputWrapperStyle, children: [
    label,
    wrappedInput,
    helpText,
    errorMessage
  ] });
}
function PropertyInput({
  value,
  field,
  onChange,
  onError,
  placeholder,
  align = "right"
}) {
  placeholder = placeholder ?? ("placeholder" in field ? field.placeholder : "");
  function onValueChange(rawValue) {
    try {
      onChange({
        value: `${rawValue}`,
        isExpression: value?.isExpression ?? (["number", "switch", "slider"].includes(field.type) ? true : false),
        isPrimitive: true,
        isComputed: false
      });
      onError(null);
    } catch (error) {
      onError(error.issues?.[0]?.message ?? error.message);
    }
  }
  switch (field.type) {
    case "box": {
      return /* @__PURE__ */ jsx35(BoxInput, { value: value?.value, onChange: onValueChange });
    }
    case "code": {
      return /* @__PURE__ */ jsx35(CodeInput, { placeholder, value: value?.value, onChange: onValueChange });
    }
    case "color": {
      return /* @__PURE__ */ jsx35(ColorInput, { value: value?.value, onChange: onValueChange });
    }
    case "hidden": {
      return null;
    }
    case "media": {
      return /* @__PURE__ */ jsxs24(Fragment15, { children: [
        /* @__PURE__ */ jsx35(TextInput, { placeholder, value: value?.value, onChange: onValueChange }, "url"),
        /* @__PURE__ */ jsx35(
          SelectMediaButtonFromEditor,
          {
            label: "Choose Media",
            field,
            onSelected: onValueChange,
            style: {
              marginTop: 4
            }
          }
        )
      ] });
    }
    case "number": {
      const number = Number(value?.value);
      if (isNaN4(number) && value?.value != null) {
        return /* @__PURE__ */ jsx35(TextInput, { placeholder, value: value?.value, onChange: onValueChange });
      } else {
        return /* @__PURE__ */ jsx35(
          StepperInput,
          {
            value: isNaN4(number) ? void 0 : number,
            onChange: onValueChange,
            min: field.min,
            max: field.max,
            step: field.step
          }
        );
      }
    }
    case "radius": {
      return /* @__PURE__ */ jsx35(RadiusInput, { value: value?.value, onChange: onValueChange });
    }
    case "select": {
      return /* @__PURE__ */ jsx35(
        SelectInput,
        {
          value: value?.value,
          onChange: onValueChange,
          options: field.options,
          style: {
            width: "100%"
          },
          search: field.search
        }
      );
    }
    case "shadow": {
      return /* @__PURE__ */ jsx35(
        ShadowInput,
        {
          value: value?.value,
          onChange: (value2) => {
            onValueChange(value2);
          }
        }
      );
    }
    case "size": {
      return /* @__PURE__ */ jsx35(SizeInput, { align, value: value?.value, onChange: onValueChange, units: field.units });
    }
    case "slider": {
      const number = Number(value?.value);
      if (isNaN4(number) && value?.value != null) {
        return /* @__PURE__ */ jsx35(TextInput, { placeholder, value: value?.value, onChange: onValueChange });
      } else {
        return /* @__PURE__ */ jsx35(
          SliderInput,
          {
            value: isNaN4(number) ? void 0 : number,
            onChange: onValueChange,
            min: field.min,
            max: field.max,
            step: field.step
          }
        );
      }
    }
    case "switch": {
      return /* @__PURE__ */ jsx35(
        SwitchInput,
        {
          value: value?.value === "true" ? true : value?.value === "false" ? false : void 0,
          onChange: onValueChange
        }
      );
    }
    case "text": {
      return /* @__PURE__ */ jsx35(TextInput, { placeholder, value: value?.value, onChange: onValueChange });
    }
    case "toggle": {
      return /* @__PURE__ */ jsx35(ToggleInput, { value: value?.value, onChange: onValueChange, options: field.options });
    }
    case "url": {
      return /* @__PURE__ */ jsx35(UrlInput, { placeholder, value: value?.value, onChange: onValueChange });
    }
    case "unknown":
    default: {
      return /* @__PURE__ */ jsx35(TextInput, { readOnly: true, value: value?.value, onChange: onValueChange });
    }
  }
}
function Label3({
  children,
  hidden,
  style
}) {
  return /* @__PURE__ */ jsx35(
    "label",
    {
      style: {
        display: "inline-block",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: "13px",
        color: "#6F7488",
        flexShrink: 0,
        lineHeight: 1.5,
        marginBottom: 0,
        textTransform: "none",
        ...hidden ? { position: "absolute", height: 1, width: 1, overflow: "hidden", clip: "rect(1px, 1px, 1px, 1px)" } : {},
        ...style
      },
      children
    }
  );
}
function SelectMediaButtonFromEditor({
  field,
  ...props
}) {
  const { visualEditor } = useEncompass();
  if (!visualEditor.state.supportsSelectMedia || field.type !== "media") {
    return null;
  }
  return /* @__PURE__ */ jsx35(SelectMediaButton, { onSelect: () => visualEditor.actions.onSelectMedia(field?.accept), ...props });
}
var ErrorMessage3 = styled23("div", {
  color: "#DB234B",
  fontSize: "12px",
  lineHeight: "16px",
  marginTop: "4px",
  gridColumn: "1 / -1"
});
var Container4 = styled23("div", {
  display: "flex",
  flexDirection: "column",
  height: "100%"
});
var CenteredText2 = styled23("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  color: "#6F7488",
  padding: "16px 16px"
});
var PopoverContent3 = styled23(Popover4.Content, {
  borderRadius: 4,
  padding: 16,
  width: 300,
  backgroundColor: "white",
  boxShadow: "hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px",
  zIndex: 1,
  outline: 0
});
function WithClearButton({
  children,
  onClear,
  showClear
}) {
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-end"
      },
      children: [
        /* @__PURE__ */ jsx35(
          "div",
          {
            style: {
              flexGrow: 1
            },
            children
          }
        ),
        showClear ? /* @__PURE__ */ jsx35(
          "button",
          {
            onClick: onClear,
            style: {
              width: 20,
              fontSize: 13,
              textAlign: "center",
              flexShrink: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              outline: "none",
              padding: "4px 0",
              marginRight: -2,
              marginLeft: 4,
              marginTop: 4
            },
            className: "material-symbols-outlined",
            children: "replay"
          }
        ) : /* @__PURE__ */ jsx35("div", { style: { width: 22, flexShrink: 0 } })
      ]
    }
  );
}

// src/visual-editor/properties-menu/core/index.tsx
import { Fragment as Fragment16, jsx as jsx36, jsxs as jsxs25 } from "react/jsx-runtime";
function useReclaculatePosition() {
  const { visualEditor } = useEncompass();
  const [delta, setDelta] = useState19({
    x: 0,
    y: 0
  });
  function recalculate(newDelta) {
    setDelta({
      x: delta.x + newDelta.x,
      y: delta.y + newDelta.y
    });
  }
  useEffect18(() => {
    if (!visualEditor.state.corePropertiesMenu.isVisible) {
      setDelta({
        x: 0,
        y: 0
      });
    }
  }, [visualEditor.state.corePropertiesMenu.isVisible]);
  return [delta, recalculate];
}
function CorePropertiesMenu() {
  const { visualEditor } = useEncompass();
  const [delta, recalculatePosition] = useReclaculatePosition();
  const activationConstraint = {
    distance: 15
    // drag distance in pixels before activation
  };
  const mouseSensor = useSensor3(MouseSensor3, {
    activationConstraint
  });
  const keyboardSensor = useSensor3(KeyboardSensor3, {});
  const sensors = useSensors3(mouseSensor, keyboardSensor);
  const component = visualEditor.state.editMode === "structure" ? visualEditor.state.selectedComponents?.[0] : visualEditor.state.contentTarget ? {
    type: "component",
    name: visualEditor.state.contentTarget.name,
    index: visualEditor.state.contentTarget.index
  } : null;
  const enabledRef = useRef9(visualEditor.state.corePropertiesMenu.isVisible);
  useEffect18(() => {
    if (visualEditor.state.corePropertiesMenu.isVisible && enabledRef.current === false) {
      enabledRef.current = true;
      return;
    }
    if (!visualEditor.state.corePropertiesMenu.isVisible) {
      enabledRef.current = false;
      return;
    }
    visualEditor.actions.hideCorePropertiesMenu();
  }, [visualEditor.state.corePropertiesMenu.isVisible, JSON.stringify(component)]);
  const open = !!(visualEditor.state.corePropertiesMenu.isVisible && visualEditor.state.selectedComponents.length === 1 && visualEditor.state.editMode === "structure");
  return /* @__PURE__ */ jsx36(
    DndContext3,
    {
      sensors,
      onDragEnd: ({ delta: delta2 }) => {
        recalculatePosition(delta2);
      },
      children: /* @__PURE__ */ jsxs25(
        RadixPopover2.Root,
        {
          open,
          onOpenChange: (open2) => {
            if (!open2) {
              visualEditor.actions.hideCorePropertiesMenu();
            }
          },
          children: [
            /* @__PURE__ */ jsx36(TargetedAchor, { open }),
            /* @__PURE__ */ jsx36(RadixPopover2.Portal, { children: open ? /* @__PURE__ */ jsx36(
              RadixPopover2.Content,
              {
                align: "start",
                style: {
                  zIndex: 1e3
                },
                "data-encompass-area": true,
                children: /* @__PURE__ */ jsx36(CorePropertiesMenuInternal, { delta, component })
              }
            ) : null })
          ]
        }
      )
    }
  );
}
function TargetedAchor({ open }) {
  const { visualEditor } = useEncompass();
  const { target } = visualEditor.state.corePropertiesMenu;
  const style = open ? {
    position: "absolute",
    top: target.top,
    left: target.left,
    height: target.height,
    width: target.width,
    zIndex: 1e3
  } : {
    position: "absolute",
    top: 0,
    left: 0,
    height: 10,
    width: 10,
    zIndex: 1e3
  };
  return /* @__PURE__ */ jsx36(
    Portal5,
    {
      style: {
        position: "absolute",
        top: 0,
        left: 0
      },
      children: /* @__PURE__ */ jsx36(RadixPopover2.Anchor, { style })
    }
  );
}
function CorePropertiesMenuInternal({
  delta,
  component
}) {
  const { visualEditor } = useEncompass();
  const definition = visualEditor.actions.getComponentDefinition(component.name);
  const { attributes, listeners, setNodeRef, transform } = useDraggable4({
    id: "core-properties-menu"
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`
  } : void 0;
  return /* @__PURE__ */ jsxs25(
    Panel,
    {
      setNodeRef,
      style: {
        top: delta.y,
        left: delta.x,
        position: "absolute",
        ...style
      },
      children: [
        /* @__PURE__ */ jsx36(PanelTitle, { attributes, listeners, children: definition.label }),
        /* @__PURE__ */ jsx36(
          "div",
          {
            style: {
              margin: 0,
              padding: "4px 0 4px 20px",
              display: "flex",
              flexDirection: "column",
              minHeight: "50px",
              maxHeight: "350px",
              overflowY: "auto"
            },
            children: /* @__PURE__ */ jsx36(PropertiesMenu, { core: true })
          }
        )
      ]
    }
  );
}
var Panel = styled24(
  function({
    className,
    children,
    setNodeRef,
    style
  }) {
    return /* @__PURE__ */ jsx36("div", { ref: setNodeRef, style, className, children });
  },
  {
    width: "300px",
    backgroundColor: "#fff",
    border: "1px solid #EBECF2",
    borderRadius: "4px",
    marginTop: 4,
    marginBottom: 4,
    boxShadow: "0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
    outline: "none",
    display: "flex",
    flexDirection: "column"
  }
);
function PanelTitle({
  children,
  attributes,
  listeners
}) {
  const { visualEditor } = useEncompass();
  return /* @__PURE__ */ jsxs25(Fragment16, { children: [
    /* @__PURE__ */ jsxs25(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: 0,
          padding: "20px 24px 16px 24px",
          cursor: "grab"
        },
        ...attributes,
        ...listeners,
        children: [
          /* @__PURE__ */ jsx36(
            "h3",
            {
              style: {
                fontSize: "14px",
                fontWeight: 600,
                color: "#4B4B60",
                padding: "0 16px 0 0",
                margin: 0
              },
              children
            }
          ),
          /* @__PURE__ */ jsx36(
            "button",
            {
              style: {
                background: "none",
                border: "none",
                cursor: "pointer",
                margin: 0,
                fontSize: "16px",
                padding: 0
              },
              className: "material-symbols-outlined",
              onClick: () => {
                visualEditor.actions.hideCorePropertiesMenu();
              },
              children: "close"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx36("div", { style: { padding: "0 24px" }, children: /* @__PURE__ */ jsx36("hr", { style: { padding: 0, margin: 0, border: "none", height: 1, background: "#D9D9D9" } }) })
  ] });
}

// src/visual-editor/properties-menu/full/index.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
function FullPropertiesMenu() {
  return /* @__PURE__ */ jsx37("div", { style: { overflowY: "scroll", background: "#FFF", height: "100%" }, children: /* @__PURE__ */ jsx37(PropertiesMenu, { core: false }) });
}

// src/visual-editor/index.tsx
import { styled as styled26 } from "@stitches/react";

// src/visual-editor/loading/index.tsx
import { styled as styled25 } from "@stitches/react";
import { Fragment as Fragment17, jsx as jsx38, jsxs as jsxs26 } from "react/jsx-runtime";
function Loading() {
  const { visualEditor } = useEncompass();
  const { isLoading } = visualEditor.state;
  return /* @__PURE__ */ jsxs26(Fragment17, { children: [
    /* @__PURE__ */ jsx38(Cover2, { "data-loading": isLoading }),
    /* @__PURE__ */ jsx38(Spinner, { "data-loading": isLoading, children: "Loading..." })
  ] });
}
var Cover2 = styled25("div", {
  transition: "opacity 0.1s linear 0.25s",
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "#FFF",
  opacity: 0,
  pointerEvents: "none",
  '&[data-loading="true"]': {
    opacity: 0.8,
    pointerEvents: "all"
  }
});
var Spinner = styled25("div", {
  transition: "opacity 0.1s linear 0.25s",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  opacity: 0,
  pointerEvents: "none",
  '&[data-loading="true"]': {
    opacity: 1,
    pointerEvents: "all"
  }
});

// src/visual-editor/index.tsx
import { jsx as jsx39, jsxs as jsxs27 } from "react/jsx-runtime";
function Root17({ children }) {
  const { visualEditor } = useEncompass();
  useEffect19(() => {
    if (visualEditor.__preview) {
      visualEditor.actions.mount(visualEditor.__preview);
      return () => {
        visualEditor.actions.unmount();
      };
    }
  }, [visualEditor.__preview]);
  return /* @__PURE__ */ jsx39(Context3, { children });
}
function View({ children }) {
  const { visualEditor } = useEncompass();
  return /* @__PURE__ */ jsxs27(
    ViewContainer,
    {
      id: "encompass-preview",
      onMouseOver: (event) => {
        event.stopPropagation();
      },
      children: [
        /* @__PURE__ */ jsxs27(
          MarshaledPreview,
          {
            instance: visualEditor.__preview,
            style: { height: 0, flexGrow: 1 },
            children: [
              /* @__PURE__ */ jsx39(Overlays, {}),
              children
            ]
          }
        ),
        visualEditor.state.error ? /* @__PURE__ */ jsx39(ErrorDisplay, { error: visualEditor.state.error }) : null,
        /* @__PURE__ */ jsx39(Loading, {})
      ]
    }
  );
}
var ViewContainer = styled26("div", {
  height: 0,
  flexGrow: 1,
  flexDirection: "column",
  display: "flex",
  position: "relative"
});

// src/context/index.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
function EncompassProvider({ children, ...props }) {
  return /* @__PURE__ */ jsx40(MainProvider, { ...props, children: /* @__PURE__ */ jsx40(SelectionProvider, { children: /* @__PURE__ */ jsx40(Root17, { children }) }) });
}

// src/code-editor/index.tsx
import Editor, { loader } from "@monaco-editor/react";
import "monaco-editor/esm/vs/basic-languages/html/html.contribution";
import "monaco-editor/esm/vs/basic-languages/css/css.contribution";
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { useEffect as useEffect20, useRef as useRef10, useState as useState20 } from "react";
import { useDebounceCallback } from "@react-hook/debounce";
import { diff_match_patch } from "diff-match-patch";
import { compact as compact2, noop } from "lodash";
import { jsx as jsx41 } from "react/jsx-runtime";
loader.config({ monaco });
var dmp = new diff_match_patch();
function EncompassCodeEditor({
  onChange,
  onInput,
  onMount,
  inputDebounce = 1e3,
  ...props
}) {
  const isMounted = useRef10(false);
  const [value, setValue] = useState20(null);
  const { codeEditor: editor } = useEncompass();
  const monacoEditor = useRef10(null);
  const syncLock = useRef10(false);
  const onDebouncedInput = useDebounceCallback(onInput ?? noop, inputDebounce);
  useEffect20(() => {
    if (!isMounted.current) {
      return;
    }
    onChange?.(value);
    if (syncLock.current) {
      syncLock.current = false;
      return;
    }
    editor.actions.updateValue(value);
    onDebouncedInput(value);
  }, [value]);
  useEffect20(() => {
    if (value !== editor.state.value) {
      if (!isMounted.current) {
        syncLock.current = true;
        setValue(editor.state.value);
        isMounted.current = true;
        return;
      }
      const model = monacoEditor.current?.getModel();
      if (!model) {
        syncLock.current = true;
        setValue(editor.state.value);
        return;
      }
      const diffs = dmp.diff_main(value, editor.state.value);
      dmp.diff_cleanupEfficiency(diffs);
      let offset = 0;
      let additionOffset = 0;
      const edits = compact2(
        diffs.map((d) => {
          if (d[0] === 0) {
            offset += d[1].length;
            return null;
          }
          const startPosition = model.getPositionAt(offset - additionOffset);
          const endPosition = model.getPositionAt(offset + d[1].length - additionOffset);
          offset += d[1].length;
          if (d[0] === -1) {
            return {
              range: new monaco.Range(
                startPosition.lineNumber,
                startPosition.column,
                endPosition.lineNumber,
                endPosition.column
              ),
              text: ""
            };
          }
          if (d[0] === 1) {
            additionOffset += d[1].length;
            return {
              range: new monaco.Range(
                startPosition.lineNumber,
                startPosition.column,
                startPosition.lineNumber,
                startPosition.column
              ),
              text: d[1]
            };
          }
        })
      );
      syncLock.current = true;
      model.pushEditOperations([], edits, void 0);
    }
  }, [editor.state.value]);
  if (value === null) {
    return null;
  }
  return /* @__PURE__ */ jsx41(
    Editor,
    {
      language: "html",
      value,
      onChange: (value2) => {
        setValue(value2 ?? "");
      },
      options: {
        minimap: {
          enabled: false
        },
        wordWrap: "on",
        readOnly: editor.state.readOnly
      },
      onMount: (editor2, monaco2) => {
        onMount?.(editor2, monaco2);
        monaco2.editor.defineTheme("cio", {
          base: "vs-dark",
          inherit: true,
          colors: {
            "editor.background": "#2A2A38"
          },
          rules: [
            {
              token: "comment",
              foreground: "9698A7"
            },
            {
              token: "tag",
              foreground: "F5D44E"
            },
            {
              token: "attribute.name",
              foreground: "FF95CA"
            },
            {
              token: "attribute.value",
              foreground: "10CCA6"
            },
            {
              token: "metatag.html",
              foreground: "FFFFFF"
            },
            {
              token: "metatag.content.html",
              foreground: "FFFFFF"
            },
            {
              token: "tag.css",
              foreground: "FF9E5E"
            },
            {
              token: "keyword.css",
              foreground: "FF9E5E"
            },
            {
              token: "attribute.name.css",
              foreground: "10CCA6"
            },
            {
              token: "attribute.value.css",
              foreground: "FF9E5E"
            },
            {
              token: "attribute.value.number.css",
              foreground: "00C9E3"
            },
            {
              token: "attribute.value.unit.css",
              foreground: "00C9E3"
            },
            {
              token: "metatag",
              foreground: "FFFFFF"
            }
          ]
        });
        editor2.updateOptions({
          theme: "cio"
        });
        monacoEditor.current = editor2;
      },
      ...props
    }
  );
}

// src/layout.tsx
var layout_exports = {};
__export(layout_exports, {
  Panel: () => Panel3,
  PanelGroup: () => PanelGroup2,
  ResizeHandle: () => ResizeHandle,
  Root: () => Root18
});
import { useLayoutEffect as useLayoutEffect3, useState as useState21, useId as useId4 } from "react";
import * as Resizable2 from "react-resizable-panels";
import { styled as styled27 } from "@stitches/react";
import { jsx as jsx42 } from "react/jsx-runtime";
function getBoundedPercentage(percentage) {
  if (percentage > 100) {
    return 100;
  }
  return percentage;
}
var Root18 = styled27(
  function Root19(props) {
    return /* @__PURE__ */ jsx42("div", { "data-encompass-area": true, ...props });
  },
  {
    display: "flex",
    height: "100%",
    overflow: "hidden"
  }
);
function PanelGroup2({
  children,
  style,
  direction = "vertical"
}) {
  return /* @__PURE__ */ jsx42(Resizable2.PanelGroup, { direction, style, children });
}
function Panel3({
  id: givenId,
  minSize: minSizePercent,
  maxSize: maxSizePercent,
  defaultSize: defaultSizePercent,
  minSizePx,
  maxSizePx,
  defaultSizePx,
  ...rest
}) {
  const generatedId = `panel-${useId4()}`;
  const id = givenId ?? generatedId;
  if (minSizePx && minSizePercent) {
    throw new Error("Cannot specify both `minSizePx` and `minSize`");
  }
  if (maxSizePx && maxSizePercent) {
    throw new Error("Cannot specify both `maxSizePx` and `maxSize`");
  }
  if (defaultSizePx && defaultSizePercent) {
    throw new Error("Cannot specify both `defaultSizePx` and `defaultSize`");
  }
  const [panelGroupSize, setPanelGroupSize] = useState21(void 0);
  useLayoutEffect3(() => {
    const panel = document.querySelector(`[data-panel-id="${id}"]`);
    if (!panel) {
      return;
    }
    const panelGroup = panel.closest("[data-panel-group-id]");
    if (!panelGroup) {
      return;
    }
    const direction = panelGroup.getAttribute("data-panel-group-direction");
    setPanelGroupSize(direction === "horizontal" ? panelGroup.offsetWidth : panelGroup.offsetHeight);
    const observer = new ResizeObserver(() => {
      setPanelGroupSize(direction === "horizontal" ? panelGroup.offsetWidth : panelGroup.offsetHeight);
    });
    observer.observe(panelGroup);
    return () => {
      observer.disconnect();
    };
  }, []);
  if (!panelGroupSize) {
    return /* @__PURE__ */ jsx42("div", { "data-panel-id": id });
  }
  let defaultSize = getBoundedPercentage(
    defaultSizePercent ? defaultSizePercent : defaultSizePx ? defaultSizePx / panelGroupSize * 100 : void 0
  );
  let minSize = getBoundedPercentage(
    minSizePercent ? minSizePercent : minSizePx ? minSizePx / panelGroupSize * 100 : void 0
  );
  let maxSize = getBoundedPercentage(
    maxSizePercent ? maxSizePercent : maxSizePx ? maxSizePx / panelGroupSize * 100 : void 0
  );
  return /* @__PURE__ */ jsx42(Resizable2.Panel, { ...rest, id, defaultSize, minSize, maxSize });
}
var ResizeHandle = styled27(Resizable2.PanelResizeHandle, {
  '&[data-panel-group-direction="horizontal"]': {
    zIndex: 1,
    width: "7px",
    height: "100%",
    margin: "0 -3px",
    borderLeft: "3px solid transparent",
    borderRight: "3px solid transparent",
    "&:after": {
      content: '""',
      display: "block",
      width: "1px",
      height: "100%",
      background: "#EBECF2",
      margin: "0 auto"
    },
    "&:before": {
      // icon
      content: "\u2026",
      fontSize: "20px",
      color: "rgb(0 0 0)",
      background: "rgb(255 255 255)",
      border: "1px solid #EBECF2",
      // positioning
      display: "none",
      position: "absolute",
      transform: "rotate(90deg)",
      top: 0,
      left: "-10px",
      right: 0,
      bottom: 0,
      margin: "auto",
      textAlign: "center",
      // sizing
      lineHeight: "0rem",
      height: "10px",
      width: "20px",
      paddingBottom: "2px",
      borderRadius: "4px"
    },
    "&:hover:before": {
      display: "inline"
    },
    "&[data-resize-handle-active]:before": {
      display: "inline"
    }
  },
  '&[data-panel-group-direction="vertical"]': {
    zIndex: 1,
    width: "100%",
    height: "7px",
    margin: "-3px 0",
    borderTop: "3px solid transparent",
    borderBottom: "3px solid transparent",
    "&:after": {
      content: '""',
      display: "block",
      width: "100%",
      height: "1px",
      background: "#EBECF2",
      margin: "auto 0"
    },
    "&:before": {
      // icon
      content: "\u2026",
      fontSize: "20px",
      color: "rgb(0 0 0)",
      background: "rgb(255 255 255)",
      border: "1px solid #EBECF2",
      // positioning
      display: "none",
      position: "absolute",
      top: 0,
      left: "-10px",
      right: 0,
      bottom: 0,
      margin: "auto",
      textAlign: "center",
      // sizing
      lineHeight: "0rem",
      height: "10px",
      width: "20px",
      paddingBottom: "2px",
      borderRadius: "4px"
    },
    "&:hover:before": {
      display: "inline"
    },
    "&[data-resize-handle-active]:before": {
      display: "inline"
    }
  }
});

// src/test-carta-components.ts
var customButton = `
  <script>
  export const config = {
    label: 'Button',
    componentType: 'content',
    presets: [
      {
        label: 'Primary Button',
        content: '<custom-button variant="primary" size="medium" href="">Click Me</custom-button>',
      },
      {
        label: 'Secondary Button',
        content: '<custom-button variant="secondary" size="medium" href="">Click Me</custom-button>',
      },
    ],
  }

  export const props = defineProps({
    href: {
      section: 'Action',
      label: 'Link',
      schema: PropTypes.string(),
    },
    'full-width': {
      section: 'Layout',
      label: 'Full Width',
      schema: PropTypes.boolean().optional().default(false),
    },
    'border-radius': {
      section: 'Style',
      label: 'Radius',
      schema: PropTypes.string().optional(),
      type: 'radius',
    },
    'opacity': {
      section: 'Style',
      label: 'Opacity',
      schema: PropTypes.number().optional(),
      type: 'slider',
      min: 0,
      max: 1,
      step: 0.1,
    },

    'padding': {
      section: 'Layout',
      label: 'Padding',
      schema: PropTypes.string().optional(),
      type: 'box',
    },
    'margin': {
      section: 'Layout',
      label: 'Margin',
      schema: PropTypes.string().optional(),
      type: 'box',
    },
    'color': {
      section: 'Text',
      label: 'Color',
      schema: PropTypes.string().optional(),
      type: 'color',
    },
    'font-family': {
      section: 'Text',
      label: 'Font',
      type: 'select',
      options: [
        { label: 'Arial', value: 'Arial' },
        { label: 'Helvetica', value: 'Helvetica' },
        { label: 'Times New Roman', value: 'Times New Roman' },
        { label: 'Courier New', value: 'Courier New' },
        { label: 'Verdana', value: 'Verdana' },
        { label: 'Georgia', value: 'Georgia' },
      ],
      schema: PropTypes.string().optional(),
    },
    'font-size': {
      section: 'Text',
      label: 'Size',
      schema: PropTypes.union([PropTypes.string(), PropTypes.number()]).optional(),
      type: 'number'
    },
    'font-weight': {
      section: 'Text',
      label: 'Weight',
      schema: PropTypes.enum(['normal', 'bold']).optional(),
    },
    'text-align': {
      section: 'Text',
      label: 'Text Align',
      options: [
        { label: 'Left', value: 'left', icon: 'format_align_left' },
        { label: 'Center', value: 'center', icon: 'format_align_center' },
        { label: 'Right', value: 'right', icon: 'format_align_right' },
      ],
      schema: PropTypes.enum(['left', 'center', 'right']).optional(),
      type: 'toggle'
    },
    'hover-background-color': {
      section: 'Effects',
      set: 'Hover',
      label: 'Fill',
      schema: PropTypes.string().optional(),
    },
    'hover-color': {
      section: 'Effects',
      set: 'Hover',
      label: 'Color',
      schema: PropTypes.string().optional(),
    },
    'box-shadow': {
      section: 'Effects',
      label: 'Shadow',
      schema: PropTypes.string().optional(),
      type: 'shadow',
    }
  })

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })

  const style = {
    'background-color': props.variant === 'primary' ? '#0652BB' : 'white',
    padding: props.size === 'small' ? '4px 8px' : props.size === 'medium' ? '8px 16px' : '12px 24px',
    color: props.variant === 'primary' ? 'white' : '#343446',
    'text-align': 'center',
    'text-decoration': 'none',
    'border-radius': '8px',
    'border': '1px solid',
    'width': '120px',
    'margin': '0 auto',
    'font-size': props['font-size'] || '14px',
  }
</script>

<template>
  <a
  :href="props.href"
  :style="style"
  ><slot></slot></a>
</template>
`;
var paragraph = `
  <script>
  export const config = {
    label: 'Paragraph',
    componentType: 'content',
    prose: true,
    presets: [
      {
        label: 'Paragraph',
        content: '<paragraph></paragraph>',
      },
    ]
  }

  export const props = defineProps({
    'font-size': PropTypes.string().optional(),

    // validate the color as a hex value
    'font-color': PropTypes.string().length(7, {
      message: 'Color must a # followed by 6 characters',
    }).regex(/^#/, {
      message: 'Color must start with #',
    }).optional(),

    'background-color': {
      label: 'Background',
      schema: PropTypes.string().optional()
    },
    'text-align': {
      label: 'Text Align',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      schema: PropTypes.enum(['left', 'center', 'right']).optional(),
    }
  })

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })

  const style = {
    'font-size': \`\${props['font-size']}px\`,
    'color': props['font-color'],
    'background-color': props['background-color'],
    'text-align': props['text-align'],
    'line-height': '1.35',
    margin: '0 0 1em',
  }

  </script>

  <template><p :style="style"><slot /></p></template>`;
var heading1 = `
  <script>
  export const config = {
    label: 'Heading 1',
    componentType: 'content',
    prose: true,
    presets: [
      {
        label: 'Heading 1',
        content: '<heading1></heading1>',
      }
    ]
  }

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })
  </script>
  <template><h1 #root><slot /></h1></template>`;
var heading2 = `
  <script>
  export const config = {
    label: 'Heading 2',
    componentType: 'content',
    prose: true,
    presets: [
      {
        label: 'Heading 2',
        content: '<heading2></heading2>',
      }
    ]
  }

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })
  </script>
  <template><h2><slot /></h2></template>`;
var heading3 = `
  <script>
  export const config = {
    label: 'Heading 3',
    componentType: 'content',
    prose: true,
    presets: [
      {
        label: 'Heading 3',
        content: '<heading3></heading3>',
      }
    ]
  }

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })
  </script>
  <template><h3><slot /></h3></template>`;
var customImage = `
  <script>
  export const config = {
    componentType: 'content',
    presets: [
      {
        label: 'Image',
        content: '<custom-image src="https://picsum.photos/seed/300/600/300?grayscale" alt="Image" />',
      }
    ]
  }

  export const props = defineProps({
    src: PropTypes.string(),
    alt: PropTypes.string(),
  })

  </script>
  <template><img :src="props.src" :alt="props.alt" style="border-radius: 6px; box-shadow: 0 13px 27px -5px #32325d40, 0 8px 16px -8px #0000004d, 0 -6px 16px -6px #00000008;" /></template>`;
var row = `
  <script>
    export const config = {
      componentType: 'structure',
      presets: [
        {
          label: 'Row',
          content: '<row><column><paragraph></paragraph></column></row>',
        },
      ]
    }

    export const slots = defineSlots({
      default: {
        schema: SlotTypes.children(['column']),
        direction: 'horizontal',
      }
    })
  </script>
  <template><div style="display: flex; gap: 36px; padding-bottom: 20px;"><slot /></div></template>`;
var column = `
  <script>
    export const config = {
      componentType: 'structure',
      presets: [
        {
          label: 'Column',
          content: '<column><paragraph></paragraph></column>',
        },
      ]
    }

    export const slots = defineSlots({
      default: SlotTypes.children(['custom-button', 'paragraph', 'heading1', 'heading2', 'heading3', 'custom-image', 'slot-with-shared-parent', 'multiple-text-slots', 'multiple-children-slots']),
    })
  </script>
  <template><div style="display: flex; flex-direction: column; width: 0; flex-grow: 1; flex-shrink: 0; "><slot /></div></template>`;
var container = `
  <script>
    export const config = {componentType: 'structure'}

    export const slots = defineSlots({
      default: SlotTypes.children(['row']),
    })
  </script>
  <template><table cellpadding="0" cellspacing="0" border="0" width="98%" style="width:98%;max-width:800px;margin:auto;" #root><tr><td><div style="margin: auto; max-width: 80%; font-family: Arial; background: white;"><slot /></div></td></tr></table></template>`;
var slotWithSharedParent = `
  <script>
  export const config = { componentType: 'content' }

  export const slots = defineSlots({
    default: SlotTypes.text(),
  })
  </script>
  <template><b>hello <slot /></b></template>`;
var multipleTextSlots = ` 
  <script>
  export const config = { componentType: 'content' }
  
  export const slots = defineSlots({
    first: SlotTypes.text(),
    second: SlotTypes.text(),
  })
  </script>
  <template><b>hello <slot name="first" /> <slot name="second" /></b>
  </template>`;
var multipleChildrenSlots = ` 
  <script>
  export const config = { componentType: 'structure' }

  export const slots = defineSlots({
    first: SlotTypes.children(['paragraph']),
    second: SlotTypes.children(['paragraph']),
  })

  </script>

  <template>
    <div><slot name="first" /></div>
    <div><slot name="second" /></div>
  </template>`;
var allTypes = ` 
  <script>
  export const config = { componentType: 'structure' }

  export const props = defineProps({
    'box': {
      schema: PropTypes.string().optional(),
      type: 'box',
    },
    'code': {
      schema: PropTypes.string().optional(),
      type: 'code',
    },
    'color': {
      schema: PropTypes.string().optional(),
      type: 'color',
    },
    'hidden': {
      schema: PropTypes.string().optional(),
      type: 'hidden',
    },
    'media': {
      schema: PropTypes.string().optional(),
      type: 'media',
    },
    'number': {
      schema: PropTypes.number().optional(),
      type: 'number',
    },
    'radius': {
      schema: PropTypes.string().optional(),
      type: 'radius',
    },
    'select': {
      schema: PropTypes.string().optional(),
      type: 'select',
      options: [
        { label: 'Option 1', value: 'option-1' },
        { label: 'Option 2', value: 'option-2' },
        { label: 'Option 3', value: 'option-3' },
      ],
    },
    'shadow': {
      schema: PropTypes.string().optional(),
      type: 'shadow',
    },
    'size': {
      schema: PropTypes.string().optional(),
      type: 'size',
    },
    'slider': {
      schema: PropTypes.number().optional(),
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
    },
    'switch': {
      schema: PropTypes.boolean().optional(),
      type: 'switch',
    },
    'text': {
      schema: PropTypes.string().optional(),
      type: 'text',
    },
    'toggle': {
      schema: PropTypes.string().optional(),
      type: 'toggle',
      options: [
        { label: '1', value: 'option-1' },
        { label: '2', value: 'option-2' },
        { label: '3', value: 'option-3' },
      ],
    },
    'url': {
      schema: PropTypes.string().optional(),
      type: 'url',
    },
    'unknown': {
      schema: PropTypes.string().optional(),
      type: 'a-type-that-does-not-exist',
    },
  })

</script>
<template>
<div>
background: "\${props.background}"<br />
box: "\${props.box}"<br />
code: "\${props.code}"<br />
color: "\${props.color}"<br />
hidden: "\${props.hidden}"<br />
media: "\${props.media}"<br />
number: "\${props.number}"<br />
radius: "\${props.radius}"<br />
select: "\${props.select}"<br />
shadow: "\${props.shadow}"<br />
size: "\${props.size}"<br />
slider: "\${props.slider}"<br />
switch: "\${props.switch}"<br />
text: "\${props.text}"<br />
toggle: "\${props.toggle}"<br />
url: "\${props.url}"<br />
unknown: "\${props.unknown}"<br />
</div>
</template>`;
var testComponents = {
  "custom-button": customButton,
  paragraph,
  heading1,
  heading2,
  heading3,
  "custom-image": customImage,
  row,
  column,
  container,
  "slot-with-shared-parent": slotWithSharedParent,
  "multiple-text-slots": multipleTextSlots,
  "multiple-children-slots": multipleChildrenSlots,
  "all-types": allTypes
};

// src/theme.tsx
import { Fragment as Fragment18, jsx as jsx43 } from "react/jsx-runtime";
function EncompassTheme({ children, theme }) {
  return /* @__PURE__ */ jsx43(Fragment18, { children });
}

// src/index.ts
function createCodeEditorState(defaults) {
  const state = atom({ path: "", value: "", readOnly: false, ...defaults });
  const actions = {
    update({ path, value, readOnly }) {
      state.set({
        ...state.get(),
        path,
        value,
        readOnly
      });
    },
    updateValue(value) {
      state.set({ ...state.get(), value });
    }
    // no "updatePath" since we should never change the path
    // without also changing the value
  };
  return {
    state,
    actions
  };
}
function createCartaVisualEditorState(options2) {
  const preview = new MarshaledPreview2();
  const visualized = new CartaVisualized(options2);
  const state = atom({
    isLoading: false,
    editMode: visualized.editMode,
    tree: visualized.tree,
    nodes: visualized.nodes,
    registeredComponents: visualized.registeredComponents,
    contentTarget: visualized.contentTarget,
    selectedComponents: visualized.selectedComponents,
    hoveredComponent: visualized.hoveredComponent,
    draggingBoundingBox: visualized.ephemeralState.draggingBoundingBox,
    commandMenuMode: visualized.commandMenuMode,
    textSelection: visualized.textSelection,
    error: null,
    scroll: preview.scroll,
    size: preview.size,
    corePropertiesMenu: {
      target: {
        top: 0,
        left: 0,
        height: 0,
        width: 0
      },
      isVisible: false
    },
    fullPropertiesMenu: {
      isVisible: false
    },
    supportsSelectMedia: !!visualized.options.onSelectMedia,
    supportsUploadMedia: !!visualized.options.onUploadMedia,
    defaultProseComponent: visualized.options.defaultProseComponent
  });
  const actions = {
    mount: (...args) => {
      return visualized.mount(...args);
    },
    unmount: (...args) => {
      return visualized.unmount(...args);
    },
    registerGlobalComponent: (...args) => {
      return visualized.registerGlobalComponent(...args);
    },
    unregisterGlobalComponent: (...args) => {
      return visualized.unregisterGlobalComponent(...args);
    },
    queueSelection: (...args) => {
      return visualized.queueSelection(...args);
    },
    queueSiblingSelection: (...args) => {
      return visualized.queueSiblingSelection(...args);
    },
    setContentTarget: (...args) => {
      return visualized.setContentTarget(...args);
    },
    getComponentDefinition: (...args) => {
      return visualized.getComponentDefinition(...args);
    },
    getSlotDefinition: (...args) => {
      return visualized.getSlotDefinition(...args);
    },
    getParentComponentTarget: (...args) => {
      return visualized.getParentComponentTarget(...args);
    },
    getParentSlotTarget: (...args) => {
      return visualized.getParentSlotTarget(...args);
    },
    getParentTargetAndChildIndex: (...args) => {
      return visualized.getParentTargetAndChildIndex(...args);
    },
    isRootComponent: (...args) => {
      return visualized.isRootComponent(...args);
    },
    setSelectedComponent: (...args) => {
      return visualized.setSelectedComponent(...args);
    },
    addSelectedComponent: (...args) => {
      return visualized.addSelectedComponent(...args);
    },
    removeSelectedComponent: (...args) => {
      return visualized.removeSelectedComponent(...args);
    },
    onSelectMedia: (...args) => {
      return visualized.onSelectMedia(...args);
    },
    setHoveredComponent: (...args) => {
      return visualized.setHoveredComponent(...args);
    },
    isSelectedComponent: (...args) => {
      return visualized.isSelectedComponent(...args);
    },
    editorExecCommand: (...args) => {
      return visualized.textExecCommand(...args);
    },
    blur(...args) {
      return visualized.blur(...args);
    },
    focus(...args) {
      return visualized.focus(...args);
    },
    setCommandMenuMode: (...args) => {
      return visualized.setCommandMenuMode(...args);
    },
    discardCommandMenu: (...args) => {
      return visualized.discardCommandMenu(...args);
    },
    setComponentTagName: (target, tagName) => {
      return visualized.setComponentTagName(target, tagName);
    },
    getProseComponentTextContent: (...args) => {
      return visualized.getProseComponentTextContent(...args);
    },
    insertDefaultProseComponentAfterTarget: (...args) => {
      return visualized.insertDefaultProseComponentAfterTarget(...args);
    },
    insertTextAtCursor: (text) => {
      return visualized.insertTextAtCursor(text);
    },
    getHTML: (...args) => {
      return visualized.getHTML(...args);
    },
    scrollBy: (...args) => {
      return preview.scrollBy(...args);
    },
    undo: (...args) => {
      return visualized.undo(...args);
    },
    redo: (...args) => {
      return visualized.redo(...args);
    },
    getComponentProps: (...args) => {
      return visualized.getComponentProps(...args);
    },
    hideCorePropertiesMenu: () => {
      return state.set({
        ...state.get(),
        corePropertiesMenu: {
          target: { top: 0, left: 0, width: 0, height: 0 },
          isVisible: false
        }
      });
    },
    showCorePropertiesMenu: (target) => {
      return state.set({
        ...state.get(),
        corePropertiesMenu: { isVisible: true, target }
      });
    },
    showFullPropertiesMenu: () => {
      return state.set({
        ...state.get(),
        fullPropertiesMenu: { isVisible: true }
      });
    },
    hideFullPropertiesMenu: () => {
      return state.set({
        ...state.get(),
        fullPropertiesMenu: { isVisible: false }
      });
    },
    track: (...args) => {
      return visualized.track(...args);
    }
  };
  visualized.on("*", () => {
    state.set({
      ...state.get(),
      error: visualized.error,
      editMode: visualized.editMode,
      tree: visualized.tree,
      nodes: visualized.nodes,
      registeredComponents: visualized.registeredComponents,
      contentTarget: visualized.contentTarget,
      selectedComponents: visualized.selectedComponents,
      hoveredComponent: visualized.hoveredComponent,
      draggingBoundingBox: visualized.ephemeralState.draggingBoundingBox,
      commandMenuMode: visualized.commandMenuMode,
      textSelection: visualized.textSelection,
      defaultProseComponent: visualized.options.defaultProseComponent
    });
  });
  visualized.on("beforeRender", () => {
    state.set({
      ...state.get(),
      isLoading: true
    });
  });
  visualized.on("render", () => {
    state.set({
      ...state.get(),
      isLoading: false
    });
  });
  visualized.on("error", (error) => {
    if (error) {
      state.set({
        ...state.get(),
        isLoading: false
      });
    }
  });
  preview.on(() => {
    state.set({
      ...state.get(),
      scroll: preview.scroll,
      size: preview.size
    });
  });
  return {
    // these should almost never be used so __ is added to the beginning of the name
    __preview: preview,
    __visualized: visualized,
    // these are the public APIs
    state,
    actions,
    commands: visualized.commands
  };
}
function createEncompass(defaults) {
  const codeEditor = createCodeEditorState(defaults?.codeEditor);
  const visualEditor = createCartaVisualEditorState({
    ...defaults?.visualEditor,
    source: codeEditor.state.get().value
  });
  visualEditor.__visualized.on("sourceUpdate", ({ source }) => {
    codeEditor.actions.updateValue(source);
  });
  return {
    codeEditor,
    visualEditor
  };
}
export {
  EncompassCodeEditor,
  layout_exports as EncompassLayout,
  EncompassProvider,
  EncompassTheme,
  visual_editor_exports as EncompassVisualEditor,
  components,
  createEncompass,
  testComponents,
  useCodeEditor,
  useEncompass,
  useSelection
};
